import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBack, useWindowWidth } from '../hooks';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import api from '../api';
import { ExcelForm } from './Orders';
import CapacityCheckModal from '../components/CapacityCheckModal';
import AtRiskPanel from '../components/AtRiskPanel';
import BacklogPanel from '../components/BacklogPanel';
import ScrollStrip from '../components/ScrollStrip';
import MoveModal from '../components/MoveModal';
import RunBar from '../components/RunBar';
import { fmtSpan } from '../components/RunsPicture';
import DowntimeModal from '../components/DowntimeModal';
import ShiftWindowsModal from '../components/ShiftWindowsModal';
import ExtraStaffModal from '../components/ExtraStaff';
import ReflowModal from '../components/ReflowModal';
import ScheduleHistoryModal from '../components/ScheduleHistoryModal';
import ReasonStrip from '../components/ReasonStrip';
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer } from 'recharts';

// Shop floor names for the shifts, in order. One copy, because two would drift
// and these are already the names the director corrected me on: the first shift
// is a morning, the second an afternoon, the third a night.
const SHIFT_LABEL = { 1: 'morning', 2: 'afternoon', 3: 'night' };

// ── Date helpers ──────────────────────────────────────────────

function getMonday(d) {
  const date = new Date(d);
  const day = date.getDay();
  date.setDate(date.getDate() - day + (day === 0 ? -6 : 1));
  date.setHours(0, 0, 0, 0);
  return date;
}

export function addDays(d, n) {
  const date = new Date(d);
  date.setDate(date.getDate() + n);
  return date;
}

export function fmt(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isoWeekOf(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const week = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return { week, year: date.getFullYear() };
}

// Return the Monday of ISO week `week` in `year`
function isoWeekToMonday(week, year) {
  const jan4 = new Date(year, 0, 4);
  const dow  = (jan4.getDay() + 6) % 7; // days since Monday
  return new Date(jan4.getTime() - dow * 86400000 + (week - 1) * 7 * 86400000);
}

function getRange(viewDate, viewMode, weekSpan = 1) {
  if (viewMode === 'day') {
    return { from: fmt(addDays(viewDate, -7)), to: fmt(viewDate) };
  }
  if (viewMode === 'week') {
    const start = getMonday(viewDate);
    return { from: fmt(addDays(start, -7)), to: fmt(addDays(start, 7 * weekSpan + 6)) };
  }
  const y = viewDate.getFullYear(), mo = viewDate.getMonth();
  return { from: fmt(new Date(y, mo, 1)), to: fmt(new Date(y, mo + 1, 0)) };
}

// Expand closure ranges into a {date: label} map for use alongside buildHolidays()
function closuresToMap(closures) {
  const map = {};
  closures.forEach(c => {
    let d = new Date(c.date_from + 'T12:00:00');
    const end = new Date(c.date_to + 'T12:00:00');
    const label = c.reason || 'Company closure';
    while (d <= end) {
      map[fmt(d)] = label;
      d = new Date(d.getTime() + 86400000);
    }
  });
  return map;
}

// Returns the date string of the Nth working day (0-indexed) on or after startDate.
function getNthWorkingDay(startDate, n, holidays) {
  let count = 0;
  let cur = new Date(startDate);
  for (let safety = 0; safety < 500; safety++) {
    const ds = fmt(cur);
    if (!isWeekendStr(ds) && !holidays[ds]) {
      if (count === n) return ds;
      count++;
    }
    cur = addDays(cur, 1);
  }
  return fmt(startDate);
}

// ── Daily capacity ───────────────────────────────────────────────────────────
// Mirrors calcDailyEffSecs() in backend/src/routes/orders.js, which is the source
// of truth. Keep the two in step: if they drift, a job planned by hand needs a
// different number of days from the same job scheduled automatically, and nobody
// can tell which one is right.
const DEFAULT_WARMUP_MIN = 30;                    // assumed when a machine has none set
const SHIFT_2_EFF_SECS   = 7 * 3600 + 50 * 60;    // shift 2 starts on a warm machine

function dailyCapacity({ machine, mould, cycleS, shifts }) {
  const cavities      = mould?.cavities || 1;
  const machineWarmup = machine?.warmup_min ?? DEFAULT_WARMUP_MIN;
  const mouldStartup  = mould?.additional_startup_min || 0;
  const warmupMin     = machineWarmup + mouldStartup;
  const changeoverMin = (mould?.toolchange_mount_min || 0) + (mould?.toolchange_unmount_min || 0);

  const shift1Secs   = Math.max(0, 8 * 3600 - warmupMin * 60);
  const dailyEffSecs = shifts > 1 ? shift1Secs + SHIFT_2_EFF_SECS : shift1Secs;

  return {
    cavities, machineWarmup, mouldStartup, warmupMin, changeoverMin,
    dailyEffSecs,
    usingDefaultWarmup: machine?.warmup_min == null,
    shotsPerDay:   Math.floor(dailyEffSecs / cycleS),
    partsPerDay:   Math.floor(dailyEffSecs / cycleS) * cavities,
    firstDayParts: Math.floor(Math.max(0, dailyEffSecs - changeoverMin * 60) / cycleS) * cavities,
  };
}

// Seconds → "7h 25m", for the capacity breakdown.
function fmtHm(secs) {
  const m = Math.round(secs / 60);
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}

// Shift a date string forward by n working days.
function shiftByWorkingDays(dateStr, n, holidays) {
  if (n <= 0) return dateStr;
  return getNthWorkingDay(new Date(dateStr + 'T12:00:00'), n, holidays);
}

// Shift a date string BACKWARD by n working days.
function shiftBackByWorkingDays(dateStr, n, holidays) {
  if (n <= 0) return dateStr;
  let count = 0;
  let cur = new Date(dateStr + 'T12:00:00');
  for (let safety = 0; safety < 500; safety++) {
    cur = addDays(cur, -1);
    const ds = fmt(cur);
    if (!isWeekendStr(ds) && !holidays[ds]) {
      count++;
      if (count === n) return ds;
    }
  }
  return dateStr;
}

// Returns display_name if set, otherwise falls back to name.
const machineName = m => m?.display_name || m?.name || '—';

// The versions of runs as this screen last saw them, sent as x-expect with
// anything that acts on them. If someone changed a run first -- moved it,
// started it, edited it -- the server refuses instead of silently overwriting,
// and says who and how (migration 046). Rows without a version send nothing.
function expectOf(rows) {
  const out = {};
  for (const r of rows || []) if (r && r.id && r.version != null) out[r.id] = r.version;
  return out;
}
function withExpect(rows) {
  const e = expectOf(rows);
  return Object.keys(e).length ? { headers: { 'x-expect': JSON.stringify(e) } } : {};
}

function moveDate(d, mode, dir) {
  if (mode === 'day')  return addDays(d, dir);
  if (mode === 'week') return addDays(d, dir * 7);
  const nd = new Date(d); nd.setDate(1); nd.setMonth(nd.getMonth() + dir); return nd;
}

// fromDate: where the dates start when the board is scrolled past the first
// loaded week -- the same week the "Wk" number beside them names (12 Oct 2026:
// "Wk 42–52 · 14 Sept — 27 Dec" named two different starts).
function getLabel(viewDate, viewMode, weekSpan = 1, fromDate = null) {
  if (viewMode === 'day') return viewDate.toLocaleDateString('sl-SI', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  if (viewMode === 'week') {
    const start = getMonday(fromDate || viewDate), end = addDays(getMonday(viewDate), 7 * weekSpan - 1);
    return `${start.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} — ${end.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
  }
  return viewDate.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

function getMonthCalDays(viewDate) {
  const y = viewDate.getFullYear(), mo = viewDate.getMonth();
  const first = new Date(y, mo, 1), last = new Date(y, mo + 1, 0);
  const start = getMonday(first);
  const days = [];
  let cur = new Date(start);
  while (cur <= last || days.length % 7 !== 0) {
    days.push(new Date(cur));
    cur = addDays(cur, 1);
    if (days.length >= 42) break;
  }
  return days;
}

// ── Slovenian public holidays ─────────────────────────────────

function getEaster(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const d = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day   = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function getSlovenianHolidays(year) {
  const h = {};
  const add = (d, name) => { h[fmt(d)] = name; };

  add(new Date(year,  0,  1), 'Novo leto');
  add(new Date(year,  0,  2), 'Novo leto');
  add(new Date(year,  1,  8), 'Prešernov dan');
  add(new Date(year,  3, 27), 'Dan upora proti okupatorju');
  add(new Date(year,  4,  1), 'Praznik dela');
  add(new Date(year,  4,  2), 'Praznik dela');
  add(new Date(year,  5, 25), 'Dan državnosti');
  add(new Date(year,  7, 15), 'Marijino vnebovzetje');
  add(new Date(year,  9, 31), 'Dan reformacije');
  add(new Date(year, 10,  1), 'Dan mrtvih');
  add(new Date(year, 11, 25), 'Božič');
  add(new Date(year, 11, 26), 'Dan samostojnosti in enotnosti');

  const easter = getEaster(year);
  add(easter,               'Velika noč');
  add(addDays(easter, 1),   'Velikonočni ponedeljek');
  add(addDays(easter, 49),  'Binkoštna nedelja');

  return h;
}

export function buildHolidays(viewDate) {
  const y = viewDate.getFullYear();
  return {
    ...getSlovenianHolidays(y - 1),
    ...getSlovenianHolidays(y),
    ...getSlovenianHolidays(y + 1),
  };
}

export function isWeekendStr(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  return d.getDay() === 0 || d.getDay() === 6;
}

// Cross-hatch background for non-working days
function crossHatch() {
  const c = 'rgba(255,255,255,0.04)';
  return [
    `repeating-linear-gradient( 45deg, ${c} 0, ${c} 1px, transparent 1px, transparent 8px)`,
    `repeating-linear-gradient(-45deg, ${c} 0, ${c} 1px, transparent 1px, transparent 8px)`,
  ].join(', ');
}

// ── Constants ─────────────────────────────────────────────────

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function jobMatchesFilter(job, q) {
  if (!q) return true;
  const l = q.toLowerCase();
  return (
    (job.parts?.name        || '').toLowerCase().includes(l) ||
    (job.parts?.part_number || '').toLowerCase().includes(l) ||
    (job.order_number       || '').toLowerCase().includes(l) ||
    (job.moulds?.ref        || '').toLowerCase().includes(l)
  );
}

export function getMbColor(recipe) {
  if (!recipe?.recipe_components) return null;
  const mb = recipe.recipe_components.find(c => c.type === 'masterbatch');
  return mb?.material?.colour || null;
}

export function getMbHex(recipe, colours) {
  const name = getMbColor(recipe);
  if (!name || !colours?.length) return null;
  return colours.find(c => c.name === name)?.hex || null;
}

export function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

// Returns #ffffff or #111827 — whichever has higher contrast against the computed dark bg.
export function getContrastTitle(mbHex) {
  if (!mbHex) return '#f0f2f5';
  const h = mbHex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  // Reconstruct the same blended bg as getMbDarkBg
  const t = 0.25;
  const br = Math.round(30 + (r - 30) * t);
  const bg = Math.round(35 + (g - 35) * t);
  const bb = Math.round(41 + (b - 41) * t);
  // WCAG relative luminance
  const lin = c => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4); };
  const L = 0.2126 * lin(br) + 0.7152 * lin(bg) + 0.0722 * lin(bb);
  return L > 0.179 ? '#111827' : '#ffffff';
}

// Blend the colour towards white at 70 % — gives a light, readable tint for text on dark bg.
export function getMbTextColor(hex) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const t = 0.70;
  return `rgb(${Math.round(r + (255 - r) * t)},${Math.round(g + (255 - g) * t)},${Math.round(b + (255 - b) * t)})`;
}

// Part colour at 30 % opacity (or grey #808080 at 60 % when no colour is set).
// Returns { bg, border } where border is the same hue at 25 % opacity.
// Any value that isn't a valid 3- or 6-digit hex is treated as "no colour".
function partColourBg(hex) {
  const valid = /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/.test(hex || '') ? hex : null;
  const h = (valid || '#808080').replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const a = valid ? 0.30 : 0.60;
  return { bg: `rgba(${r},${g},${b},${a})`, border: `rgba(${r},${g},${b},0.70)` };
}

// Blend the colour into the card dark-bg (#1e2329 = 30,35,41) at 25 %.
// Produces a dark, desaturated tint that's clearly recognisable but never garish.
export function getMbDarkBg(hex) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  const t = 0.25;
  return `rgb(${Math.round(30 + (r - 30) * t)},${Math.round(35 + (g - 35) * t)},${Math.round(41 + (b - 41) * t)})`;
}

function getStatusColor(job) {
  if (job.status === 'started')   return { bg: 'rgba(134,239,172,0.08)', border: 'rgba(134,239,172,0.3)', text: '#86efac' };
  if (job.status === 'cancelled') return { bg: 'rgba(239,68,68,0.06)',   border: 'rgba(239,68,68,0.2)',  text: '#ef4444' };
  if (job.status === 'completed') {
    const reason = job.job?.end_reason;
    if (reason === 'machine' || reason === 'mould' || reason === 'quality')
      return { bg: 'rgba(239,68,68,0.06)',   border: 'rgba(239,68,68,0.2)',  text: '#ef4444' };
    if (reason === 'shift')
      return { bg: 'rgba(251,191,36,0.06)',  border: 'rgba(251,191,36,0.2)', text: '#fbbf24' };
    return   { bg: 'rgba(107,114,128,0.06)', border: '#2a3040',               text: '#6b7280' };
  }
  return { bg: 'rgba(20,184,166,0.07)', border: '#2a3040', text: '#14b8a6' };
}

// ── Print view ────────────────────────────────────────────────

const PB = '#e5e7eb';   // print border
const PT = '#111827';   // print main text
const PS = '#6b7280';   // print secondary text
const PG = '#f9fafb';   // print cell bg

// Where a run's card crosses days nobody works on it -- a weekend, a holiday,
// a closure, the machine down -- those stretches are drawn faded: the board's
// non-working hatch over a dark tint, on top of the card's colour and under
// its words (the director, 21 Oct 2026: "easy to follow the job card over the
// screen but also obvious that something is different").
//
// off: [[from, to)] column ranges inside the card, in its own columns; n: the
// card's width in columns; gap: px between columns; inset: the card's
// padding in its box; radius: its corners. Neighbouring ranges join, and a
// stretch reaches to the middle of the gap beside it, so it reads as one.
const RUN_FADE = `${crossHatch()}, rgba(15,17,23,0.45)`;
function fadeStretches(off, n, { gap, inset = 0, radius = 0 }) {
  const ranges = [];
  for (const [a, b] of [...off].sort((x, y) => x[0] - y[0])) {
    const last = ranges[ranges.length - 1];
    if (last && a <= last[1]) last[1] = Math.max(last[1], b); else ranges.push([a, b]);
  }
  return ranges.filter(([a, b]) => n > 0 && b > a).map(([a, b]) => {
    const first = a <= 0, end = b >= n;
    const lPct = first ? 0 : (a / n) * 100, lPx = first ? inset : (gap * a) / n - gap / 2;
    const rPct = end ? 100 : (b / n) * 100, rPx = end ? -inset : (gap * b) / n - gap / 2;
    return {
      key: `${a}-${b}`,
      style: {
        position: 'absolute', top: inset, bottom: inset, zIndex: 2, pointerEvents: 'none', background: RUN_FADE,
        left: `calc(${lPct}% + ${lPx}px)`, width: `calc(${rPct - lPct}% + ${rPx - lPx}px)`,
        borderRadius: `${first ? radius : 0}px ${end ? radius : 0}px ${end ? radius : 0}px ${first ? radius : 0}px`,
      },
    };
  });
}
// The card's words and badges sit above the fade; its colour below it.
const RUN_FADE_RULE = '.mo-runcard > * { position: relative; z-index: 3; }';

function crossHatchPrint() {
  const c = 'rgba(0,0,0,0.07)';
  return [
    `repeating-linear-gradient( 45deg, ${c} 0,${c} 1px,transparent 1px,transparent 8px)`,
    `repeating-linear-gradient(-45deg, ${c} 0,${c} 1px,transparent 1px,transparent 8px)`,
  ].join(', ');
}

function buildPrintSpans(machineId, days, scheduled) {
  const planMap = {};
  days.forEach((day, dayIdx) => {
    const ds = fmt(day);
    scheduled.filter(j => j.machine_id === machineId && j.scheduled_date === ds && j.status !== 'cancelled').forEach(job => {
      const key = job.plan_id || `solo-${job.id}-${ds}`;
      if (!planMap[key]) planMap[key] = [];
      planMap[key].push({ job, dayIdx });
    });
  });
  const dayBefore = fmt(addDays(days[0], -1));
  const dayAfter  = fmt(addDays(days[days.length - 1], 1));
  return Object.values(planMap).map(entries => {
    entries.sort((a, b) => a.dayIdx - b.dayIdx);
    const jobs = entries.map(e => e.job);
    const planId = jobs[0].plan_id;
    const cameFromBefore = !!planId && scheduled.some(j => j.machine_id === machineId && j.scheduled_date === dayBefore && j.plan_id === planId && j.status !== 'cancelled');
    const continuesAfter = !!planId && scheduled.some(j => j.machine_id === machineId && j.scheduled_date === dayAfter  && j.plan_id === planId && j.status !== 'cancelled');
    return { jobs, startIdx: entries[0].dayIdx, endIdx: entries[entries.length - 1].dayIdx, totalQty: jobs.reduce((s, j) => s + (j.target_qty || 0), 0), cameFromBefore, continuesAfter };
  });
}

function PrintSpanTile({ jobs, totalQty, colours, cameFromBefore, continuesAfter }) {
  const rep = jobs[0];
  const hex = colours.find(c => c.name === rep.parts?.colour)?.hex || null;
  const { bg, border } = partColourBg(hex);
  const isStarted = jobs.some(j => j.status === 'started');
  const orderLine = rep.order_line?.order;
  const kwColor = (() => {
    if (!orderLine?.delivery_week) return null;
    const fri = weekToFriday(orderLine.delivery_week, orderLine.delivery_year);
    const [fy, fm, fd] = fri.split('-').map(Number);
    const hardDL    = fmt(addDays(new Date(fy, fm - 1, fd), -2));
    const primaryDL = fmt(addDays(new Date(fy, fm - 1, fd), -7));
    const lastDay = jobs[jobs.length - 1].scheduled_date;
    return lastDay > hardDL ? '#ef4444' : lastDay > primaryDL ? '#f59e0b' : null;
  })();
  const tl = cameFromBefore ? '0' : '5px';
  const tr = continuesAfter  ? '0' : '5px';
  return (
    <div style={{ background: bg, border: `1.5px solid ${border}`, borderRadius: `${tl} ${tr} ${tr} ${tl}`, padding: '2px 4px', display: 'flex', alignItems: 'center', gap: '3px', overflow: 'hidden', height: '100%', boxSizing: 'border-box' }}>
      {cameFromBefore && <span style={{ fontSize: '9px', color: PT, opacity: 0.5, flexShrink: 0, lineHeight: 1 }}>◀</span>}
      {isStarted && <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#16a34a', flexShrink: 0 }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '10px', fontWeight: '700', color: PT, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{rep.parts?.name || '—'}</div>
        <div style={{ fontSize: '8px', color: PS, whiteSpace: 'nowrap' }}>
          {totalQty.toLocaleString()} pcs{jobs.length > 1 ? ` · ${jobs.length}d` : ''}
          {orderLine?.delivery_week && (
            <span style={kwColor ? { color: kwColor, fontWeight: '700', marginLeft: '3px' } : { marginLeft: '3px' }}>
              · KW{orderLine.delivery_week}
            </span>
          )}
        </div>
      </div>
      {continuesAfter && <span style={{ fontSize: '9px', color: PT, opacity: 0.5, flexShrink: 0, lineHeight: 1 }}>▶</span>}
    </div>
  );
}

function PrintScheduleView({ viewMode, viewDate, machines, scheduled, colours, holidays, TODAY, onClose }) {
  const weekStart  = getMonday(viewDate);
  const weekDays   = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const { week: currWeek } = isoWeekOf(weekStart);
  const calDays    = viewMode === 'month' ? getMonthCalDays(viewDate) : null;
  const curMonth   = viewDate.getMonth();

  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.id = 'sched-print-css';
    const isDay = viewMode === 'day';
    styleEl.textContent = `
      @media print {
        @page { size: ${isDay ? 'A4 portrait' : 'A3 landscape'}; margin: 8mm; }
        body * { visibility: hidden; }
        #sched-print-root {
          visibility: visible !important;
          position: absolute !important;
          top: 0 !important; left: 0 !important; right: 0 !important;
          overflow: visible !important;
          background: white !important;
        }
        #sched-print-root * {
          visibility: visible !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        #sched-print-controls { display: none !important; }
      }
    `;
    document.head.appendChild(styleEl);
    return () => document.getElementById('sched-print-css')?.remove();
  }, [viewMode]);

  return (
    <div id="sched-print-root" style={{ position: 'fixed', inset: 0, background: 'white', zIndex: 9000, overflow: 'auto', fontFamily: 'system-ui', color: PT }}>

      {/* Controls bar (hidden in print) */}
      <div id="sched-print-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', borderBottom: `1px solid ${PB}`, background: PG, position: 'sticky', top: 0, zIndex: 1 }}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: '700', color: PT }}>Print Preview — {getLabel(viewDate, viewMode)}</div>
          <div style={{ fontSize: '11px', color: PS, marginTop: '2px' }}>Click "Print / Save as PDF" to export</div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => window.print()} style={{ background: '#14b8a6', color: 'white', border: 'none', borderRadius: '7px', padding: '8px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="fa-solid fa-print" /> Print / Save as PDF
          </button>
          <button onClick={onClose} style={{ background: 'none', border: `1px solid ${PB}`, color: PT, borderRadius: '7px', padding: '8px 14px', fontSize: '13px', cursor: 'pointer' }}>✕ Close</button>
        </div>
      </div>

      {/* Print content */}
      <div style={{ padding: '12px 16px' }}>
        {/* Page title (visible in print, inside the @media print root) */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px', paddingBottom: '6px', borderBottom: `1.5px solid ${PT}` }}>
          <div style={{ fontSize: '16px', fontWeight: '800', color: PT }}>Production Schedule</div>
          <div style={{ fontSize: '12px', color: PS }}>{getLabel(viewDate, viewMode)}</div>
        </div>

        {/* ── WEEK VIEW ── */}
        {viewMode === 'week' && (
          <div style={{ display: 'grid', gridTemplateColumns: '130px repeat(7, 1fr)', gridAutoRows: '52px', gap: '2px' }}>
            {/* Header */}
            <div style={{ background: PG, border: `1px solid ${PB}`, borderRadius: '4px', padding: '4px 8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: '9px', fontWeight: '700', color: PS, textTransform: 'uppercase' }}>Wk {currWeek}</span>
            </div>
            {weekDays.map((day, i) => {
              const ds = fmt(day); const isToday = ds === TODAY; const holiday = holidays[ds]; const isWknd = isWeekendStr(ds);
              return (
                <div key={ds} style={{ background: isToday ? 'rgba(20,184,166,0.06)' : holiday ? 'rgba(251,191,36,0.05)' : isWknd ? '#f3f4f6' : PG, border: `1px solid ${isToday ? 'rgba(20,184,166,0.4)' : PB}`, borderRadius: '4px', padding: '4px', textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div style={{ fontSize: '9px', fontWeight: '700', color: isToday ? '#0d9488' : holiday ? '#b45309' : isWknd ? '#9ca3af' : '#374151', textTransform: 'uppercase' }}>{DAY_LABELS[i]}</div>
                  <div style={{ fontSize: '11px', color: isToday ? '#0d9488' : holiday ? '#b45309' : '#6b7280' }}>{day.getDate()} {day.toLocaleDateString('en-GB', { month: 'short' })}</div>
                  {holiday && <div style={{ fontSize: '7px', color: '#b45309', lineHeight: 1.1, marginTop: '1px', overflow: 'hidden' }}>{holiday}</div>}
                </div>
              );
            })}

            {/* Machine rows */}
            {machines.map(machine => {
              const spans = buildPrintSpans(machine.id, weekDays, scheduled);
              return (
                <React.Fragment key={machine.id}>
                  <div style={{ background: PG, border: `1px solid ${PB}`, borderRadius: '4px', padding: '4px 8px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: PT }}>{machineName(machine)}</div>
                    {machine.tonnes && <div style={{ fontSize: '9px', color: PS }}>{machine.tonnes}T</div>}
                  </div>
                  <div style={{ gridColumn: '2 / -1', display: 'grid', gridTemplateColumns: 'subgrid', gridTemplateRows: '1fr', height: '100%', gap: '2px' }}>
                    {weekDays.map((day, dayIdx) => {
                      const ds = fmt(day); const isToday = ds === TODAY; const holiday = holidays[ds]; const isWknd = isWeekendStr(ds); const nonWork = holiday || isWknd;
                      return (
                        <div key={ds} style={{ gridColumn: dayIdx + 1, gridRow: 1, backgroundImage: nonWork ? crossHatchPrint() : undefined, backgroundColor: isToday ? 'rgba(20,184,166,0.04)' : nonWork ? '#f3f4f6' : 'white', border: `1px solid ${isToday ? 'rgba(20,184,166,0.3)' : PB}`, borderRadius: '3px', zIndex: 0 }} />
                      );
                    })}
                    {spans.map((span, i) => (
                      <div key={i} style={{ gridColumn: `${span.startIdx + 1} / ${span.endIdx + 2}`, gridRow: 1, zIndex: 1, padding: '3px', pointerEvents: 'none' }}>
                        <PrintSpanTile jobs={span.jobs} totalQty={span.totalQty} colours={colours} cameFromBefore={span.cameFromBefore} continuesAfter={span.continuesAfter} />
                      </div>
                    ))}
                  </div>
                </React.Fragment>
              );
            })}
          </div>
        )}

        {/* ── DAY VIEW ── */}
        {viewMode === 'day' && (() => {
          const ds = fmt(viewDate);
          return (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ background: PG }}>
                  {['Machine', 'Part', 'Qty', 'Mould', 'Recipe', 'Status'].map(h => (
                    <th key={h} style={{ border: `1px solid ${PB}`, padding: '6px 10px', textAlign: 'left', fontWeight: '700', color: '#374151', fontSize: '11px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {machines.map((machine, mi) => {
                  const jobs = scheduled.filter(j => j.machine_id === machine.id && j.scheduled_date === ds && j.status !== 'cancelled');
                  if (jobs.length === 0) return (
                    <tr key={machine.id} style={{ background: mi % 2 === 0 ? 'white' : PG }}>
                      <td style={{ border: `1px solid ${PB}`, padding: '6px 10px', fontWeight: '600', color: PT }}>{machineName(machine)}{machine.tonnes ? <span style={{ fontWeight: 400, color: PS, fontSize: '10px' }}> · {machine.tonnes}T</span> : null}</td>
                      {Array(5).fill(null).map((_, i) => <td key={i} style={{ border: `1px solid ${PB}`, padding: '6px 10px', color: PS }}>—</td>)}
                    </tr>
                  );
                  return jobs.map((job, ji) => {
                    const hex = colours.find(c => c.name === job.parts?.colour)?.hex || null;
                    const { border } = partColourBg(hex);
                    return (
                      <tr key={job.id} style={{ background: mi % 2 === 0 ? 'white' : PG }}>
                        {ji === 0 && (
                          <td rowSpan={jobs.length} style={{ border: `1px solid ${PB}`, borderLeft: `3px solid ${border}`, padding: '6px 10px', fontWeight: '600', color: PT, verticalAlign: 'top' }}>
                            {machineName(machine)}{machine.tonnes ? <div style={{ fontWeight: 400, color: PS, fontSize: '10px' }}>{machine.tonnes}T</div> : null}
                          </td>
                        )}
                        <td style={{ border: `1px solid ${PB}`, padding: '6px 10px', fontWeight: '600', color: PT }}>{job.parts?.name || '—'}</td>
                        <td style={{ border: `1px solid ${PB}`, padding: '6px 10px', color: PS }}>{job.target_qty?.toLocaleString() || '—'}</td>
                        <td style={{ border: `1px solid ${PB}`, padding: '6px 10px', color: PS }}>{job.moulds?.ref || '—'}</td>
                        <td style={{ border: `1px solid ${PB}`, padding: '6px 10px', color: PS }}>{job.recipe?.name || '—'}</td>
                        <td style={{ border: `1px solid ${PB}`, padding: '6px 10px' }}>
                          <span style={{ fontSize: '10px', fontWeight: '700', color: job.status === 'started' ? '#16a34a' : job.status === 'completed' ? '#6b7280' : '#2563eb' }}>
                            {job.status === 'started' ? '● Running' : job.status === 'completed' ? 'Done' : 'Scheduled'}
                          </span>
                        </td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          );
        })()}

        {/* ── MONTH VIEW ── */}
        {viewMode === 'month' && (() => {
          const byDate = {};
          scheduled.forEach(j => { if (!byDate[j.scheduled_date]) byDate[j.scheduled_date] = []; byDate[j.scheduled_date].push(j); });
          const weekGroups = [];
          for (let i = 0; i < calDays.length; i += 7) weekGroups.push(calDays.slice(i, i + 7));

          return (
            <div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '2px' }}>
                {DAY_LABELS.map(d => <div key={d} style={{ textAlign: 'center', fontSize: '9px', fontWeight: '700', color: PS, textTransform: 'uppercase', padding: '3px 0' }}>{d}</div>)}
              </div>
              {weekGroups.map((wDays, weekIdx) => {
                const { week, year } = isoWeekOf(wDays[0]);
                const planMap = {};
                wDays.forEach((day, colIdx) => {
                  const ds = fmt(day);
                  (byDate[ds] || []).filter(j => j.status !== 'cancelled').forEach(job => {
                    const key = job.plan_id || `solo-${job.id}-${ds}`;
                    if (!planMap[key]) planMap[key] = { entries: [], planId: job.plan_id };
                    planMap[key].entries.push({ job, colIdx });
                  });
                });
                const wDayBefore = fmt(addDays(wDays[0], -1));
                const wDayAfter  = fmt(addDays(wDays[6],  1));
                const spans = Object.values(planMap).map(({ entries, planId }) => {
                  entries.sort((a, b) => a.colIdx - b.colIdx);
                  const jobs = entries.map(e => e.job);
                  const machineId = jobs[0].machine_id;
                  const cameFromBefore = !!planId && (byDate[wDayBefore] || []).some(j => j.plan_id === planId && j.machine_id === machineId && j.status !== 'cancelled');
                  const continuesAfter = !!planId && (byDate[wDayAfter]  || []).some(j => j.plan_id === planId && j.machine_id === machineId && j.status !== 'cancelled');
                  return { startCol: entries[0].colIdx, endCol: entries[entries.length - 1].colIdx, jobs, totalQty: jobs.reduce((s, j) => s + (j.target_qty || 0), 0), key: planId || `solo-${jobs[0].id}`, cameFromBefore, continuesAfter };
                });
                const sorted = [...spans].sort((a, b) => a.startCol - b.startCol);
                const laneEnds = [];
                const spansWithLane = sorted.map(span => {
                  let lane = laneEnds.findIndex(end => end < span.startCol);
                  if (lane === -1) { lane = laneEnds.length; laneEnds.push(-1); }
                  laneEnds[lane] = span.endCol;
                  return { ...span, lane };
                });

                return (
                  <div key={`${year}-${week}`} style={{ borderTop: `1px solid ${PB}`, paddingTop: '2px', marginBottom: '4px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px' }}>
                      {wDays.map(day => {
                        const ds = fmt(day); const isToday = ds === TODAY; const holiday = holidays[ds]; const isWknd = isWeekendStr(ds); const nonWork = holiday || isWknd; const isCurMonth = day.getMonth() === curMonth;
                        return (
                          <div key={ds} style={{ backgroundImage: nonWork ? crossHatchPrint() : undefined, backgroundColor: isToday ? 'rgba(20,184,166,0.06)' : nonWork ? '#f3f4f6' : 'white', border: `1px solid ${isToday ? 'rgba(20,184,166,0.35)' : PB}`, borderRadius: '4px', padding: '3px 5px', minHeight: '34px', opacity: isCurMonth ? 1 : 0.3 }}>
                            <span style={{ fontSize: '13px', fontWeight: isToday ? '700' : '400', color: isToday ? '#0d9488' : holiday ? '#b45309' : isWknd ? '#9ca3af' : PT, lineHeight: 1 }}>{day.getDate()}</span>
                            {holiday && isCurMonth && <div style={{ fontSize: '7px', color: '#b45309', lineHeight: 1.1, marginTop: '1px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{holiday}</div>}
                          </div>
                        );
                      })}
                    </div>
                    {spansWithLane.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: `repeat(${laneEnds.length}, 24px)`, gap: '2px', marginTop: '2px' }}>
                        {spansWithLane.map(span => (
                          <div key={span.key} style={{ gridColumn: `${span.startCol + 1} / ${span.endCol + 2}`, gridRow: span.lane + 1 }}>
                            <PrintSpanTile jobs={span.jobs} totalQty={span.totalQty} colours={colours} cameFromBefore={span.cameFromBefore} continuesAfter={span.continuesAfter} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ── Completed-job conflict modal ──────────────────────────────
// Shown when a drag lands on a slot occupied by a completed job.
// The director picks the date the job was actually finished; the system
// shifts the completed plan backward so its last day falls on that date.
function CompletedJobConflictModal({ conflictName, conflictPlanLength, today, onConfirm, onCancel }) {
  const [date, setDate] = React.useState(today);
  const st = {
    overlay:  { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, fontFamily: 'system-ui' },
    card:     { background: '#1e2329', border: '1px solid #2a3040', borderRadius: 16, width: '90%', maxWidth: 420 },
    head:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 14px', borderBottom: '1px solid #2a3040' },
    title:    { fontSize: 16, fontWeight: 700 },
    body:     { padding: '16px 20px 8px', fontSize: 13, color: '#9ca3af', lineHeight: 1.6 },
    label:    { display: 'block', marginBottom: 6, fontSize: 12, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' },
    input:    { width: '100%', boxSizing: 'border-box', padding: '9px 12px', background: '#141920', border: '1px solid #2a3040', borderRadius: 8, color: '#e5e7eb', fontSize: 14, outline: 'none' },
    footer:   { padding: '12px 20px 20px', display: 'flex', gap: 8, justifyContent: 'flex-end' },
    btnPrim:  { padding: '9px 20px', background: '#14b8a6', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 },
    btnGhost: { padding: '9px 16px', background: 'transparent', color: '#6b7280', border: '1px solid #2a3040', borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  };
  return (
    <div style={st.overlay} onClick={onCancel}>
      <div style={st.card} onClick={e => e.stopPropagation()}>
        <div style={st.head}>
          <div style={st.title}>Job already completed</div>
          <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 16, cursor: 'pointer' }} onClick={onCancel}>✕</button>
        </div>
        <div style={st.body}>
          <span style={{ color: '#34d399', fontWeight: 600 }}>{conflictName}</span>
          {conflictPlanLength > 1 && ` (${conflictPlanLength}-day run)`} is marked complete but its scheduled slot is in the way.
          <br /><br />
          Enter the date this job was <strong>actually finished</strong>. The system will shift it backward so its last day lands on that date, freeing the slot for the new job.
        </div>
        <div style={{ padding: '4px 20px 8px' }}>
          <label style={st.label}>Actual completion date</label>
          <input
            type="date"
            max={today}
            value={date}
            onChange={e => setDate(e.target.value)}
            style={st.input}
          />
        </div>
        <div style={st.footer}>
          <button style={st.btnGhost} onClick={onCancel}>Cancel</button>
          <button style={st.btnPrim} onClick={() => date && onConfirm(date)} disabled={!date}>
            Move back &amp; place new job
          </button>
        </div>
      </div>
    </div>
  );
}

// True when a horizontally scrollable element can no longer move in `dir`.
// The 1px slack absorbs sub-pixel rounding, which otherwise leaves scrollLeft a
// fraction short of the maximum forever and the escalation never fires.
function atScrollEnd(sc, dir) {
  return dir < 0
    ? sc.scrollLeft <= 0
    : sc.scrollLeft >= sc.scrollWidth - sc.clientWidth - 1;
}

// ── Capacity breakdown ────────────────────────────────────────
// Shows how the day count was reached, so "why is this 12 days?" is answerable
// without leaving the form — and so a wrong cycle time or cavity count is
// obvious at the point of use rather than after the run.

function CapacityBreakdown({ plan, totalQty }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);

  const row   = { display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '2px 0' };
  const label = { color: '#9ca3af' };
  const val   = { color: '#f0f2f5', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };
  const sub   = { fontSize: '10px', color: '#4b5563' };
  const rule  = { borderTop: '1px solid #2a3040', margin: '5px 0' };

  // Reads as a control, not a caption — the previous muted label was being
  // missed entirely.
  const lit = open || hover;

  return (
    <div style={{ marginBottom: '8px' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '7px',
          background: lit ? 'rgba(20,184,166,0.12)' : 'rgba(255,255,255,0.035)',
          border: `1px solid ${lit ? 'rgba(20,184,166,0.5)' : '#2a3040'}`,
          borderRadius: '7px', padding: '6px 11px', marginBottom: '8px',
          color: lit ? '#14b8a6' : '#9ca3af',
          fontSize: '11px', fontWeight: '700', fontFamily: 'system-ui',
          cursor: 'pointer', transition: 'background 0.13s, border-color 0.13s, color 0.13s',
        }}
      >
        <i className="fa-solid fa-calculator" style={{ fontSize: '10px' }} />
        How this was worked out
        <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: '8px', opacity: 0.75 }} />
      </button>

      {open && (
        <div style={{ background: '#131920', border: '1px solid #2a3040', borderRadius: '6px', padding: '9px 11px', fontSize: '11px', marginBottom: '8px' }}>

          <div style={row}>
            <span style={label}>Shift{plan.shifts > 1 ? ' 1' : ''}</span>
            <span style={val}>8h 00m</span>
          </div>
          <div style={row}>
            <span style={label}>
              Machine warm-up
              {plan.usingDefaultWarmup && <span style={sub}> · assumed{plan.machineName ? `, none set on ${plan.machineName}` : ''}</span>}
            </span>
            <span style={val}>− {fmtHm(plan.machineWarmup * 60)}</span>
          </div>
          {plan.mouldStartup > 0 && (
            <div style={row}>
              <span style={label}>Mould startup</span>
              <span style={val}>− {fmtHm(plan.mouldStartup * 60)}</span>
            </div>
          )}
          {plan.shifts > 1 && (
            <div style={row}>
              <span style={label}>Shift 2 <span style={sub}>· starts warm</span></span>
              <span style={val}>+ {fmtHm(SHIFT_2_EFF_SECS)}</span>
            </div>
          )}

          <div style={rule} />

          <div style={row}>
            <span style={label}>Productive time</span>
            <span style={{ ...val, fontWeight: '700' }}>{fmtHm(plan.dailyEffSecs)}</span>
          </div>
          <div style={row}>
            <span style={label}>÷ {plan.cycleS}s cycle</span>
            <span style={val}>{plan.shotsPerDay.toLocaleString()} shots</span>
          </div>
          <div style={row}>
            <span style={label}>× {plan.cavities} {plan.cavities === 1 ? 'cavity' : 'cavities'}</span>
            <span style={{ ...val, color: '#14b8a6', fontWeight: '700' }}>{plan.partsPerDay.toLocaleString()} pcs/day</span>
          </div>

          {plan.isMultiDay && plan.changeoverMin > 0 && (
            <>
              <div style={rule} />
              <div style={row}>
                <span style={label}>Day 1 changeover <span style={sub}>· mount + unmount</span></span>
                <span style={val}>− {fmtHm(plan.changeoverMin * 60)}</span>
              </div>
              <div style={row}>
                <span style={label}>Day 1 output</span>
                <span style={{ ...val, color: '#f59e0b', fontWeight: '700' }}>{plan.day1Parts.toLocaleString()} pcs</span>
              </div>
            </>
          )}

          <div style={rule} />

          <div style={row}>
            <span style={label}>{totalQty.toLocaleString()} pcs to make</span>
            <span style={{ ...val, color: '#14b8a6', fontWeight: '700' }}>
              {plan.daysNeeded} working day{plan.daysNeeded !== 1 ? 's' : ''}
            </span>
          </div>

          <div style={{ ...sub, marginTop: '7px', lineHeight: 1.5 }}>
            Weekends, holidays and closures are skipped when the days are laid out.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Non-working day drop confirmation modal ───────────────────

function NonWorkingDropModal({ state, holidays, onExecute, onClose }) {
  const { targetDate, planLength, blockLen } = state;
  const [n, setN] = useState(Math.max(1, Math.min(blockLen, planLength)));

  const calDays = Math.max(1, Math.min(n, planLength));
  const overrideDates = [];
  const restDates = [];
  let d = new Date(targetDate + 'T12:00:00');
  for (let i = 0; i < calDays; i++) {
    overrideDates.push(fmt(d));
    d = addDays(d, 1);
  }
  let added = 0;
  for (let safety = 0; safety < 500 && added < planLength - calDays; safety++) {
    const ds = fmt(d);
    if (!isWeekendStr(ds) && !holidays[ds]) { restDates.push(ds); added++; }
    d = addDays(d, 1);
  }

  const dayLabel = isWeekendStr(targetDate) ? 'weekend' : (holidays[targetDate] || 'non-working day');
  const backdrop = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, fontFamily: 'system-ui' };
  const card = { background: '#1e2329', border: '1px solid #2a3040', borderRadius: '16px', width: '90%', maxWidth: '460px' };

  return (
    <div style={backdrop} onClick={onClose}>
      <div style={card} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px 14px', borderBottom: '1px solid #2a3040' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fa-solid fa-calendar-xmark" style={{ color: '#f59e0b', fontSize: '14px' }} />
            <span style={{ fontSize: '15px', fontWeight: '700' }}>Drop on non-working day</span>
          </div>
          <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '16px', cursor: 'pointer' }} onClick={onClose}>✕</button>
        </div>

        <div style={{ padding: '14px 20px 6px', fontSize: '12px', color: '#9ca3af' }}>
          <strong style={{ color: '#f0f2f5' }}>{targetDate}</strong> is a {dayLabel}.
          {planLength === 1
            ? ' Place this job here anyway?'
            : ' Choose how to handle the non-working days.'}
        </div>

        {planLength > 1 && (
          <div style={{ margin: '10px 20px', padding: '10px 14px', background: '#141920', borderRadius: '10px', border: '1px solid #2a3040' }}>
            <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>Date preview</div>
            <div style={{ fontSize: '12px', color: '#f0f2f5', lineHeight: '1.6' }}>
              <span style={{ color: '#f59e0b' }}>{overrideDates.join(' · ')}</span>
              {restDates.length > 0 && (
                <>
                  <span style={{ color: '#4b5563', margin: '0 6px' }}>|</span>
                  <span style={{ color: '#14b8a6' }}>{restDates.join(' · ')}</span>
                </>
              )}
            </div>
            <div style={{ fontSize: '10px', marginTop: '6px', color: '#4b5563' }}>
              <span style={{ color: '#f59e0b' }}>●</span> calendar days &nbsp;
              {restDates.length > 0 && <><span style={{ color: '#14b8a6' }}>●</span> working days</>}
            </div>
          </div>
        )}

        {planLength > 1 && (
          <div style={{ padding: '4px 20px 14px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12px', color: '#9ca3af' }}>
            <label style={{ whiteSpace: 'nowrap' }}>Days on non-working schedule:</label>
            <input
              type="number" min="1" max={planLength} value={n}
              onChange={e => setN(Math.max(1, Math.min(planLength, parseInt(e.target.value, 10) || 1)))}
              style={{ width: '56px', padding: '4px 8px', background: '#141920', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', fontSize: '12px', fontFamily: 'system-ui', textAlign: 'center' }}
            />
            <span style={{ color: '#4b5563' }}>/ {planLength}</span>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '0 20px 20px' }}>
          {planLength === 1 ? (
            <button
              onClick={() => onExecute(1)}
              style={{ background: '#f59e0b', color: '#000', border: 'none', borderRadius: '8px', padding: '11px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui' }}>
              Acknowledge &amp; place here
            </button>
          ) : (
            <>
              <button
                onClick={() => onExecute(planLength)}
                style={{ background: '#1a2030', border: '1px solid #2a3040', borderRadius: '8px', padding: '11px 16px', textAlign: 'left', cursor: 'pointer', fontFamily: 'system-ui', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#f59e0b'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#2a3040'}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#f0f2f5', marginBottom: '2px' }}>Drop all {planLength} days here</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>All days placed as consecutive calendar days from {targetDate}</div>
              </button>
              <button
                onClick={() => onExecute(calDays)}
                style={{ background: '#1a2030', border: '1px solid #2a3040', borderRadius: '8px', padding: '11px 16px', textAlign: 'left', cursor: 'pointer', fontFamily: 'system-ui', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#14b8a6'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#2a3040'}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#f0f2f5', marginBottom: '2px' }}>Split — {calDays} day{calDays !== 1 ? 's' : ''} here, rest on working days</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>
                  {restDates.length > 0 ? `${restDates.length} day${restDates.length !== 1 ? 's' : ''} continue from ${restDates[0]}` : 'All days fit in the calendar block'}
                </div>
              </button>
            </>
          )}
          <button
            onClick={onClose}
            style={{ background: 'none', border: '1px solid #2a3040', borderRadius: '8px', padding: '9px 18px', fontSize: '13px', color: '#6b7280', cursor: 'pointer', fontFamily: 'system-ui' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────

// What a run's days made: each production record ONCE. Since 1 Oct 2026 every
// day of a run points at the run's one record, so adding it per day counted a
// five-day run five times (2 Oct 2026).
function madeOnce(days) {
  const seen = new Map();
  for (const d of days || []) {
    const id = d.job_id ?? d.job?.id;
    if (id == null || seen.has(id)) continue;
    seen.set(id, d.job?.good_parts_count || 0);
  }
  let total = 0;
  for (const v of seen.values()) total += v;
  return total;
}

export default function Schedule() {
  const goTo   = useNavigate();
  const goBack = useBack('/admin');
  const { isDirector, can } = useAuth();
  const mayPlan = can('plan');
  const { toast } = useToast();

  // What a push did that the director would want to know: a run that jumped
  // past one that is running or finished, or one that will now be late. Shown
  // in the corner and left to fade -- his choice, 25 Sep. The sentences are
  // written by the server (utils/cascade.js), the same for every kind of move.
  const showMoveNotes = (notes) => {
    for (const n of notes || []) {
      if (n.kind === 'late') toast.warning(n.says, 8000);
      else toast.info(n.says, 7000);
    }
  };

  const [viewDate, setViewDate]   = useState(new Date());
  const [viewMode, setViewMode]       = useState(() => {
    const p = localStorage.getItem('schedule_default_view') || 'arrow';
    return (p === 'day') ? 'day' : (p === 'month') ? 'month' : 'week';
  });
  const [weekSpan, setWeekSpan]       = useState(() => {
    const p = localStorage.getItem('schedule_default_view') || 'arrow';
    if (p === '2w') return 2;
    if (p === '3w') return 3;
    if (p === '4w') return 4;
    if (p === 'arrow') return 5; // >4 means → is active; real span applied once data loads
    return 1;
  });
  const arrowAutoApplied = useRef(false);
  const [showMenu, setShowMenu]       = useState(false);
  const [showCapacity, setShowCapacity] = useState(false);
  const [showWindows, setShowWindows]   = useState(false);
  const [showMachineOrder, setShowMachineOrder] = useState(false);
  const [shiftWindows, setShiftWindows] = useState([]);
  // Days a single machine is down (migration 064), and the machine whose down
  // days are being edited.
  const [downtime, setDowntime] = useState([]);
  const [downTarget, setDownTarget] = useState(null);
  const [reflowFor, setReflowFor]       = useState(null); // { ids, names } | null
  const [showHistory, setShowHistory]   = useState(false);
  const [lastJobDate, setLastJobDate] = useState(null);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [machines, setMachines]   = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [parts, setParts]         = useState([]);
  const [moulds, setMoulds]       = useState([]);
  const [recipes, setRecipes]     = useState([]);
  const [colours, setColours]     = useState([]);
  // Each part's base material and place in the colour order (Settings), for
  // the purge marker between runs. Read once; it changes only when saved.
  const [partColours, setPartColours] = useState({});
  useEffect(() => {
    api.get('/api/v1/materials/colour-order')
      .then(r => setPartColours((r.data && r.data.parts) || {}))
      .catch(() => {});
  }, []);
  const [dispatchDates, setDispatchDates] = useState({}); // { dateStr: { type: 'confirmed'|'suggested', orders: [...] } }
  const [loading, setLoading]     = useState(true);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [editJob, setEditJob]     = useState(undefined);
  // "+ Staff": extra people on particular days (28 Sep). Saving bumps the seq
  // so the day headers ask again what each shift needs against what it has.
  const [showStaff, setShowStaff]     = useState(false);
  const [staffingSeq, setStaffingSeq] = useState(0);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [moveConfirm, setMoveConfirm]     = useState(null); // { job, targetDate, targetMachineId, remainingCount, totalCount }
  const [dropConflict, setDropConflict]   = useState(null); // { job, targetDate, targetMachineId, says, options, warnings } -- from POST /conflicts/drop
  const [startedConflict, setStartedConflict]     = useState(null); // { job, targetDate, targetMachineId, conflictName, conflictJobId, conflictPlanId }
  const [completedConflict, setCompletedConflict] = useState(null); // { job, targetDate, targetMachineId, conflictName, conflictJobId, conflictPlanId, conflictPlanLength }
  const [resizeConflict, setResizeConflict] = useState(null); // { type:'extend'|'prepend', planId, newDates, template, machineId, conflictName, conflictPlanFirstDate, conflictPlanId, conflictJobId }
  const [visScrollWeek, setVisScrollWeek] = useState(null); // week number at the left edge of the scroll view; null = use navWeek
  // Bumped when the director asks for today (the Today button). The board
  // centres on today then, on first opening and on a change of span -- and
  // at no other time, so the week arrows move it one week and leave it there.
  const [centerTodaySeq, setCenterTodaySeq] = useState(0);
  // The engine's verdict per cell while a card is in the air (WeekView), so a
  // drop onto a tinted cell can ask before it lands whichever way it is made.
  const dropHintsRef = useRef(null);
  const [scrollIntent, setScrollIntent]   = useState(null); // { date, seq } — tells WeekView to center on a specific column
  const [liveJobId, setLiveJobId]         = useState(null);
  const [jobPlanTotals] = useState({});
  const [planTotalsMap, setPlanTotalsMap] = useState({});
  const isMoving = useRef(false);
  // The Move window: { planId, jobId } from a run's bar, or { onMachineId }
  // from a machine's wrench (21 Oct 2026).
  const [moveTarget, setMoveTarget] = useState(null);
  const [nonWorkingDrop, setNonWorkingDrop] = useState(null); // { job, targetDate, targetMachineId, planLength, blockLen }
  const undoStack = useRef([]);
  const redoStack = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  // What the server says is one step either way. Drives the button labels, so
  // neither of them ever reads just "Undo" and makes you press it to find out.
  const [history, setHistory] = useState({ undo: null, redo: null, steps_back: 0 });
  // Rows the last step moved, highlighted on the board for a few seconds so the
  // change is SEEN rather than described.
  const [justMoved, setJustMoved] = useState(null);
  // Why things move. The list is the director's and lives in the database, so
  // adding "masterbatch late" is a row rather than a deploy.
  const [reasons, setReasons] = useState([]);
  const [reasonMode, setReasonMode] = useState('optional');
  // Moves still waiting for a reason, oldest first. A queue rather than a
  // single slot: when the strip stayed open and another move landed, the first
  // one's question used to be silently replaced.
  const [reasonQueue, setReasonQueue] = useState([]);
  const [reasonPaused, setReasonPaused] = useState(false);

  // What is ringed on the board: whatever an undo or redo just moved, plus the
  // move the "why?" strip is currently asking about. Seeing which run the
  // question is about beats reading a sentence describing it -- and it answers
  // "which of the three am I replying to?" without a word.
  const ringIds = useMemo(() => {
    const ids = new Set(justMoved || []);
    (reasonQueue[0]?.moved_ids || []).forEach(id => ids.add(id));
    return ids.size ? ids : null;
  }, [justMoved, reasonQueue]);
  const executeUndoRef = useRef(null);
  const executeRedoRef = useRef(null);
  const closeTopModalRef = useRef(null);
  const [filterText, setFilterText]       = useState(() => localStorage.getItem('schedule_default_filter') || '');
  const [compactMachines, setCompactMachines] = useState(new Set());
  const toggleCompact = id => setCompactMachines(prev => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  const compactDefaultApplied = useRef(false);
  const urgencyDays = useMemo(() => ({
    red: parseInt(localStorage.getItem('schedule_urgency_red') || '2', 10),
    amber: parseInt(localStorage.getItem('schedule_urgency_amber') || '7', 10),
  }), []); // eslint-disable-line
  const showCompleted = useMemo(() => localStorage.getItem('schedule_show_completed') !== 'false', []); // eslint-disable-line
  const [showImport, setShowImport]       = useState(false);
  const [showPrint, setShowPrint]         = useState(false);
  const [showCopyWeek, setShowCopyWeek]   = useState(false);
  const [completingPlan, setCompletingPlan] = useState(null); // { planId, partName, totalQty, goodPartsMade }
  const [allDeliveryWeekSet, setAllDeliveryWeekSet] = useState(new Set());
  const [closures, setClosures] = useState([]);

  const TODAY    = fmt(new Date());
  const holidays = {
    ...buildHolidays(viewDate),
    ...closuresToMap(closures),
  };

  function load(showSpinner = true) {
    if (showSpinner) setLoading(true);
    const { from, to } = getRange(viewDate, viewMode, weekSpan);
    Promise.all([
      api.get('/api/v1/machines'),
      api.get(`/api/v1/schedule?from=${from}&to=${to}`),
      api.get('/api/v1/parts'),
      api.get('/api/v1/moulds'),
      api.get('/api/v1/recipes'),
      api.get('/api/v1/colours').catch(() => ({ data: [] })),
      api.get('/api/v1/orders/dispatch-dates').catch(() => ({ data: [] })),
      api.get(`/api/v1/shift-windows?from=${from}&to=${to}`).catch(() => ({ data: [] })),
      api.get(`/api/v1/downtime?from=${from}&to=${to}`).catch(() => ({ data: { blocks: [] } })),
    ]).then(([m, s, p, mo, r, co, dd, sw, dt]) => {
      setMachines(m.data.filter(x => x.status !== 'archived'));
      setScheduled(s.data);
      setParts(p.data);
      setMoulds(mo.data.filter(x => x.status !== 'archived'));
      setRecipes(r.data.filter(x => x.status === 'active'));
      setColours(co.data);
      setDispatchDates(Object.fromEntries((dd.data || []).map(d => [d.date, { type: d.type, orders: d.orders || [] }])));
      setShiftWindows(Array.isArray(sw.data) ? sw.data : []);
      setDowntime((dt.data && dt.data.blocks) || []);
      setLoading(false);
      // Fetch full plan totals (target + made) for every plan visible in this window
      const planIds = [...new Set(s.data.map(j => j.plan_id).filter(Boolean))];
      if (planIds.length > 0) {
        api.post('/api/v1/schedule/plan-totals', { plan_ids: planIds })
          .then(r2 => setPlanTotalsMap(r2.data || {}))
          .catch(() => {});
      } else {
        setPlanTotalsMap({});
      }
    }).catch(() => setLoading(false));
  }

  // Spinner only on the very first load. Showing it again on every navigation
  // swaps the whole board for a placeholder, which unmounts WeekView and loses
  // scroll position, drag state and any in-flight placement — the new data
  // should arrive underneath the grid, exactly as the 20s poll already does.
  // Which shifts a machine works on a given day, if a window says more than one.
  // Overlaps resolve upward, the same reading the planner takes.
  // Whether the LOADED period holds a multi-shift day at all.
  //
  // Judged on what is loaded, never on what is scrolled into view -- otherwise
  // the toggle flickers on and off as the board is dragged, which is the exact
  // jitter this board has already had eliminated once (§11).
  const hasMultiShiftDays = useMemo(
    () => shiftWindows.some(w => (w.shifts || 1) > 1),
    [shiftWindows]
  );

  // Nothing is stored. The switch exists only to condense the current view;
  // navigate away and back and it derives again from what is actually there,
  // because there is no setting to remember.
  const [condensed, setCondensed] = useState(false);
  useEffect(() => { setCondensed(false); }, [hasMultiShiftDays]);
  const expandShifts = hasMultiShiftDays && !condensed;

  // machine -> date -> the downtime block that takes it out that day.
  const downFor = useMemo(() => {
    const idx = new Map();
    for (const b of downtime) {
      if (!idx.has(b.machine_id)) idx.set(b.machine_id, new Map());
      for (let d = b.date_from, n = 0; d <= b.date_to && n < 400; n++) {
        idx.get(b.machine_id).set(d, b);
        const x = new Date(d + 'T12:00:00Z'); x.setUTCDate(x.getUTCDate() + 1); d = x.toISOString().slice(0, 10);
      }
    }
    return (machineId, ds) => (idx.get(machineId) || new Map()).get(ds) || null;
  }, [downtime]);

  const shiftWindowFor = useMemo(() => {
    const byMachine = new Map();
    for (const w of shiftWindows) {
      if (!byMachine.has(w.machine_id)) byMachine.set(w.machine_id, []);
      byMachine.get(w.machine_id).push(w);
    }
    return (machineId, ds) => {
      const list = byMachine.get(machineId);
      if (!list) return null;
      let best = null;
      for (const w of list) {
        if (ds < w.start_date || ds > w.end_date) continue;
        if (!best || w.shifts > best.shifts) best = w;
      }
      return best;
    };
  }, [shiftWindows]);

  const didInitialLoad = useRef(false);
  useEffect(() => {
    load(!didInitialLoad.current);
    didInitialLoad.current = true;
    const iv = setInterval(() => load(false), 20000);
    const onVisible = () => { if (document.visibilityState === 'visible') load(false); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { clearInterval(iv); document.removeEventListener('visibilitychange', onVisible); };
  }, [viewDate, viewMode, weekSpan]); // eslint-disable-line

  // Reset scroll-synced week badge whenever the view window changes
  // No reset here on a move: the board reports which week is on screen after
  // every move (WeekView). A reset here ran after that report and put the label
  // back to the first week of the range while another week was showing.

  // One-time fetches that don't depend on the viewed date window
  useEffect(() => {
    api.get('/api/v1/orders/delivery-weeks').then(res => {
      setAllDeliveryWeekSet(new Set(
        res.data.map(r => `${r.delivery_year}-${String(r.delivery_week).padStart(2, '0')}`)
      ));
    }).catch(() => {});
    api.get('/api/v1/closures').then(res => setClosures(res.data)).catch(() => {});
    api.get('/api/v1/schedule?horizon=true').then(res => setLastJobDate(res.data?.last_date || null)).catch(() => {});
  }, []); // eslint-disable-line

  // Full-horizon view: four weeks of history, through to four weeks past the
  // last scheduled job — so there is always visible runway on both sides rather
  // than the window stopping dead on the final run.
  const SPAN_PAD_WEEKS = 4;
  // Every day column is a live drop target that re-renders on each dragover, so
  // the window is bounded at roughly six months. Past that the grid stops being
  // something you can drag on, which is worse than not seeing the far future.
  const SPAN_MAX_WEEKS = 26;

  function handleAllSpan() {
    const todayMon  = getMonday(new Date());
    const viewStart = addDays(todayMon, -7 * SPAN_PAD_WEEKS);
    const lastMon   = lastJobDate ? getMonday(new Date(lastJobDate + 'T12:00:00')) : todayMon;
    // Never end before the padding past today, so an empty or fully-historic
    // schedule still opens on a sensible window instead of collapsing.
    const endMon    = addDays(lastMon > todayMon ? lastMon : todayMon, 7 * SPAN_PAD_WEEKS);
    const weeks     = Math.round((endMon - viewStart) / (7 * 86400000)) + 1;
    setViewMode('week');
    setViewDate(viewStart);
    setWeekSpan(Math.min(SPAN_MAX_WEEKS, Math.max(weeks, SPAN_PAD_WEEKS * 2 + 1)));
  }

  // Auto-apply → view once lastJobDate loads (only when default pref is 'arrow')
  useEffect(() => {
    const pref = localStorage.getItem('schedule_default_view') || 'arrow';
    if (pref === 'arrow' && lastJobDate && !arrowAutoApplied.current) {
      arrowAutoApplied.current = true;
      handleAllSpan();
    }
  }, [lastJobDate]); // eslint-disable-line

  // Apply compact-all default once machines first load
  useEffect(() => {
    if (machines.length && !compactDefaultApplied.current && localStorage.getItem('schedule_compact_default') === 'true') {
      compactDefaultApplied.current = true;
      setCompactMachines(new Set(machines.map(m => m.id)));
    }
  }, [machines.length]); // eslint-disable-line

  // Auto-refresh schedule data
  useEffect(() => {
    const interval = parseInt(localStorage.getItem('schedule_refresh_interval') || '0', 10);
    if (!interval) return;
    const id = setInterval(() => load(false), interval * 1000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line

  const refreshHistory = useCallback(async () => {
    if (!mayPlan) return; // undo and redo are the director's
    try {
      const { data } = await api.get('/api/v1/history/state');
      setHistory(data);
      setCanUndo(!!data.undo);
      setCanRedo(!!data.redo);
    } catch {
      // The board does not depend on this. If it cannot be read, the buttons
      // simply go quiet rather than the page breaking.
      setCanUndo(false); setCanRedo(false);
    }
  }, [mayPlan]);

  useEffect(() => { refreshHistory(); }, [refreshHistory]);

  useEffect(() => {
    api.get('/api/v1/reasons')
      .then(({ data }) => {
        setReasons(data.reasons || []);
        setReasonMode(data.mode || 'optional');
        setReasonPaused(!!data.paused_until);
      })
      .catch(() => setReasonMode('off'));   // unreadable means unasked, never a broken board
  }, []);

  // Asked after the change has landed, not before it. The entry the board just
  // wrote is whatever undo would now step back over, so no route had to be
  // changed to hand its id back.
  const askForReason = useCallback(async () => {
    if (reasonMode === 'off') return;
    try {
      const { data } = await api.get('/api/v1/history/state');
      setHistory(data);
      setCanUndo(!!data.undo); setCanRedo(!!data.redo);
      const ev = data.undo;
      if (ev && !ev.reason_code && !reasonPaused) {
        setReasonQueue(q => q.some(x => x.event_id === ev.event_id) ? q : [...q, ev]);
      }
    } catch { /* the move is saved either way */ }
  }, [reasonMode, reasonPaused]);

  // Paused for the rest of today. The date is the record, so it lapses on its
  // own overnight rather than needing anyone to remember to switch it back.
  const pauseReasons = useCallback(async () => {
    // Optimistic: the question stops the instant it is asked to. If the write
    // fails the next page load tells the truth, and the cost of being wrong for
    // a moment is one question.
    setReasonPaused(true);
    setReasonQueue([]);
    try {
      // The end of the day HERE, not on the server. Render runs in UTC, so a
      // pause taken at four in the afternoon would otherwise expire at two in
      // the morning — technically a day, practically not the one meant.
      const endOfDay = new Date();
      endOfDay.setHours(23, 59, 59, 999);
      await api.post('/api/v1/reasons/pause', { until: endOfDay.toISOString() });
      toast.success('Not asking again today, on any device. Changes are still recorded.');
    } catch {
      setReasonPaused(false);
      toast.error('Could not pause that — it will keep asking');
    }
  }, [toast]);

  const resumeReasons = useCallback(async () => {
    setReasonPaused(false);
    try {
      await api.delete('/api/v1/reasons/pause');
      toast.success('Asking why, again');
    } catch {
      setReasonPaused(true);
      toast.error('Could not resume that');
    }
  }, [toast]);

  // "A change just landed."
  //
  // It kept its name because five call sites already say it at exactly the
  // right moment, but the stack it used to push onto is gone -- the server
  // holds the history now. What it does instead is ask the two questions that
  // follow a change: what can be undone, and why did that happen.
  //
  // The before/after arguments are no longer needed; the server captured its
  // own either side of the write.
  function pushUndo() {
    askForReason();
  }

  // Every row of a run this board has loaded.
  const runRows = (job) => (job?.plan_id
    ? scheduled.filter(j => j.plan_id === job.plan_id)
    : scheduled.filter(j => j.id === job?.id));

  // A failed move, said once. When someone else changed the run first, the
  // board reloads so the sentence and the screen agree.
  function moveFailed(err, fallback) {
    const d = err.response?.data;
    toast.error(d?.error || fallback, d?.stale ? 9000 : undefined);
    if (d?.stale) load(false);
  }

  // A drop on a day that is taken. The options come from the conflict engine
  // and what each would move from the one cascade, both on the server
  // (POST /conflicts/drop) -- so every button can say what it will do, and
  // what it said is what happens. The board decides nothing here itself.
  // Also asked before a drop onto a tinted cell: the tint promises "you will be
  // asked", and this is the asking. Returns true when there was something to
  // say and the pop-up is open; false when the drop can simply go ahead.
  async function openDropOptions(job, targetDate, targetMachineId) {
    try {
      const { data } = await api.post('/api/v1/conflicts/drop', {
        plan_id: job.plan_id || null,
        job_id: job.id,
        job_original_date: job.scheduled_date,
        target_date: targetDate,
        target_machine_id: targetMachineId,
      });
      if (data.free && !(data.warnings || []).some(w => w.severity !== 'note')) return false;
      setDropConflict({ job, targetDate, targetMachineId, ...data, seen: runRows(job) });
      return true;
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not work out the options for this drop');
      return true;   // nothing moved; the message says why
    }
  }

  // One option, one request, one step in the history.
  async function executeDropOption(opt) {
    const steps = opt?.requests || (opt?.request ? [opt.request] : []);
    if (isMoving.current || !steps.length) return;
    isMoving.current = true;
    const seen = dropConflict?.seen || [];
    setDropConflict(null);
    try {
      // An option may be two requests in one click -- "Add a person" writes the
      // dated change to that shift, then makes the drop -- in that order.
      let data = null;
      for (const r of steps) {
        const isMove = /\/schedule\//.test(r.url);
        ({ data } = await api[r.method](r.url, r.body, isMove ? withExpect(seen) : {}));
      }
      load(false);
      pushUndo();
      showMoveNotes(data?.notes);
      offerGapClose(data?.close_gap);
    } catch (err) {
      moveFailed(err, 'Move failed');
    } finally {
      isMoving.current = false;
    }
  }

  // A move that left a gap the runs behind could close. Offered, never done
  // by itself: a gap is sometimes deliberate (the director's choice, 25 Sep).
  function offerGapClose(offer) {
    if (!offer) return;
    toast.offer(offer.says, {
      label: 'Close the gap',
      onClick: async () => {
        try {
          const { data } = await api.post('/api/v1/schedule/close-gap', {
            machine_id: offer.machine_id, from: offer.from, to: offer.to, keep_ids: offer.keep_ids,
          });
          load(false);
          pushUndo();
          toast.success(`Gap closed — ${data.moved} run${data.moved === 1 ? '' : 's'} moved up`);
          showMoveNotes(data?.notes);
        } catch (err) {
          toast.error(err.response?.data?.error || 'Could not close the gap');
        }
      },
    });
  }

  // Both directions are the same call with a different word, because they are
  // the same thing pointed two ways. Anything that is NOT "back one step" gets
  // a different name entirely -- "Revert to this point" jumps to a moment.
  // The old in-memory undo/redo pair lived here. They are gone: a stack in a
  // useRef could not survive a refresh, and popping the most recent entry of
  // ANY type is what undid a drag when a re-flow was meant.

  async function stepHistory(direction) {
    if (!mayPlan) return;
    try {
      const { data } = await api.post(`/api/v1/history/${direction}`);
      const moved = [
        ...(data.moved_ids || []),
      ];
      setJustMoved(moved.length ? new Set(moved) : null);
      if (direction === 'undo' && data.event_id) {
        setReasonQueue(q => q.filter(x => x.event_id !== data.event_id));
      }
      if (moved.length) setTimeout(() => setJustMoved(null), 6000);

      await load(false);
      await refreshHistory();

      // Say what the STEP did, not what the original change did. The summary is
      // the entry's own sentence -- "1 day placed on the board" -- and shown bare
      // after an undo it read as though a day had just been placed, when the
      // undo had just removed it. The word in front is the whole difference.
      const verb = direction === 'undo' ? 'Undone' : 'Redone';
      const what = data.summary ? `${verb} — ${data.summary}` : `Change ${verb.toLowerCase()}`;
      toast.success(data.note ? `${what}. ${data.note}` : what);
    } catch (err) {
      const r = err?.response?.data;
      if (r?.options) {
        // Never a bare refusal: the reason, then the ways forward.
        toast.error([r.error, r.why].filter(Boolean).join(' '));
        (r.options || []).forEach(o => console.info('[history]', o.label, '—', o.why));
      } else {
        toast.error(r?.error || `${direction === 'undo' ? 'Undo' : 'Redo'} failed`);
      }
      await refreshHistory();
    }
  }



  async function handleResize({ type, span, machineId, newDates, cutoffDate }) {
    const planId = span.jobs[0]?.plan_id;
    if (!planId) return;

    if (type === 'extend') {
      // Fetch full plan to use last entry as template for new rows
      const { data: allEntries } = await api.get(`/api/v1/schedule?plan_id=${planId}`);
      const planEntries = (allEntries || [])
        .filter(j => j.status === 'scheduled')
        .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
      const template = planEntries[planEntries.length - 1];
      if (!template) return;

      const conflictDate = newDates.find(d =>
        scheduled.find(j => j.machine_id === machineId && j.scheduled_date === d && j.status !== 'cancelled')
      );
      if (conflictDate) {
        const conflictingJob = scheduled.find(j => j.machine_id === machineId && j.scheduled_date === conflictDate && j.status !== 'cancelled');
        const cPlanId = conflictingJob?.plan_id || null;
        const conflictPlanFirstDate = cPlanId
          ? scheduled.filter(j => j.plan_id === cPlanId && j.status !== 'cancelled').map(j => j.scheduled_date).sort()[0]
          : conflictDate;
        setResizeConflict({
          type: 'extend', planId, newDates, template, machineId,
          conflictName: conflictingJob?.parts?.name || 'Unknown',
          conflictPlanFirstDate,
          conflictPlanId: cPlanId,
          conflictJobId: conflictingJob?.id,
        });
        return;
      }

      const jobs = newDates.map(d => ({
        machine_id: machineId,
        part_id: template.part_id || null,
        mould_id: template.mould_id || null,
        recipe_id: template.recipe_id || null,
        target_qty: template.target_qty || null,
        scheduled_date: d,
        order_line_id: template.order_line_id || null,
      }));

      try {
        const { data: created } = await api.post('/api/v1/schedule/batch', { plan_id: planId, jobs });
        const createdIds = (created || []).map(j => j.id);
        load(false);
        undoStack.current.push({ type: 'extend', createdIds });
        redoStack.current = [];
        setCanUndo(true);
        setCanRedo(false);
      } catch (err) {
        toast.error(err.response?.data?.error || 'Extend failed');
      }

    } else if (type === 'shrink') {
      // Fetch full plan and remove entries strictly after the cutoff date
      const { data: allEntries } = await api.get(`/api/v1/schedule?plan_id=${planId}`);
      const toRemove = (allEntries || []).filter(j => j.scheduled_date > cutoffDate && j.status === 'scheduled');
      if (toRemove.length === 0) return;

      const removedRows = toRemove.map(j => ({
        id: j.id,
        machine_id: j.machine_id,
        part_id: j.part_id || null,
        mould_id: j.mould_id || null,
        recipe_id: j.recipe_id || null,
        target_qty: j.target_qty || null,
        scheduled_date: j.scheduled_date,
        order_line_id: j.order_line_id || null,
        plan_id: j.plan_id,
      }));

      try {
        await api.delete('/api/v1/schedule/batch', { data: { ids: removedRows.map(r => r.id) } });
        load(false);
        undoStack.current.push({ type: 'shrink', planId, removedRows });
        redoStack.current = [];
        setCanUndo(true);
        setCanRedo(false);
      } catch (err) {
        toast.error(err.response?.data?.error || 'Shrink failed');
      }

    } else if (type === 'prepend') {
      // Use the FIRST entry as template for new rows prepended to the left
      const { data: allEntries } = await api.get(`/api/v1/schedule?plan_id=${planId}`);
      const planEntries = (allEntries || [])
        .filter(j => j.status === 'scheduled')
        .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
      const template = planEntries[0];
      if (!template) return;

      const conflictDate = newDates.find(d =>
        scheduled.find(j => j.machine_id === machineId && j.scheduled_date === d && j.status !== 'cancelled')
      );
      if (conflictDate) {
        const conflictingJob = scheduled.find(j => j.machine_id === machineId && j.scheduled_date === conflictDate && j.status !== 'cancelled');
        const cPlanId = conflictingJob?.plan_id || null;
        const conflictPlanFirstDate = cPlanId
          ? scheduled.filter(j => j.plan_id === cPlanId && j.status !== 'cancelled').map(j => j.scheduled_date).sort()[0]
          : conflictDate;
        setResizeConflict({
          type: 'prepend', planId, newDates, template, machineId,
          conflictName: conflictingJob?.parts?.name || 'Unknown',
          conflictPlanFirstDate,
          conflictPlanId: cPlanId,
          conflictJobId: conflictingJob?.id,
        });
        return;
      }

      const jobs = newDates.map(d => ({
        machine_id: machineId,
        part_id: template.part_id || null,
        mould_id: template.mould_id || null,
        recipe_id: template.recipe_id || null,
        target_qty: template.target_qty || null,
        scheduled_date: d,
        order_line_id: template.order_line_id || null,
      }));

      try {
        const { data: created } = await api.post('/api/v1/schedule/batch', { plan_id: planId, jobs });
        const createdIds = (created || []).map(j => j.id);
        load(false);
        undoStack.current.push({ type: 'extend', createdIds }); // undo = delete the same IDs
        redoStack.current = [];
        setCanUndo(true);
        setCanRedo(false);
      } catch (err) {
        toast.error(err.response?.data?.error || 'Prepend failed');
      }

    } else if (type === 'shrink-left') {
      // Remove entries strictly before the new start date
      const { data: allEntries } = await api.get(`/api/v1/schedule?plan_id=${planId}`);
      const toRemove = (allEntries || []).filter(j => j.scheduled_date < cutoffDate && j.status === 'scheduled');
      if (toRemove.length === 0) return;

      const removedRows = toRemove.map(j => ({
        id: j.id,
        machine_id: j.machine_id,
        part_id: j.part_id || null,
        mould_id: j.mould_id || null,
        recipe_id: j.recipe_id || null,
        target_qty: j.target_qty || null,
        scheduled_date: j.scheduled_date,
        order_line_id: j.order_line_id || null,
        plan_id: j.plan_id,
      }));

      try {
        await api.delete('/api/v1/schedule/batch', { data: { ids: removedRows.map(r => r.id) } });
        load(false);
        undoStack.current.push({ type: 'shrink', planId, removedRows }); // undo = re-insert same rows
        redoStack.current = [];
        setCanUndo(true);
        setCanRedo(false);
      } catch (err) {
        toast.error(err.response?.data?.error || 'Shrink failed');
      }
    }
  }

  // Ctrl+Z and Ctrl+Y step the server-side history, same as the buttons. The
  // local stack functions are kept only for the resize flows that still use
  // their own before/after, and are no longer reachable from the keyboard.
  executeUndoRef.current = () => stepHistory('undo');
  executeRedoRef.current = () => stepHistory('redo');
  closeTopModalRef.current = () => {
    if (showHistory)          { setShowHistory(false);          return; }
    if (reflowFor)            { setReflowFor(null);             return; }
    if (showWindows)          { setShowWindows(false);          return; }
    if (showCapacity)         { setShowCapacity(false);         return; }
    if (showShortcuts)        { setShowShortcuts(false);        return; }
    if (showMenu)             { setShowMenu(false);             return; }
    if (liveJobId)            { setLiveJobId(null);             return; }
    if (completingPlan)       { setCompletingPlan(null);        return; }
    if (dropConflict)         { setDropConflict(null);          return; }
    if (startedConflict)      { setStartedConflict(null);       return; }
    if (completedConflict)    { setCompletedConflict(null);     return; }
    if (resizeConflict)       { setResizeConflict(null);        return; }
    if (moveConfirm)          { setMoveConfirm(null);           return; }
    if (deleteConfirm)        { setDeleteConfirm(null);         return; }
    if (moveTarget)           { setMoveTarget(null);            return; }
    if (pendingPlacement)     { setPendingPlacement(null);      return; }
    if (showCopyWeek)         { setShowCopyWeek(false);         return; }
    if (showImport)           { setShowImport(false);           return; }
    if (showPrint)            { setShowPrint(false);            return; }
    if (editJob !== undefined){ setEditJob(undefined);          return; }
  };
  useEffect(() => {
    function onKey(e) {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        executeUndoRef.current?.();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        executeRedoRef.current?.();
      }
      if (e.key === 'Escape') {
        closeTopModalRef.current?.();
      }
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey &&
          !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
        setShowShortcuts(s => !s);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line

  function jobsFor(machineId, dateStr) {
    return scheduled.filter(j => j.machine_id === machineId && j.scheduled_date === dateStr);
  }

  function prevJobOnMachine(machineId, beforeDate) {
    return scheduled
      .filter(j => j.machine_id === machineId && j.scheduled_date < beforeDate)
      .sort((a, b) => b.scheduled_date.localeCompare(a.scheduled_date))[0] || null;
  }

  function nextJobOnMachine(machineId, afterDate) {
    return scheduled
      .filter(j => j.machine_id === machineId && j.scheduled_date > afterDate)
      .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))[0] || null;
  }

  // Did the machine stand a working day between two runs?
  function stoodBetween(from, to) {
    const d = new Date(from + 'T12:00:00Z');
    for (let i = 0; i < 40; i++) {
      d.setUTCDate(d.getUTCDate() + 1);
      const ds = d.toISOString().slice(0, 10);
      if (ds >= to) return false;
      if (!isWeekendStr(ds) && !holidays[ds]) return true;
    }
    return true;
  }

  // A purge between two runs: another base material, or lighter after darker in
  // the colour order. Nothing when either colour is not known -- not guessed.
  // A machine that stood after PVC or glass-filled PC holds natural LDPE or PP
  // (the director, 8 Oct 2026); after anything else it holds what it ran.
  function getPurge(fromJob, toJob) {
    if (!fromJob || !toJob || fromJob.part_id === toJob.part_id) return null;
    let a = partColours[fromJob.part_id];
    const b = partColours[toJob.part_id];
    if (!a || !b) return null;
    if (a.idle && stoodBetween(fromJob.scheduled_date, toJob.scheduled_date)) a = { known: true, family: a.idle.family, rank: 0, label: a.idle.label };
    if (a.family && b.family && a.family !== b.family) return { kind: 'material', from: a.family, to: b.family };
    if (!a.known || !b.known || b.rank >= a.rank) return null;
    return { kind: 'colour', from: a.label, to: b.label };
  }

  function getChangeover(fromJob, toJob) {
    if (!fromJob || !toJob || fromJob.mould_id === toJob.mould_id) return null;
    const u = fromJob.moulds?.toolchange_unmount_min || 0;
    const m = toJob.moulds?.toolchange_mount_min     || 0;
    if (!u && !m) return null;
    return { total: u + m, fromRef: fromJob.moulds?.ref, toRef: toJob.moulds?.ref };
  }

  function deleteJob(id) {
    const job = scheduled.find(j => j.id === id);
    if (job?.status === 'held') {
      setDeleteConfirm({
        id,
        heldPlanId: job.plan_id,
        holdNote: job.hold_note || null,
        planCount: scheduled.filter(j => j.plan_id === job.plan_id && j.status === 'held').length,
      });
      return;
    }
    if (job?.plan_id) {
      const planJobs   = scheduled.filter(j => j.plan_id === job.plan_id);
      const anyStarted = planJobs.some(j => j.status === 'started' || j.status === 'completed');
      setDeleteConfirm({
        id,
        anyStarted,
        planCount: planJobs.length,
        fromDate: job.scheduled_date,
      });
    } else {
      setDeleteConfirm({ id, single: true });
    }
  }

  function confirmDelete() {
    const { id, heldPlanId } = deleteConfirm;
    setDeleteConfirm(null);
    const req = heldPlanId
      ? api.delete(`/api/v1/orders/schedule/hold/${heldPlanId}`)
      : api.delete(`/api/v1/schedule/${id}`, withExpect(scheduled.filter(j => j.id === id)));
    // Taking a run off offers to close the gap it leaves, as a move does (15 Oct 2026).
    req.then(r => { load(false); if (!heldPlanId) offerGapClose(r?.data?.close_gap); })
      .catch(err => moveFailed(err, 'Could not delete that'));
  }
  async function requestMove(job, targetDate, targetMachineId, fromDrag = false) {
    if (isMoving.current) return;
    isMoving.current = true;
    try {
      if (fromDrag) {
        // A cell the engine tinted while the card was in the air: ask before it
        // lands, so a warning comes with its ways out instead of the drop just
        // happening. A clean cell goes straight through, with no extra wait.
        const hint = dropHintsRef.current && dropHintsRef.current.get(`${targetMachineId}|${targetDate}`);
        if (hint && await openDropOptions(job, targetDate, targetMachineId)) return;

        // Drag / keyboard nudge: backend handles full cascade across all dates (no view-window limit)
        const { data } = await api.put('/api/v1/schedule/plan-move', {
          plan_id: job.plan_id || null,
          job_id: job.id,
          job_original_date: job.scheduled_date,
          target_date: targetDate,
          target_machine_id: targetMachineId,
          scope: 'all',
          push: false,
        }, withExpect(runRows(job)));
        load(false);
        if (data?.snapshot) pushUndo(data.snapshot.before, data.snapshot.after);
        showMoveNotes(data?.notes);
        offerGapClose(data?.close_gap);
        return;
      }

      // Not from drag: show scope modal for multi-day plans
      if (job.plan_id) {
        const { data: allEntries } = await api.get(`/api/v1/schedule?plan_id=${job.plan_id}`);
        const planJobs = (allEntries || []).filter(j => j.status === 'scheduled');
        if (planJobs.length > 1) {
          const remainingCount = planJobs.filter(j => j.scheduled_date >= job.scheduled_date).length;
          setMoveConfirm({ job, targetDate, targetMachineId, remainingCount, totalCount: planJobs.length });
          return;
        }
      }

      // Single-entry move (solo job or 1-day plan not triggered by drag)
      const { data: moved } = await api.put(`/api/v1/schedule/${job.id}`, { scheduled_date: targetDate, machine_id: targetMachineId }, withExpect(runRows(job)));
      // A day that already had a morning job used to refuse this drop outright.
      // Now it can land on a later shift instead -- which is the right answer,
      // but it must not happen silently. Sharing a machine-day is a thing the
      // director would want to know he has just done.
      if (moved && (moved.shift_slot || 1) > 1) {
        toast.success(`Dropped onto the ${SHIFT_LABEL[moved.shift_slot] || 'later'} shift — the morning was taken`);
      }
      load(false);
      pushUndo(
        [{ id: job.id, scheduled_date: job.scheduled_date, machine_id: job.machine_id }],
        [{ id: job.id, scheduled_date: targetDate, machine_id: targetMachineId }]
      );
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.conflict_job_id != null) {
        const d = err.response.data;
        if (d.conflict_status === 'completed') {
          setCompletedConflict({ job, targetDate, targetMachineId,
            conflictName: d.conflict_name,
            conflictJobId: d.conflict_job_id,
            conflictPlanId: d.conflict_plan_id,
            conflictPlanLength: d.conflict_plan_length,
          });
        } else if (d.conflict_status === 'started') {
          setStartedConflict({ job, targetDate, targetMachineId,
            conflictName: d.conflict_name,
            conflictJobId: d.conflict_job_id,
            conflictPlanId: d.conflict_plan_id,
          });
        } else if (!(await openDropOptions(job, targetDate, targetMachineId))) {
          toast.info('That day has just come free — drop it again to place it.');
        }
      } else {
        moveFailed(err, 'Move failed');
      }
    } finally {
      isMoving.current = false;
    }
  }


  // Handler for the 3-option modal when a started (running) job blocks the drop.
  // mode: 'cancel' | 'place_after_today' | 'emergency_stop'
  async function executeStartedConflict(mode) {
    if (mode === 'cancel') { setStartedConflict(null); return; }
    if (isMoving.current) return;
    isMoving.current = true;
    const { job, targetDate, targetMachineId, conflictName, conflictJobId, conflictPlanId } = startedConflict;
    setStartedConflict(null);
    try {
      if (mode === 'place_after_today') {
        // Find the first free working day after today on the target machine.
        // Uses the frontend's in-memory scheduled list — backend will still
        // validate and return 409 if something changed since last load.
        const hols = { ...buildHolidays(new Date(TODAY + 'T12:00:00')) };
        const occupiedDates = new Set(
          scheduled
            .filter(j => j.machine_id === targetMachineId && j.status !== 'cancelled')
            .map(j => j.scheduled_date)
        );
        let candidate = getNthWorkingDay(new Date(TODAY + 'T12:00:00'), 1, hols);
        for (let safety = 0; safety < 200; safety++) {
          if (!occupiedDates.has(candidate)) break;
          candidate = getNthWorkingDay(new Date(candidate + 'T12:00:00'), 1, hols);
        }
        await requestMove(job, candidate, targetMachineId, true);
        return; // requestMove handles load + undo
      }

      if (mode === 'emergency_stop') {
        // Step 1: cancel the running job (and its remaining plan days if it's a plan).
        // We cancel by marking each scheduled entry as cancelled via the schedule DELETE endpoint.
        if (conflictPlanId) {
          const { data: planEntries } = await api.get(`/api/v1/schedule?plan_id=${conflictPlanId}`);
          const toCancel = (planEntries || [])
            .filter(j => j.status === 'scheduled' || j.status === 'started')
            .map(j => j.id);
          if (toCancel.length > 0) {
            await api.delete('/api/v1/schedule/batch', { data: { ids: toCancel } });
          }
        } else {
          await api.delete(`/api/v1/schedule/${conflictJobId}`);
        }

        // Step 2: notify operators and setters.
        await api.post('/api/v1/notifications', {
          type: 'emergency_stop',
          entity_type: 'scheduled_job',
          entity_id: String(conflictJobId),
          entity_name: conflictName,
          title: `Emergency stop: ${conflictName}`,
          body: `The director has cancelled the current run of "${conflictName}". Please stop the machine and await further instructions.`,
          target_roles: ['operator', 'setter'],
        });

        // Step 3: place the dragged plan in the now-vacant slot.
        load(false);
        await requestMove(job, targetDate, targetMachineId, true);
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Action failed');
    } finally {
      isMoving.current = false;
    }
  }

  // Move a completed plan backward so its last day = completionDate, then
  // place the dragged job in the now-vacant original slot.
  async function executeCompletedConflict(completionDate) {
    if (isMoving.current) return;
    isMoving.current = true;
    const { job, targetDate, targetMachineId, conflictJobId, conflictPlanId } = completedConflict;
    setCompletedConflict(null);
    try {
      // Step 1: move the completed plan backward to the actual completion date.
      await api.put('/api/v1/schedule/plan-move-completed', {
        plan_id: conflictPlanId || null,
        job_id: conflictJobId,
        target_last_date: completionDate,
      });

      // Step 2: place the dragged plan in the freed slot.
      // requestMove handles load() + undo internally — we don't double-push undo
      // here because the completed-job date correction is an admin action that
      // shouldn't be part of the drag-and-drop undo chain.
      load(false);
      await requestMove(job, targetDate, targetMachineId, true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not move completed job');
    } finally {
      isMoving.current = false;
    }
  }

  async function executeResizeConflict(mode) {
    if (isMoving.current) return;
    isMoving.current = true;
    const { type: resizeType, planId, newDates, template, machineId, conflictName, conflictPlanFirstDate, conflictPlanId, conflictJobId } = resizeConflict;
    setResizeConflict(null);
    try {
      const hols = { ...buildHolidays(new Date(newDates[0] + 'T12:00:00')), ...closuresToMap(closures) };

      // Compute where the conflict plan's first date should land after being moved.
      // Extend (right): move blocker forward past the last new date.
      // Prepend (left): move blocker backward so it ends just before the first new date.
      let conflictNewStart;
      if (resizeType === 'extend') {
        // first working day after all newDates
        conflictNewStart = getNthWorkingDay(new Date(newDates[0] + 'T12:00:00'), newDates.length, hols);
      } else {
        // prepend: walk backward from newDates[0] by the blocker's own length
        const blockerLength = conflictPlanId
          ? scheduled.filter(j => j.plan_id === conflictPlanId && j.status !== 'cancelled').length
          : 1;
        let cur = new Date(newDates[0] + 'T12:00:00');
        let count = 0;
        for (let safety = 0; safety < 500 && count < blockerLength; safety++) {
          cur = addDays(cur, -1);
          if (!isWeekendStr(fmt(cur)) && !hols[fmt(cur)]) count++;
        }
        conflictNewStart = fmt(cur);
      }

      // Step 1: move the blocking plan out of the way.
      let step1Snapshot = null;
      try {
        const { data: step1Data } = await api.put('/api/v1/schedule/plan-move', {
          plan_id: conflictPlanId || null,
          job_id: conflictJobId,
          job_original_date: conflictPlanFirstDate,
          target_date: conflictNewStart,
          target_machine_id: machineId,
          scope: 'all',
          push: mode === 'push_all',
        });
        step1Snapshot = step1Data?.snapshot || null;
        showMoveNotes(step1Data?.notes);
      } catch (err) {
        if (mode === 'push_one' && err.response?.status === 409) {
          toast.error(`Can't bump just "${conflictName}" — its new slot is also taken. Try "cascade all" instead.`);
        } else {
          toast.error(err.response?.data?.error || 'Move failed');
        }
        return;
      }

      // Step 2: add the new dates to the resized plan.
      const jobs = newDates.map(d => ({
        machine_id: machineId,
        part_id: template.part_id || null,
        mould_id: template.mould_id || null,
        recipe_id: template.recipe_id || null,
        target_qty: template.target_qty || null,
        scheduled_date: d,
        order_line_id: template.order_line_id || null,
      }));
      const { data: created } = await api.post('/api/v1/schedule/batch', { plan_id: planId, jobs });
      const createdIds = (created || []).map(j => j.id);

      load(false);
      // Push a single composite undo entry so one Ctrl+Z reverses both steps.
      undoStack.current.push({
        type: 'resize_conflict',
        createdIds,
        step1Before: step1Snapshot?.before || [],
      });
      redoStack.current = [];
      setCanUndo(true);
      setCanRedo(false);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Resize failed');
    } finally {
      isMoving.current = false;
    }
  }


  async function insertAfterPlan(draggedJob, blockerJob, targetMachineId) {
    if (isMoving.current) return;
    isMoving.current = true;
    try {
      const { data } = await api.put('/api/v1/schedule/insert-after', {
        blocker_plan_id:   blockerJob.plan_id || null,
        blocker_job_id:    blockerJob.id,
        inserted_plan_id:  draggedJob.plan_id || null,
        inserted_job_id:   draggedJob.id,
        target_machine_id: targetMachineId,
      }, withExpect(runRows(draggedJob)));
      load(false);
      if (data?.snapshot) pushUndo(data.snapshot.before, data.snapshot.after);
      showMoveNotes(data?.notes);
      offerGapClose(data?.close_gap);
    } catch (err) {
      moveFailed(err, 'Insert after failed');
    } finally {
      isMoving.current = false;
    }
  }

  function handleNonWorkingDrop(job, targetDate, targetMachineId, planLength, blockLen) {
    setNonWorkingDrop({ job, targetDate, targetMachineId, planLength, blockLen });
  }

  async function executeOverrideDrop(n) {
    if (!nonWorkingDrop) return;
    const { job, targetDate, targetMachineId } = nonWorkingDrop;
    setNonWorkingDrop(null);
    if (isMoving.current) return;
    isMoving.current = true;
    try {
      const { data } = await api.put('/api/v1/schedule/override-drop', {
        plan_id: job.plan_id || null,
        job_id: job.id,
        target_date: targetDate,
        target_machine_id: targetMachineId,
        n,
      }, withExpect(runRows(job)));
      load(false);
      if (data?.snapshot) pushUndo(data.snapshot.before, data.snapshot.after);
      showMoveNotes(data?.notes);
      offerGapClose(data?.close_gap);
    } catch (err) {
      moveFailed(err, 'Override drop failed');
    } finally {
      isMoving.current = false;
    }
  }

  async function duplicatePlan(job, targetDate, targetMachineId) {
    if (isMoving.current) return;
    isMoving.current = true;
    try {
      const { data } = await api.post('/api/v1/schedule/plan-duplicate', {
        plan_id: job.plan_id || null,
        job_id: job.id,
        job_original_date: job.scheduled_date,
        target_date: targetDate,
        target_machine_id: targetMachineId,
      });
      load(false);
      if (data?.created_ids?.length) {
        undoStack.current.push({ type: 'extend', createdIds: data.created_ids });
        redoStack.current = [];
        setCanUndo(true);
        setCanRedo(false);
      }
    } catch (err) {
      const d = err.response?.data;
      toast.error(d?.error || 'Duplicate failed');
    } finally {
      isMoving.current = false;
    }
  }

  async function executeMove(scope) {
    if (isMoving.current) return;
    isMoving.current = true;
    const { job, targetDate, targetMachineId } = moveConfirm;
    setMoveConfirm(null);
    try {
      const { data } = await api.put('/api/v1/schedule/plan-move', {
        plan_id: job.plan_id,
        job_id: job.id,
        job_original_date: job.scheduled_date,
        target_date: targetDate,
        target_machine_id: targetMachineId,
        scope,
        push: false,
      }, withExpect(runRows(job)));
      load(false);
      if (data?.snapshot) pushUndo(data.snapshot.before, data.snapshot.after);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Move failed');
    } finally {
      isMoving.current = false;
    }
  }

  async function handleSwap(draggedJob, targetJobs, machineId) {
    if (isMoving.current) return;
    isMoving.current = true;
    try {
      // Fetch full plan data from API — targetJobs/draggedJob may be view-window subsets
      let draggedPlanJobs;
      if (draggedJob.plan_id) {
        const { data } = await api.get(`/api/v1/schedule?plan_id=${draggedJob.plan_id}`);
        draggedPlanJobs = (data || []).filter(j => j.status === 'scheduled')
          .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
      } else {
        draggedPlanJobs = [draggedJob];
      }

      const targetPlanId = targetJobs[0]?.plan_id;
      let targetPlanJobs;
      if (targetPlanId) {
        const { data } = await api.get(`/api/v1/schedule?plan_id=${targetPlanId}`);
        targetPlanJobs = (data || []).filter(j => j.status === 'scheduled')
          .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
      } else {
        targetPlanJobs = [...targetJobs].filter(j => j.status === 'scheduled')
          .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
      }

      if (!draggedPlanJobs.length || !targetPlanJobs.length) return;

      // Determine which plan comes first chronologically
      const [firstJobs, secondJobs] =
        draggedPlanJobs[0].scheduled_date <= targetPlanJobs[0].scheduled_date
          ? [draggedPlanJobs, targetPlanJobs]
          : [targetPlanJobs,  draggedPlanJobs];

      const firstStart  = new Date(firstJobs[0].scheduled_date + 'T12:00:00');
      const firstEnd    = new Date(firstJobs[firstJobs.length - 1].scheduled_date + 'T12:00:00');
      const secondStart = new Date(secondJobs[0].scheduled_date + 'T12:00:00');
      const secondEnd   = new Date(secondJobs[secondJobs.length - 1].scheduled_date + 'T12:00:00');

      // Gap and durations in calendar days (weekends stay in the gap naturally)
      const secondCalSpan = Math.round((secondEnd   - secondStart) / 86400000) + 1;
      const gapCalDays    = Math.round((secondStart - firstEnd)    / 86400000) - 1;

      const secondNewStart = firstStart;
      const firstNewStart  = addDays(secondNewStart, secondCalSpan + gapCalDays);

      const hols = { ...buildHolidays(secondNewStart), ...closuresToMap(closures) };
      const moves = [
        ...secondJobs.map((j, n) => ({ id: j.id, scheduled_date: getNthWorkingDay(secondNewStart, n, hols), machine_id: machineId })),
        ...firstJobs.map((j, n)  => ({ id: j.id, scheduled_date: getNthWorkingDay(firstNewStart,  n, hols), machine_id: machineId })),
      ];
      const before = [
        ...secondJobs.map(j => ({ id: j.id, scheduled_date: j.scheduled_date, machine_id: j.machine_id })),
        ...firstJobs.map(j  => ({ id: j.id, scheduled_date: j.scheduled_date, machine_id: j.machine_id })),
      ];
      await api.put('/api/v1/schedule/batch-move', { moves });
      load(false);
      pushUndo(before, moves);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Swap failed');
    } finally {
      isMoving.current = false;
    }
  }

  function goToDay(d) { setViewDate(d); setViewMode('day'); }
  // From the at-risk list: jump to the day and ring the run's cards, as the
  // board does after a move.
  function showRiskOnBoard(item) {
    if (!item.at) return;
    if (viewMode !== 'week') setViewMode('week');
    if (weekSpan <= 4) setViewDate(getMonday(new Date(item.at + 'T12:00:00')));
    setScrollIntent({ date: item.at, seq: Date.now() });
    if (item.rows && item.rows.length) { setJustMoved(new Set(item.rows)); setTimeout(() => setJustMoved(null), 6000); }
  }

  // A link from Orders (?at=YYYY-MM-DD&rows=ids, 15 Oct 2026): once the board
  // has loaded, jump to that day and ring the run's cards, as the at-risk list
  // does. Once only; the link stays in the address bar.
  const linkedRun = useRef(false);
  useEffect(() => {
    if (loading || linkedRun.current) return;
    const q = new URLSearchParams(window.location.search);
    const at = q.get('at');
    if (!at || !/^\d{4}-\d{2}-\d{2}$/.test(at)) return;
    linkedRun.current = true;
    const rows = (q.get('rows') || '').split(',').filter(Boolean).map(x => (/^\d+$/.test(x) ? Number(x) : x));
    showRiskOnBoard({ at, rows });
    // The continuous view lays its weeks out once its data is in: centre again then.
    setTimeout(() => setScrollIntent({ date: at, seq: Date.now() }), 900);
  }, [loading]); // eslint-disable-line react-hooks/exhaustive-deps

  // Same-machine forward drop onto an adjacent subsequent span:
  // slide the plan to end exactly at targetSpanJobs[0] (insert-before semantics).
  async function handleMoveInsertBefore(job, targetSpanJobs, machineId) {
    if (isMoving.current) return;
    isMoving.current = true;
    try {
      // Fetch full plan to get true length and first date
      let planFirstDate, planLength;
      if (job.plan_id) {
        const { data: allEntries } = await api.get(`/api/v1/schedule?plan_id=${job.plan_id}`);
        const planJobs = (allEntries || []).filter(j => j.status === 'scheduled')
          .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
        planLength = planJobs.length || 1;
        planFirstDate = planJobs[0]?.scheduled_date || job.scheduled_date;
      } else {
        planLength = 1;
        planFirstDate = job.scheduled_date;
      }
      const targetFirst = [...targetSpanJobs]
        .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date))[0].scheduled_date;
      const hols = { ...buildHolidays(new Date(targetFirst + 'T12:00:00')), ...closuresToMap(closures) };
      // Shift back by planLength so the plan ends exactly where targetFirst begins
      const newTarget = shiftBackByWorkingDays(targetFirst, planLength, hols);

      // Pass planFirstDate as job_original_date so offset = (newTarget − planFirstDate),
      // making newFirstDate = planFirstDate + offset = newTarget exactly.
      const { data } = await api.put('/api/v1/schedule/plan-move', {
        plan_id: job.plan_id || null,
        job_id: job.id,
        job_original_date: planFirstDate,
        target_date: newTarget,
        target_machine_id: machineId,
        scope: 'all',
        push: false,
      }, withExpect(runRows(job)));
      load(false);
      if (data?.snapshot) pushUndo(data.snapshot.before, data.snapshot.after);
    } catch (err) {
      moveFailed(err, 'Insert failed');
    } finally {
      isMoving.current = false;
    }
  }

  // Cross-week drag: when user drops onto the edge navigation zones
  const [pendingPlacement, setPendingPlacement] = useState(null); // { job } pending click-to-place
  function handleEdgeDrop(job, dir) {
    if (pendingPlacement) {
      toast.error(`Pending placement cancelled — placing ${job.parts?.name || 'job'} instead`);
    }
    setPendingPlacement(job);
    setViewDate(d => moveDate(d, 'week', dir));
  }

  // Placing a run carried over from another week. Its first day goes where
  // the director clicked; from there it is exactly a drag -- a free day moves
  // it, a taken day brings up the same options. Until 25 Sep this path
  // worked out its own push in the browser and had its own pop-up.
  async function placeStartingAt(job, targetDate, machineId) {
    let first = job;
    if (job.plan_id) {
      const { data } = await api.get(`/api/v1/schedule?plan_id=${job.plan_id}`);
      const rows = (data || []).filter(j => j.status === 'scheduled')
        .sort((x, y) => x.scheduled_date.localeCompare(y.scheduled_date));
      if (rows.length) first = { ...job, id: rows[0].id, scheduled_date: rows[0].scheduled_date };
    }
    await requestMove(first, targetDate, machineId, true);
  }

  async function handlePendingPlace(job, targetDate, machineId) {
    if (job.backlog) { openBacklogForm(job, targetDate, machineId); return; }
    setPendingPlacement(null);
    await placeStartingAt(job, targetDate, machineId);
  }

  async function handleInsertConflict({ job, targetDate, machineId }) {
    if (job.backlog) { openBacklogForm(job, targetDate, machineId); return; }
    setPendingPlacement(null);
    await placeStartingAt(job, targetDate, machineId);
  }

  // The backlog beside the board (the director, 20 Oct 2026): every open
  // order line the board does not cover, with why, to drag onto it.
  const [showBacklog, setShowBacklog] = useState(false);
  const [backlog, setBacklog] = useState(null);
  const [backlogError, setBacklogError] = useState('');
  const backlogAt = useRef(0);
  const loadBacklog = useCallback((force) => {
    if (!force && Date.now() - backlogAt.current < 30000) return;
    backlogAt.current = Date.now();
    api.get('/api/v1/orders/backlog')
      .then(r => { setBacklog(Array.isArray(r.data) ? r.data : []); setBacklogError(''); })
      .catch(err => setBacklogError(err.response?.data?.error || 'The backlog could not be read just now.'));
  }, []);
  useEffect(() => { loadBacklog(false); }, [loadBacklog, scheduled]);
  useEffect(() => { if (showBacklog) loadBacklog(true); }, [showBacklog, loadBacklog]);
  const [backlogFocus, setBacklogFocus] = useState([]);
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (!q.get('backlog')) return;
    setShowBacklog(true);
    setBacklogFocus((q.get('lines') || '').split(',').map(Number).filter(Boolean));
  }, []);

  // Working days a line needs on a machine: the job form's own arithmetic.
  function backlogDaysOn(line, machineId) {
    const part = parts.find(p => p.id === line.part_id);
    if (!part || !(Number(part.target_cycle_s) > 0)) return null;
    const mould = moulds.find(m => m.id === part.mould_id);
    const machine = machines.find(m => m.id === Number(machineId));
    const cap = dailyCapacity({ machine, mould, cycleS: Number(part.target_cycle_s), shifts: 1 });
    if (!(cap.partsPerDay > 0)) return null;
    const qty = Number(line.missing) || 0;
    const day1 = qty > cap.partsPerDay && cap.changeoverMin > 0 ? cap.firstDayParts : cap.partsPerDay;
    return day1 >= qty ? 1 : 1 + Math.ceil((qty - day1) / cap.partsPerDay);
  }
  // Start placing a line: the board's placing mode, as for a run carried over
  // from another week -- its machines highlighted, its footprint and pushes shown.
  function pickBacklog(line) {
    const part = parts.find(p => p.id === line.part_id);
    if (!part) { toast.error('That part is not in the parts list. Reload the board and try again.'); return; }
    const eligible = [part.primary_machine_id, ...(part.secondary_machine_ids || [])].filter(Boolean).map(Number);
    if (!eligible.length) { toast.error(`${part.name} has no machine it can run on. Give it one on its part page, then place it.`); return; }
    setPendingPlacement({
      id: `backlog-${line.line_id}`, backlog: line, parts: part, part_id: part.id,
      machine_id: Number(part.primary_machine_id) || eligible[0], plan_id: null, scheduled_date: null,
      days_on: Object.fromEntries(eligible.map(id => [id, backlogDaysOn(line, id) || 1])),
    });
  }
  // Dropped (or a day clicked): the job form, filled in. Its save pushes what
  // is in the way, only as far as needed; it says what before saving.
  function openBacklogForm(job, date, machineId) {
    const part = job.parts;
    setPendingPlacement(null);
    setEditJob({ part_id: part.id, machine_id: Number(machineId), mould_id: part.mould_id || null, scheduled_date: date,
                 target_qty: job.backlog.missing, order_line_id: job.backlog.line_id, shifts: 1 });
  }

  const common = {
    machines, scheduled, TODAY, holidays, jobsFor, prevJobOnMachine, nextJobOnMachine, getChangeover, getPurge,
    justMoved: ringIds,
    onNavigateWeek: dir => setViewDate(d => addDays(getMonday(d), dir * 7 * weekSpan)),
    isDirector: isDirector(), onEdit: setEditJob, onDelete: deleteJob, onMove: requestMove, onSwap: handleSwap,
    shiftWindowFor,
    downFor,
    onMachineDown: can('report_downtime') ? (m => setDownTarget(m)) : null,
    expandShifts,
    colours, onLiveView: id => setLiveJobId(id), jobPlanTotals, planTotalsMap, filterText, allDeliveryWeekSet, toast,
    pendingPlacement, onEdgeDrop: handleEdgeDrop, onClearPendingPlacement: () => setPendingPlacement(null),
    onInsertConflict: handleInsertConflict, onPendingPlace: handlePendingPlace,
    onMoveInsertBefore: handleMoveInsertBefore,
    onResizeEnd: handleResize,
    onDuplicate: duplicatePlan,
    onInsertAfter: insertAfterPlan,
    onNonWorkingDrop: handleNonWorkingDrop,
    onBulkReschedule: m => setMoveTarget({ onMachineId: m.id }),
    onMoveFrom: a => setMoveTarget(a),
    compactMachines, toggleCompact,
    onComplete: planData => setCompletingPlan(planData),
    onUncomplete: planId => api.delete(`/api/v1/schedule/plan/${planId}/complete`)
      .then(() => load(false))
      .catch(err => toast.error(err?.response?.data?.error || 'Failed to revert')),
    // NEW: kept when the board was put back to an earlier point although it
    // came after it (6 Oct 2026). His to dismiss once seen -- the whole run.
    onDismissNew: (planId, ids) => api.post('/api/v1/history/new-marks/dismiss', { plans: planId ? [planId] : [], runs: ids })
      .then(() => load(false))
      .catch(err => toast.error(err?.response?.data?.error || 'Could not dismiss the NEW label')),
    urgencyDays,
    showCompleted,
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: '#6b7280', fontFamily: 'system-ui', background: '#0f1117' }}>
      Loading schedule...
    </div>
  );

  return (
    // No minHeight here: the app around it already fills the window, and a
    // window-tall page below the nav bar always scrolled 89px (9 Oct 2026).
    // The bottom padding is small so seven machines fit his screen unscrolled.
    <div style={{ padding: '20px 20px 8px', fontFamily: 'system-ui, sans-serif', color: '#f0f2f5', background: '#0f1117' }}>

      <button onClick={goBack} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '12px', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
        <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
      </button>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '22px', fontWeight: '700', margin: '0 0 4px' }}>Schedule</h1>
          <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>Production planning</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Week span selector — only in week mode */}
          {viewMode === 'week' && (
            <div style={{ display: 'flex', background: '#1e2329', border: '1px solid #2a3040', borderRadius: '8px', overflow: 'hidden' }}>
              {[1, 2, 3, 4].map(n => (
                <button key={n} onClick={() => {
                  setWeekSpan(n);
                  if (n === 2) setViewDate(getMonday(new Date()));
                  if (n === 3) setViewDate(addDays(getMonday(new Date()), -7));
                  if (n === 4) setViewDate(addDays(getMonday(new Date()), -7));
                }}
                  style={{ padding: '7px 11px', fontSize: '12px', fontWeight: '600', border: 'none', cursor: 'pointer', fontFamily: 'system-ui', background: weekSpan === n ? '#14b8a6' : 'transparent', color: weekSpan === n ? '#fff' : '#9ca3af' }}>
                  {n}w
                </button>
              ))}
              {/* Full-horizon span: 4 weeks back → 4 weeks past the last job */}
              <button
                onClick={handleAllSpan}
                title={lastJobDate
                  ? `4 weeks back through 4 weeks past the last scheduled job (${lastJobDate})`
                  : '4 weeks either side of today — nothing scheduled yet'}
                style={{ padding: '7px 10px', fontSize: '13px', fontWeight: '700', border: 'none', borderLeft: '1px solid #2a3040', cursor: 'pointer',
                  fontFamily: 'system-ui', background: weekSpan > 4 ? '#14b8a6' : 'transparent', color: weekSpan > 4 ? '#fff' : '#9ca3af' }}>
                →
              </button>
            </div>
          )}

          {/* Expand shift columns — a switch, so beside the span group rather
              than inside it. Greyed when the loaded period has no multi-shift
              day, because there would be nothing to expand. */}
          {viewMode === 'week' && (
            <button
              onClick={() => hasMultiShiftDays && setCondensed(c => !c)}
              disabled={!hasMultiShiftDays}
              title={hasMultiShiftDays
                ? (expandShifts ? 'Condense — draw multi-shift days at normal width' : 'Expand multi-shift days into their shifts')
                : 'Nothing in this period runs more than one shift'}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: expandShifts ? '#14b8a6' : '#1e2329',
                border: `1px solid ${expandShifts ? '#14b8a6' : '#2a3040'}`,
                borderRadius: '8px', padding: '7px 11px',
                fontSize: '12px', fontWeight: '600', fontFamily: 'system-ui',
                color: !hasMultiShiftDays ? '#4b5563' : expandShifts ? '#fff' : '#9ca3af',
                cursor: hasMultiShiftDays ? 'pointer' : 'not-allowed',
                opacity: hasMultiShiftDays ? 1 : 0.55,
              }}>
              <span style={{ display: 'flex', gap: '2px', alignItems: 'center' }} aria-hidden="true">
                <span style={{ width: '3px', height: '11px', borderRadius: '1px', background: 'currentColor' }} />
                <span style={{ width: '3px', height: '11px', borderRadius: '1px', background: 'currentColor', opacity: expandShifts ? 1 : 0.35 }} />
              </span>
              Shifts
            </button>
          )}

          {/* At risk: the standing list, always in view (a phone too) */}
          {can('see_risks') && <AtRiskPanel refreshKey={scheduled} onShow={showRiskOnBoard} />}

          {/* The backlog: what the board does not cover yet (20 Oct 2026) */}
          {isDirector() && <button onClick={() => setShowBacklog(v => !v)} title="Open order lines that are not on the board yet, and why"
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 11px', borderRadius: 8, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'system-ui', flexShrink: 0,
                     fontSize: 12, fontWeight: 700, color: (backlog || []).length ? '#f0f2f5' : '#6b7280', background: showBacklog ? 'rgba(20,184,166,0.15)' : 'transparent',
                     border: `1px solid ${showBacklog ? '#14b8a6' : '#2a3040'}` }}>
            <i className="fa-solid fa-inbox" style={{ fontSize: 11, color: '#14b8a6' }} />
            Backlog{backlog ? ` · ${backlog.length}` : ''}
          </button>}

          {/* The board is the director's to change (3 Nov 2026): the floor sees it, and proposes. */}
          {!isDirector() && (
            <span style={{ fontSize: 12, color: '#9ca3af' }}>View only. To propose a change, use Suggest beside the bell.</span>
          )}

          {/* + Schedule Job */}
          {isDirector() && (
            <button onClick={() => setEditJob(null)}
              style={{ background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui' }}>
              + Schedule Job
            </button>
          )}

          {/* + Staff: extra people on particular days, where he plans */}
          {isDirector() && (
            <button onClick={() => setShowStaff(true)} title="Extra staff on particular days, added to the usual week"
              style={{ background: 'rgba(20,184,166,0.1)', color: '#14b8a6', border: '1px solid #14b8a6', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui' }}>
              + Staff
            </button>
          )}

          {/* Hamburger — view mode + secondary actions */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowMenu(m => !m)}
              style={{ background: 'none', border: '1px solid #2a3040', color: '#9ca3af', borderRadius: '8px', padding: '8px 12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui', lineHeight: 1 }}>
              ☰
            </button>
            {showMenu && (
              <>
                <div onClick={() => setShowMenu(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '6px', zIndex: 100, display: 'flex', flexDirection: 'column', gap: '2px', minWidth: '190px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
                  {/* View mode section */}
                  <div style={{ padding: '4px 10px 2px', fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>View</div>
                  {[['day', 'fa-calendar-day', 'Day'], ['week', 'fa-calendar-week', 'Week'], ['month', 'fa-calendar', 'Month']].map(([v, icon, label]) => (
                    <button key={v} onClick={() => { setViewMode(v); setShowMenu(false); }}
                      style={{ background: 'none', border: 'none', borderRadius: '6px', padding: '7px 12px', fontSize: '13px', fontWeight: viewMode === v ? '700' : '600', cursor: 'pointer', fontFamily: 'system-ui', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left', color: viewMode === v ? '#14b8a6' : '#f0f2f5' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#2a3040'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                      <i className={`fa-solid ${icon}`} style={{ width: '14px', color: viewMode === v ? '#14b8a6' : '#6b7280' }} />{label}
                    </button>
                  ))}
                  {/* Divider */}
                  <div style={{ borderTop: '1px solid #2a3040', margin: '4px 0' }} />
                  {/* Secondary actions */}
                  <button onClick={() => { setShowShortcuts(true); setShowMenu(false); }}
                    style={{ background: 'none', border: 'none', color: '#f0f2f5', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#2a3040'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <i className="fa-solid fa-keyboard" style={{ width: '14px', color: '#14b8a6' }} />Keyboard shortcuts <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#4b5563', fontWeight: '600' }}>?</span>
                  </button>
                  {/* Director only, like the history itself: it is a record of people as
                      much as of the board, and asking why is part of keeping it. */}
                  {isDirector() && (<>
                  <button onClick={async () => {
                      // Paused today is undone by resuming, not by flipping the
                      // standing policy -- they are different decisions.
                      if (reasonPaused) { resumeReasons(); setShowMenu(false); return; }
                      const next = reasonMode === 'off' ? 'optional' : 'off';
                      try {
                        await api.put('/api/v1/reasons/mode', { mode: next });
                        setReasonMode(next);
                        if (next === 'off') setReasonQueue([]);
                        toast.success(next === 'off'
                          ? 'Reasons off — changes will not ask why'
                          : 'Reasons back on');
                      } catch { toast.error('Could not change that'); }
                      setShowMenu(false);
                    }}
                    title={reasonPaused
                      ? 'Paused for today only. Click to start asking again now.'
                      : reasonMode === 'off'
                        ? 'Turned off for everyone, until turned back on.'
                        : 'A change asks why, once, and lets you skip it.'}
                    style={{ background: 'none', border: 'none', color: (reasonMode === 'off' || reasonPaused) ? '#f59e0b' : '#f0f2f5', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#2a3040'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <i className={`fa-solid ${(reasonMode === 'off' || reasonPaused) ? 'fa-comment-slash' : 'fa-comment-dots'}`} style={{ width: '14px', color: (reasonMode === 'off' || reasonPaused) ? '#f59e0b' : '#14b8a6' }} />
                    {reasonPaused ? 'Asking why: paused today' : reasonMode === 'off' ? 'Asking why: off' : 'Asking why: on'}
                  </button>
                  <button onClick={() => { setShowHistory(true); setShowMenu(false); }}
                    style={{ background: 'none', border: 'none', color: '#f0f2f5', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#2a3040'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ width: '14px', color: '#14b8a6' }} />Schedule history
                  </button>
                  </>)}
                  <button onClick={() => { goTo('/admin/archive/jobs'); setShowMenu(false); }}
                    style={{ background: 'none', border: 'none', color: '#9ca3af', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#2a3040'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <i className="fa-solid fa-clock-rotate-left" style={{ width: '14px' }} />Jobs Archive
                  </button>
                  <button onClick={() => { setShowPrint(true); setShowMenu(false); }}
                    style={{ background: 'none', border: 'none', color: '#f0f2f5', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#2a3040'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <i className="fa-solid fa-print" style={{ width: '14px', color: '#9ca3af' }} />Export PDF
                  </button>
                  {isDirector() && (
                    <>
                      <button onClick={() => { setShowCapacity(true); setShowMenu(false); }}
                        style={{ background: 'none', border: 'none', color: '#f0f2f5', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#2a3040'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <i className="fa-solid fa-clipboard-question" style={{ width: '14px', color: '#14b8a6' }} />Can we take this order?
                      </button>
                      <button onClick={() => { setShowImport(true); setShowMenu(false); }}
                        style={{ background: 'none', border: 'none', color: '#f0f2f5', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#2a3040'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <i className="fa-solid fa-file-import" style={{ width: '14px', color: '#14b8a6' }} />Import Excel
                      </button>
                      <button onClick={() => { setShowMachineOrder(true); setShowMenu(false); }}
                        style={{ background: 'none', border: 'none', color: '#f0f2f5', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#2a3040'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <i className="fa-solid fa-arrow-down-short-wide" style={{ width: '14px', color: '#14b8a6' }} />Machine order
                      </button>
                      <button onClick={() => { setShowWindows(true); setShowMenu(false); }}
                        style={{ background: 'none', border: 'none', color: '#f0f2f5', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#2a3040'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <i className="fa-solid fa-layer-group" style={{ width: '14px', color: '#14b8a6' }} />Shift Windows
                      </button>
                      <button onClick={() => { goTo('/admin/closures'); setShowMenu(false); }}
                        style={{ background: 'none', border: 'none', color: '#f0f2f5', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui', display: 'flex', alignItems: 'center', gap: '8px', textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#2a3040'} onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                        <i className="fa-solid fa-calendar-xmark" style={{ width: '14px', color: '#f59e0b' }} />Non-Working Days
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Unified control bar (nav + tools + delivery badge) ──────────────── */}
      {(() => {
        const anchorDate = viewMode === 'week' && (weekSpan >= 3 || weekSpan > 4)
          ? addDays(getMonday(new Date()), -7)
          : viewMode === 'week' && weekSpan === 2
          ? getMonday(new Date())
          : new Date();
        const isCurrentView = viewMode === 'week'
          ? fmt(getMonday(viewDate)) === fmt(getMonday(anchorDate))
          : viewMode === 'day'
          ? fmt(viewDate) === TODAY
          : viewDate.getFullYear() === new Date().getFullYear() && viewDate.getMonth() === new Date().getMonth();
        const { week: navWeek } = isoWeekOf(getMonday(viewDate));
        const displayWeek = visScrollWeek ?? navWeek;
        // The Monday of that week, found among the loaded weeks, so the dates
        // in the label start where the week number does.
        const displayFrom = viewMode === 'week'
          ? (Array.from({ length: weekSpan }, (_, i) => addDays(getMonday(viewDate), 7 * i))
              .find(m => isoWeekOf(m).week === displayWeek) || null)
          : null;
        const allCompact = machines.length > 0 && machines.every(m => compactMachines.has(m.id));

        // Delivery weeks visible in current week view
        const weekStart = getMonday(viewDate);
        const visDelivery = viewMode === 'week' ? Array.from({ length: weekSpan }, (_, i) => {
          const { week: w, year: y } = isoWeekOf(addDays(weekStart, i * 7));
          return { week: w, year: y, key: `${y}-${String(w).padStart(2, '0')}` };
        }).filter(({ key }) => allDeliveryWeekSet.has(key)) : [];

        const arrowBtn  = { ...st.navBtn, fontWeight: '700', fontSize: '16px', padding: '5px 14px' };
        const todayBtn  = isCurrentView
          ? { ...st.navBtn, fontSize: '11px', fontWeight: '700', background: '#14b8a6', color: '#0f172a', border: '1px solid #14b8a6' }
          : { ...st.navBtn, fontSize: '11px', fontWeight: '700', color: '#14b8a6', border: '1px solid #14b8a6', background: 'transparent' };
        const vDiv = <div className="sched-vdiv" style={{ width: '1px', alignSelf: 'stretch', background: '#2a3040', flexShrink: 0 }} />;

        // ── 1: nav ──────────────────────────────────────────────────
        const seg1 = (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            {/* One week per click at any span — matches the edge-drag navigation
                and the continuation arrows on cards, and keeps most of the
                current window on screen so you don't lose your place. */}
            <button onClick={() => setViewDate(d => viewMode === 'week' ? addDays(getMonday(d), -7) : moveDate(d, viewMode, -1))} style={arrowBtn}>←</button>
            <div style={{ minWidth: '150px', textAlign: 'center' }}>
              {viewMode === 'week' && (
                <div style={{ fontSize: '10px', fontWeight: '800', color: '#f0f2f5', textTransform: 'uppercase', letterSpacing: '0.06em', lineHeight: 1, marginBottom: '2px' }}>
                  {weekSpan === 1 ? `Week ${displayWeek}` : `Wk ${displayWeek}–${isoWeekOf(addDays(getMonday(viewDate), 7 * weekSpan - 1)).week}`}
                </div>
              )}
              <span style={{ fontSize: '13px', fontWeight: '600', color: isCurrentView ? '#14b8a6' : '#f0f2f5' }}>
                {getLabel(viewDate, viewMode, weekSpan, displayFrom)}
              </span>
            </div>
            <button onClick={() => setViewDate(d => viewMode === 'week' ? addDays(getMonday(d), 7) : moveDate(d, viewMode, 1))} style={arrowBtn}>→</button>
          </div>
        );

        // ── 2: Today ─────────────────────────────────────────────────
        const seg2 = <button onClick={() => { setViewDate(anchorDate); setCenterTodaySeq(n => n + 1); }} style={todayBtn}>Today</button>;

        // ── 3: Collapse all ──────────────────────────────────────────
        const seg3 = viewMode === 'week' ? (
          <button
            onClick={() => setCompactMachines(allCompact ? new Set() : new Set(machines.map(m => m.id)))}
            title={allCompact ? 'Expand all machine rows' : 'Collapse all machine rows'}
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 11px', background: 'rgba(20,184,166,0.12)', border: '1px solid rgba(20,184,166,0.4)', borderRadius: '8px', color: '#14b8a6', fontSize: '12px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'system-ui', flexShrink: 0 }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(20,184,166,0.22)'; e.currentTarget.style.borderColor = 'rgba(20,184,166,0.7)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(20,184,166,0.12)'; e.currentTarget.style.borderColor = 'rgba(20,184,166,0.4)'; }}>
            <i className={`fa-solid fa-${allCompact ? 'expand' : 'compress'}`} style={{ fontSize: '11px' }} />
            {allCompact ? 'Expand all' : 'Collapse all'}
          </button>
        ) : null;

        // ── 4: Filter ────────────────────────────────────────────────
        const seg4 = (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: '120px', maxWidth: '260px' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)', color: '#4b5563', fontSize: '11px', pointerEvents: 'none' }} />
              <input
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                placeholder="Filter…"
                style={{ width: '100%', background: '#161b22', border: `1px solid ${filterText ? '#14b8a6' : '#2a3040'}`, borderRadius: '8px', color: '#f0f2f5', padding: '6px 26px 6px 28px', fontSize: '12px', outline: 'none', boxSizing: 'border-box', fontFamily: 'system-ui' }}
              />
              {filterText && (
                <button onClick={() => setFilterText('')} style={{ position: 'absolute', right: '7px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '11px', padding: '0' }}>✕</button>
              )}
            </div>
            {filterText && (() => {
              const n = scheduled.filter(j => jobMatchesFilter(j, filterText)).length;
              return <span style={{ fontSize: '11px', color: '#14b8a6', fontWeight: '600', whiteSpace: 'nowrap' }}>{n}</span>;
            })()}
          </div>
        );

        // ── 5: Delivery week badges (one per week, clickable) ─────────
        // They take the room that is left and scroll within it, in the app's
        // own strip: in the → view a long horizon has a chip for every week, and
        // they pushed Copy week and Undo / Redo off the screen (20 Oct 2026).
        // Each says just its week number; the full name is on hover.
        const seg5 = visDelivery.length > 0 ? (
          <ScrollStrip>
            {visDelivery.map(({ week, year: y }) => {
              // Find the dispatch date that falls in this ISO week, if any
              const mon = isoWeekToMonday(week, y);
              const monStr = fmt(mon);
              const sunStr = fmt(addDays(mon, 6));
              const dispatchDate = Object.keys(dispatchDates).find(d => d >= monStr && d <= sunStr) || null;
              const targetDate   = dispatchDate || monStr;
              return (
                <button
                  key={`${y}-${week}`}
                  title={dispatchDate ? `Week ${week} · dispatch ${dispatchDate}` : `Week ${week}`}
                  onClick={() => {
                    if (weekSpan === 1) {
                      setViewDate(new Date(targetDate + 'T12:00:00'));
                    }
                    setScrollIntent({ date: targetDate, seq: Date.now() });
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px', background: 'rgba(251,191,36,0.10)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: '8px', padding: '4px 8px', fontSize: '11px', color: '#fbbf24', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui', transition: 'background 0.12s, border-color 0.12s', whiteSpace: 'nowrap' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(251,191,36,0.20)'; e.currentTarget.style.borderColor = 'rgba(251,191,36,0.6)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(251,191,36,0.10)'; e.currentTarget.style.borderColor = 'rgba(251,191,36,0.3)'; }}
                >
                  <i className="fa-solid fa-truck" style={{ fontSize: '10px' }} /> {week}
                </button>
              );
            })}
          </ScrollStrip>
        ) : null;

        // ── 6: Copy week ─────────────────────────────────────────────
        const seg6 = isDirector() && viewMode === 'week' ? (
          <button
            onClick={() => setShowCopyWeek(true)}
            title="Clone this week's scheduled jobs to the following week"
            style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 11px', background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.35)', borderRadius: '8px', color: '#a78bfa', fontSize: '12px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'system-ui', flexShrink: 0 }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(167,139,250,0.20)'; e.currentTarget.style.borderColor = 'rgba(167,139,250,0.6)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(167,139,250,0.10)'; e.currentTarget.style.borderColor = 'rgba(167,139,250,0.35)'; }}>
            <i className="fa-solid fa-copy" style={{ fontSize: '11px' }} />
            Copy week →
          </button>
        ) : null;

        // ── 7: Undo / Redo ────────────────────────────────────────────
        const seg7 = can('plan') && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
            {/* Each names what it will do before it is pressed. A button reading
                only "Undo" makes you press it to find out, which is how the old
                flow lost people. */}
            <button onClick={() => stepHistory('undo')} disabled={!canUndo}
              title={history.undo ? `Undo: ${history.undo.summary}  (Ctrl+Z)` : 'Nothing to undo'}
              style={{ ...st.navBtn, opacity: canUndo ? 1 : 0.3, cursor: canUndo ? 'pointer' : 'default' }}>
              <i className="fa-solid fa-rotate-left" />
            </button>
            <button onClick={() => stepHistory('redo')} disabled={!canRedo}
              title={history.redo ? `Redo: ${history.redo.summary}  (Ctrl+Y)` : 'Nothing to redo'}
              style={{ ...st.navBtn, opacity: canRedo ? 1 : 0.3, cursor: canRedo ? 'pointer' : 'default' }}>
              <i className="fa-solid fa-rotate-right" />
            </button>
            {history.steps_back > 0 && (
              <span title="Undone steps still waiting to be redone. Any new change closes them."
                style={{ fontSize: 11, fontFamily: 'ui-monospace, monospace', color: '#f59e0b',
                         alignSelf: 'center', padding: '0 4px' }}>
                {history.steps_back} back
              </span>
            )}
          </div>
        );

        return (
          <div style={{ marginBottom: '6px' }}>
            <style>{`
              .sched-ctrl-bar {
                display: flex; align-items: center; gap: 8px;
                background: #1e2329; border: 1px solid #2a3040;
                border-radius: 10px; padding: 8px 14px;
              }
              .sched-gap { flex: 1; min-width: 0; }
              .sched-vdiv { display: block; }
              .sched-ctrl-toggle { display: none; }
              @media (max-width: 1023px) {
                .sched-ctrl-toggle {
                  display: flex; align-items: center; justify-content: space-between;
                  background: #1e2329; border: 1px solid #2a3040;
                  padding: 9px 14px; cursor: pointer; user-select: none;
                }
                .sched-ctrl-bar {
                  flex-direction: column; align-items: stretch; gap: 10px;
                  border-top: none; padding: 12px 14px;
                }
                .sched-ctrl-bar.ctrl-closed { display: none; }
                .sched-ctrl-bar.ctrl-open { border-radius: 0 0 10px 10px; }
                .sched-ctrl-toggle.ctrl-open { border-radius: 10px 10px 0 0; }
                .sched-ctrl-toggle.ctrl-closed { border-radius: 10px; }
                .sched-gap { display: none; }
                .sched-vdiv { display: none; }
              }
            `}</style>

            {/* Mobile/tablet toggle header */}
            <div
              className={`sched-ctrl-toggle ${controlsOpen ? 'ctrl-open' : 'ctrl-closed'}`}
              onClick={() => setControlsOpen(o => !o)}
            >
              <span style={{ fontSize: '13px', fontWeight: '600', color: isCurrentView ? '#14b8a6' : '#f0f2f5' }}>
                {viewMode === 'week' && weekSpan > 1 ? `Wk ${displayWeek}–${isoWeekOf(addDays(getMonday(viewDate), 7 * weekSpan - 1)).week} · ` : ''}
                {getLabel(viewDate, viewMode, weekSpan, displayFrom)}
              </span>
              <i className={`fa-solid fa-chevron-${controlsOpen ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '12px' }} />
            </div>

            {/* Control panel */}
            <div className={`sched-ctrl-bar ${controlsOpen ? 'ctrl-open' : 'ctrl-closed'}`}>
              {/* LEFT: 1 nav · 2 Today · | · 3 Collapse · 4 Filter · 5 Delivery */}
              {seg1}
              {vDiv}
              {seg2}
              {vDiv}
              {seg3}
              {seg4}
              {seg5}
              {/* GAP pushes 6+7 to the right on desktop */}
              <div className="sched-gap" />
              {can('plan') && vDiv}
              {/* RIGHT: 6 Copy week · 7 Undo/Redo */}
              {seg6}
              {seg7}
            </div>
          </div>
        );
      })()}

      {viewMode === 'week'  && <WeekView  {...common} viewDate={viewDate} weekSpan={weekSpan} dispatchDates={dispatchDates} onDispatchReload={() => api.get('/api/v1/orders/dispatch-dates').then(r => setDispatchDates(Object.fromEntries((r.data || []).map(d => [d.date, { type: d.type, orders: d.orders || [] }])))).catch(() => {})} onVisibleWeekChange={w => setVisScrollWeek(w)} onNavigateWeek={dir => { setVisScrollWeek(null); setViewDate(d => addDays(getMonday(d), (dir || 0) * 7)); }} scrollIntent={scrollIntent} centerTodaySeq={centerTodaySeq} staffingSeq={staffingSeq} onDropHints={m => { dropHintsRef.current = m; }} />}
      {viewMode === 'day'   && <DayView   {...common} viewDate={viewDate} />}
      {viewMode === 'month' && <MonthView {...common} viewDate={viewDate} onDayClick={goToDay} />}

      {moveConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, fontFamily: 'system-ui' }}
          onClick={() => setMoveConfirm(null)}>
          <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '16px', width: '90%', maxWidth: '400px' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 16px', borderBottom: '1px solid #2a3040' }}>
              <div style={{ fontSize: '16px', fontWeight: '700' }}>Move planned run</div>
              <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '16px', cursor: 'pointer' }} onClick={() => setMoveConfirm(null)}>✕</button>
            </div>
            <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                {moveConfirm.job.scheduled_date} → <span style={{ color: '#14b8a6', fontWeight: '600' }}>{moveConfirm.targetDate}</span>
              </div>
              <button onClick={() => executeMove('remaining')}
                style={{ background: '#1a2030', border: '1px solid #2a3040', borderRadius: '8px', padding: '12px 14px', textAlign: 'left', cursor: 'pointer', fontFamily: 'system-ui', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#14b8a6'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#2a3040'}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#f0f2f5', marginBottom: '3px' }}>Move this day and remaining days</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>{moveConfirm.remainingCount} day{moveConfirm.remainingCount !== 1 ? 's' : ''} move · earlier days stay in place</div>
              </button>
              <button onClick={() => executeMove('all')}
                style={{ background: '#1a2030', border: '1px solid #2a3040', borderRadius: '8px', padding: '12px 14px', textAlign: 'left', cursor: 'pointer', fontFamily: 'system-ui', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = '#14b8a6'}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#2a3040'}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: '#f0f2f5', marginBottom: '3px' }}>Move the whole job</div>
                <div style={{ fontSize: '11px', color: '#6b7280' }}>All {moveConfirm.totalCount} day{moveConfirm.totalCount !== 1 ? 's' : ''} shifted by the same offset</div>
              </button>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 20px', borderTop: '1px solid #2a3040' }}>
              <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '13px', cursor: 'pointer', fontFamily: 'system-ui' }} onClick={() => setMoveConfirm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {completedConflict && (
        <CompletedJobConflictModal
          conflictName={completedConflict.conflictName}
          conflictPlanLength={completedConflict.conflictPlanLength}
          today={TODAY}
          onConfirm={date => executeCompletedConflict(date)}
          onCancel={() => setCompletedConflict(null)}
        />
      )}

      {startedConflict && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, fontFamily: 'system-ui' }}
          onClick={() => setStartedConflict(null)}>
          <div style={{ background: '#1e2329', border: '1px solid #ef4444', borderRadius: '16px', width: '90%', maxWidth: '460px' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 16px', borderBottom: '1px solid #2a3040' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: '20px' }}>⚠️</span>
                <div style={{ fontSize: '16px', fontWeight: '700' }}>Machine is currently running</div>
              </div>
              <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '16px', cursor: 'pointer' }} onClick={() => setStartedConflict(null)}>✕</button>
            </div>
            <div style={{ padding: '16px 20px 8px', fontSize: '13px', color: '#9ca3af', lineHeight: 1.5 }}>
              <span style={{ color: '#f87171', fontWeight: '600' }}>{startedConflict.conflictName}</span> is actively running on this machine right now.
              How do you want to proceed?
            </div>
            <div style={{ padding: '8px 20px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={() => executeStartedConflict('place_after_today')}
                style={{ padding: '12px 16px', background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', lineHeight: 1.4 }}>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>Schedule after today</div>
                <div style={{ fontSize: '12px', opacity: 0.85 }}>Keep the current run going — place this job on the first free day after today</div>
              </button>
              <button
                onClick={() => executeStartedConflict('emergency_stop')}
                style={{ padding: '12px 16px', background: '#7f1d1d', color: '#fca5a5', border: '1px solid #ef4444', borderRadius: '10px', cursor: 'pointer', textAlign: 'left', lineHeight: 1.4 }}>
                <div style={{ fontWeight: '700', fontSize: '14px' }}>🚨 Emergency stop</div>
                <div style={{ fontSize: '12px', opacity: 0.85 }}>Cancel the current run, notify operators and setters to stop, and start this job immediately</div>
              </button>
              <button
                onClick={() => executeStartedConflict('cancel')}
                style={{ padding: '10px 16px', background: 'transparent', color: '#6b7280', border: '1px solid #2a3040', borderRadius: '10px', cursor: 'pointer', fontSize: '13px' }}>
                Cancel — keep original position
              </button>
            </div>
          </div>
        </div>
      )}

      {dropConflict && (() => {
        // One list of choices, each a button, each with what it moves and what
        // the engine says about it where it lands (the director, 12 Oct 2026:
        // the warnings sat apart from the ways out they belonged to, and the
        // ways out looked like text). Everything here came from the server:
        // the engine said which choices exist and checked each one. What
        // applies whichever way is said once, on top; a choice that cannot be
        // done is still shown, greyed, with the reason -- never just missing.
        const free = !!dropConflict.free;
        const legacy = !dropConflict.choices;   // a server from before 12 Oct
        const choices = legacy
          ? (dropConflict.options || []).filter(o => o.request || !o.ok).map(o => ({ ...o, key: o.do, checks: [] }))
          : dropConflict.choices;
        const common = legacy ? (dropConflict.warnings || []) : (dropConflict.common || []);
        const doable = choices.filter(c => c.ok);
        const notDoable = choices.filter(c => !c.ok);
        const tone = {
          blocker: { color: '#fca5a5', icon: 'fa-circle-xmark' },
          warning: { color: '#fcd34d', icon: 'fa-triangle-exclamation' },
          note:    { color: '#9ca3af', icon: 'fa-circle-info' },
        };
        const checkLine = (k, i, choice) => {
          const t = tone[k.severity] || tone.warning;
          return (
            <div key={i} style={{ display: 'flex', gap: '7px', alignItems: 'flex-start', fontSize: '12px', lineHeight: 1.45, color: t.color }}>
              <i className={`fa-solid ${t.icon}`} style={{ fontSize: '11px', marginTop: '3px', flexShrink: 0 }} />
              <div style={{ minWidth: 0 }}>
                <div>{k.says}</div>
                {(k.actions || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                    {k.actions.map((a, j) => (
                      <button key={j}
                        onClick={() => { if (a.link) { setDropConflict(null); goTo(a.link); } else executeDropOption({ requests: a.requests }); }}
                        style={{ background: 'rgba(252,211,77,0.08)', border: `1px solid ${t.color}`, color: t.color, borderRadius: '6px', padding: '4px 10px', fontSize: '11.5px', fontWeight: '600', cursor: 'pointer', fontFamily: 'system-ui' }}>
                        {a.link
                          ? <>{a.says} <i className="fa-solid fa-arrow-up-right-from-square" style={{ fontSize: '9px', marginLeft: '3px' }} /></>
                          : <><i className="fa-solid fa-plus" style={{ fontSize: '9px', marginRight: '4px' }} />{a.says}, then {choice.says.charAt(0).toLowerCase() + choice.says.slice(1)}</>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        };
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, fontFamily: 'system-ui' }}
            onClick={() => setDropConflict(null)}>
            <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '16px', width: '92%', maxWidth: '520px', maxHeight: '90vh', overflowY: 'auto' }}
              onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px 14px', borderBottom: '1px solid #2a3040' }}>
                <div style={{ fontSize: '16px', fontWeight: '700' }}>{free ? 'Before it lands' : 'Slot occupied'}</div>
                <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '16px', cursor: 'pointer' }} onClick={() => setDropConflict(null)}>✕</button>
              </div>
              <div style={{ padding: '14px 20px 0', fontSize: '13px', color: '#d1d5db', lineHeight: 1.5 }}>
                {free
                  ? <>The day is free, but there is something to know first. Choose where it goes:</>
                  : <><span style={{ color: '#f0a030', fontWeight: '600' }}>{dropConflict.says}.</span> Choose where it goes:</>}
              </div>

              {common.length > 0 && (
                <div style={{ margin: '12px 20px 0', padding: '10px 12px', background: '#171b22', border: '1px solid #2a3040', borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Whichever you choose</div>
                  {common.map((k, i) => checkLine({ ...k, actions: [] }, i, { says: '' }))}
                </div>
              )}

              <div style={{ padding: '14px 20px 6px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {doable.map(c => {
                  const rec = !!c.recommended;
                  const checks = c.checks || [];
                  const late = (c.notes || []).filter(n => n.kind === 'late');
                  return (
                    <div key={c.key} style={{ border: `1px solid ${rec ? 'rgba(20,184,166,0.55)' : '#2a3040'}`, background: rec ? 'rgba(20,184,166,0.05)' : '#1a2030', borderRadius: '10px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {rec && (
                        <div style={{ fontSize: '10px', fontWeight: '800', color: '#5eead4', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                          <i className="fa-solid fa-star" style={{ fontSize: '9px', marginRight: '5px' }} />Recommended — nothing to watch
                        </div>
                      )}
                      <button onClick={() => executeDropOption(c)}
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', width: '100%', textAlign: 'left',
                          background: rec ? '#14b8a6' : 'rgba(20,184,166,0.08)', color: rec ? '#0f172a' : '#5eead4',
                          border: `1px solid ${rec ? '#14b8a6' : 'rgba(20,184,166,0.6)'}`, borderRadius: '8px', padding: '10px 14px',
                          fontSize: '13.5px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui', transition: 'background 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = rec ? '#0d9488' : 'rgba(20,184,166,0.18)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = rec ? '#14b8a6' : 'rgba(20,184,166,0.08)'; }}>
                        <span>{c.says}</span>
                        <i className="fa-solid fa-arrow-right" style={{ fontSize: '12px', flexShrink: 0 }} />
                      </button>
                      {c.detail && <div style={{ fontSize: '12px', color: '#9ca3af', lineHeight: 1.45 }}>{c.detail}</div>}
                      {(c.notes || []).filter(n => n.kind !== 'late').map((n, i) => (
                        <div key={'n' + i} style={{ fontSize: '12px', color: '#93c5fd', lineHeight: 1.45 }}>↷ {n.says}</div>
                      ))}
                      {late.map((n, i) => checkLine({ severity: 'warning', says: n.says, actions: [] }, 'l' + i, c))}
                      {checks.map((k, i) => checkLine(k, i, c))}
                      {!legacy && !checks.length && !late.length && (
                        <div style={{ fontSize: '12px', color: '#6ee7b7' }}>
                          <i className="fa-solid fa-circle-check" style={{ fontSize: '11px', marginRight: '6px' }} />Nothing to watch
                        </div>
                      )}
                    </div>
                  );
                })}

                {notDoable.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '2px' }}>
                    <div style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Not possible</div>
                    {notDoable.map(c => (
                      <div key={c.key} style={{ padding: '8px 12px', border: '1px dashed #2a3040', borderRadius: '8px', opacity: 0.75 }}>
                        <div style={{ fontSize: '12.5px', fontWeight: '600', color: '#9ca3af' }}>{c.says}</div>
                        <div style={{ fontSize: '11.5px', color: '#6b7280', lineHeight: 1.45, marginTop: '2px' }}>{c.why}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px', padding: '12px 20px', borderTop: '1px solid #2a3040', marginTop: '8px' }}>
                <button style={{ background: 'none', border: '1px solid #374151', borderRadius: '8px', color: '#9ca3af', fontSize: '13px', padding: '7px 14px', cursor: 'pointer', fontFamily: 'system-ui' }} onClick={() => setDropConflict(null)}>
                  {free ? 'Put it back' : 'Cancel · keep where it was'}
                </button>
                {legacy && free && dropConflict.accept && (
                  <button onClick={() => executeDropOption({ request: dropConflict.accept })}
                    style={{ background: '#14b8a6', border: 'none', color: '#0f172a', borderRadius: '8px', padding: '8px 14px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui' }}>
                    Keep it, I'm covering
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {resizeConflict && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, fontFamily: 'system-ui' }}
          onClick={() => setResizeConflict(null)}>
          <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '16px', width: '90%', maxWidth: '440px' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 16px', borderBottom: '1px solid #2a3040' }}>
              <div style={{ fontSize: '16px', fontWeight: '700' }}>Slot occupied</div>
              <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '16px', cursor: 'pointer' }} onClick={() => setResizeConflict(null)}>✕</button>
            </div>
            <div style={{ padding: '14px 20px 4px', fontSize: '12px', color: '#6b7280' }}>
              <span style={{ color: '#f0a030', fontWeight: '600' }}>{resizeConflict.conflictName}</span> is already scheduled in the dates you're resizing into.
              How do you want to handle the conflict?
            </div>
            <div style={{ padding: '10px 20px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {resizeConflict.type === 'extend' ? (
                <>
                  <button onClick={() => executeResizeConflict('push_one')}
                    style={{ background: '#1a2030', border: '1px solid #2a3040', borderRadius: '8px', padding: '12px 14px', textAlign: 'left', cursor: 'pointer', fontFamily: 'system-ui', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#14b8a6'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#2a3040'}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#f0f2f5', marginBottom: '3px' }}>
                      Extend · bump {resizeConflict.conflictName} later
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                      Shift only {resizeConflict.conflictName} by {resizeConflict.newDates.length} working day{resizeConflict.newDates.length !== 1 ? 's' : ''} · other tasks stay in place
                    </div>
                  </button>
                  <button onClick={() => executeResizeConflict('push_all')}
                    style={{ background: '#1a2030', border: '1px solid #2a3040', borderRadius: '8px', padding: '12px 14px', textAlign: 'left', cursor: 'pointer', fontFamily: 'system-ui', transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = '#14b8a6'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = '#2a3040'}>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#f0f2f5', marginBottom: '3px' }}>
                      Extend · cascade all tasks from here
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>
                      Shift {resizeConflict.conflictName} and everything after it by {resizeConflict.newDates.length} working day{resizeConflict.newDates.length !== 1 ? 's' : ''}
                    </div>
                  </button>
                </>
              ) : (
                <button onClick={() => executeResizeConflict('push_one')}
                  style={{ background: '#1a2030', border: '1px solid #2a3040', borderRadius: '8px', padding: '12px 14px', textAlign: 'left', cursor: 'pointer', fontFamily: 'system-ui', transition: 'border-color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#14b8a6'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#2a3040'}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#f0f2f5', marginBottom: '3px' }}>
                    Prepend · move {resizeConflict.conflictName} earlier
                  </div>
                  <div style={{ fontSize: '11px', color: '#6b7280' }}>
                    Shift {resizeConflict.conflictName} back by {resizeConflict.newDates.length} working day{resizeConflict.newDates.length !== 1 ? 's' : ''} to make room
                  </div>
                </button>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '12px 20px', borderTop: '1px solid #2a3040' }}>
              <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '13px', cursor: 'pointer', fontFamily: 'system-ui' }} onClick={() => setResizeConflict(null)}>
                Cancel · keep original size
              </button>
            </div>
          </div>
        </div>
      )}

      {nonWorkingDrop && (
        <NonWorkingDropModal
          state={nonWorkingDrop}
          holidays={holidays}
          onExecute={executeOverrideDrop}
          onClose={() => setNonWorkingDrop(null)}
        />
      )}

      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, fontFamily: 'system-ui' }}
          onClick={() => setDeleteConfirm(null)}>
          <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '16px', width: '90%', maxWidth: '400px' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 16px', borderBottom: '1px solid #2a3040' }}>
              <div style={{ fontSize: '16px', fontWeight: '700' }}>
                {deleteConfirm.heldPlanId ? 'Release these days?' : deleteConfirm.single ? 'Delete scheduled job?' : 'Delete planned run?'}
              </div>
              <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '16px', cursor: 'pointer' }} onClick={() => setDeleteConfirm(null)}>✕</button>
            </div>
            <div style={{ padding: '20px' }}>
              {deleteConfirm.heldPlanId ? (
                <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0, lineHeight: 1.6 }}>
                  {deleteConfirm.planCount} day{deleteConfirm.planCount === 1 ? '' : 's'} held
                  {deleteConfirm.holdNote ? <> for <strong style={{ color: '#f0f2f5' }}>{deleteConfirm.holdNote}</strong></> : null}
                  {' '}will go back into the pool and can be scheduled by anything else. No order or job is affected — nothing was ever created for this.
                </p>
              ) : deleteConfirm.single ? (
                <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0 }}>
                  This scheduled entry will be permanently removed. If a job is currently running it will be cancelled.
                </p>
              ) : deleteConfirm.anyStarted ? (
                <>
                  <p style={{ fontSize: '13px', color: '#9ca3af', margin: '0 0 12px' }}>
                    This run has already started. The entry for <strong style={{ color: '#f0f2f5' }}>{deleteConfirm.fromDate}</strong> and all remaining future days will be deleted.
                  </p>
                  <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: '#fbbf24' }}>
                    Any currently running job will be cancelled.
                  </div>
                </>
              ) : (
                <>
                  <p style={{ fontSize: '13px', color: '#9ca3af', margin: '0 0 12px' }}>
                    This is part of a <strong style={{ color: '#f0f2f5' }}>{deleteConfirm.planCount}-day</strong> planned run. Deleting will remove all {deleteConfirm.planCount} days.
                  </p>
                  <div style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px', padding: '10px 12px', fontSize: '12px', color: '#fca5a5' }}>
                    This cannot be undone.
                  </div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 20px', borderTop: '1px solid #2a3040' }}>
              <button style={{ background: '#1e2329', color: '#9ca3af', border: '1px solid #2a3040', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', fontFamily: 'system-ui' }}
                onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button style={{ background: '#7f1d1d', color: '#fca5a5', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'system-ui' }}
                onClick={confirmDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {editJob !== undefined && (
        <ScheduleModal
          job={editJob} machines={machines} moulds={moulds} parts={parts} recipes={recipes}
          scheduled={scheduled} closures={closures}
          planJobCount={editJob?.plan_id ? scheduled.filter(j => j.plan_id === editJob.plan_id).length : 0}
          planSiblings={editJob?.plan_id ? scheduled.filter(j => j.plan_id === editJob.plan_id) : []}
          onSaved={() => { setEditJob(undefined); load(false); loadBacklog(true); }}
          onClose={() => setEditJob(undefined)}
        />
      )}

      <BacklogPanel open={showBacklog} onClose={() => setShowBacklog(false)} lines={backlog} error={backlogError}
        daysOn={backlogDaysOn} partOf={id => parts.find(p => p.id === id)}
        machineName={id => { const m = machines.find(x => x.id === Number(id)); return m ? machineName(m) : 'its machine'; }}
        onPick={pickBacklog} placingId={pendingPlacement?.backlog?.line_id || null} canPlace={isDirector()} focusIds={backlogFocus} />

      {liveJobId && (
        <LiveJobPanel jobId={liveJobId} onClose={() => setLiveJobId(null)} />
      )}

      {showCapacity && (
        <CapacityCheckModal onClose={() => setShowCapacity(false)} />
      )}

      {showStaff && (
        <ExtraStaffModal onClose={() => setShowStaff(false)} onSaved={() => setStaffingSeq(n => n + 1)} />
      )}

      <ReasonStrip
        queue={reasonQueue}
        reasons={reasons}
        mode={reasonMode}
        onPause={pauseReasons}
        onDone={(code, ids = []) => {
          setReasonQueue(q => q.filter(x => !ids.includes(x.event_id)));
          if (code) {
            const r = reasons.find(x => x.code === code);
            const n = ids.length;
            if (r) toast.success(n > 1 ? `Noted for all ${n} — ${r.label.toLowerCase()}` : `Noted — ${r.label.toLowerCase()}`);
          }
        }} />

      {showHistory && (
        <ScheduleHistoryModal
          onClose={() => setShowHistory(false)}
          onRestored={() => load(false)}
        />
      )}

      {showMachineOrder && (
        <MachineOrderModal
          machines={machines}
          toast={toast}
          onClose={() => setShowMachineOrder(false)}
          onSaved={(order) => {
            const at = new Map(order.map((id, i) => [id, i]));
            setMachines(ms => [...ms].sort((a, b) => (at.get(a.id) ?? Infinity) - (at.get(b.id) ?? Infinity)));
            setShowMachineOrder(false);
          }}
        />
      )}

      {showWindows && (
        <ShiftWindowsModal
          machines={machines}
          onClose={() => setShowWindows(false)}
          onChanged={() => load(false)}
          onReflow={(ids, names, from) => { setShowWindows(false); setReflowFor({ ids, names, from }); }}
        />
      )}

      {reflowFor && (
        <ReflowModal
          from={reflowFor.from || null}
          machineIds={reflowFor.ids}
          machineNames={reflowFor.names?.length ? reflowFor.names : machines.filter(m => reflowFor.ids.includes(m.id)).map(machineName)}
          onClose={() => setReflowFor(null)}
          onApplied={snapshotId => {
            if (snapshotId) {
              undoStack.current.push({ type: 'reflow', snapshotId });
              setCanUndo(true);
            }
            load(false);
          }}
          onUndone={() => {
            // Already reversed from the dialog, so drop the stack entry rather
            // than leave an arrow that would fail on a spent snapshot.
            undoStack.current = undoStack.current.filter(e => e.type !== 'reflow');
            setCanUndo(undoStack.current.length > 0);
            load(false);
          }}
        />
      )}

      {showPrint && (
        <PrintScheduleView
          viewMode={viewMode}
          viewDate={viewDate}
          machines={machines}
          scheduled={scheduled}
          colours={colours}
          holidays={holidays}
          TODAY={TODAY}
          onClose={() => setShowPrint(false)}
        />
      )}

      {showImport && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
          onClick={() => setShowImport(false)}>
          <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '16px', width: '100%', maxWidth: '760px', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 16px', borderBottom: '1px solid #2a3040', position: 'sticky', top: 0, background: '#1e2329', zIndex: 1 }}>
              <div>
                <div style={{ fontSize: '17px', fontWeight: '700' }}>Import from Excel</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Orders created here will also appear in the Orders page</div>
              </div>
              <button onClick={() => setShowImport(false)} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '18px', cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <ExcelForm onSaved={() => { setShowImport(false); load(false); }} />
            </div>
          </div>
        </div>
      )}

      {downTarget && (
        <DowntimeModal machine={downTarget} onClose={() => setDownTarget(null)}
          onDone={r => {
            setDownTarget(null); load(false);
            if (r && r.removed) toast.success(r.says);
            else if (r && r.block) toast.success(`${r.block.machine} is down ${r.block.span} (${r.block.reason}).${r.moved ? ` ${(r.notes || []).map(n => n.says).join(' ')}` : ''}`);
          }} />
      )}

      {moveTarget && (
        <MoveModal
          anchor={moveTarget}
          machines={machines}
          toast={toast}
          onClose={() => setMoveTarget(null)}
          onDone={() => { setMoveTarget(null); load(false); pushUndo(); }}
        />
      )}

      {showShortcuts && (
        <div
          onClick={() => setShowShortcuts(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, fontFamily: 'system-ui' }}
        >
          <div onClick={e => e.stopPropagation()} style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '16px', padding: '28px', width: '420px', maxWidth: '90vw', boxShadow: '0 24px 64px rgba(0,0,0,0.7)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <div>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#f0f2f5' }}>Keyboard shortcuts</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>Press <kbd style={{ background: '#131920', border: '1px solid #374151', borderRadius: '4px', padding: '1px 6px', fontSize: '11px', color: '#9ca3af', fontFamily: 'system-ui' }}>?</kbd> to toggle this panel</div>
              </div>
              <button onClick={() => setShowShortcuts(false)} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '18px', padding: '4px', lineHeight: 1 }}>✕</button>
            </div>
            {[
              { section: 'History' },
              { key: 'Ctrl + Z',         desc: 'Undo last action (up to 20 steps)' },
              { key: 'Ctrl + Shift + Z', desc: 'Redo' },
              { section: 'Drag & drop' },
              { key: 'Drag a plan',      desc: 'Move to a new date or machine' },
              { key: 'Alt + drag',       desc: 'Duplicate (clone to new slot, original stays)' },
              { section: 'Selection' },
              { key: 'Click a plan',     desc: 'Select it and open its bar: Complete, Edit, Move…, Delete. Click again to close' },
              { key: '← / →  arrows',   desc: 'Move selected plan by 1 working day' },
              { section: 'General' },
              { key: 'Esc',             desc: 'Close any modal or panel (acts as Cancel)' },
              { key: '?',               desc: 'Open / close this shortcuts panel' },
            ].map((row, i) =>
              row.section ? (
                <div key={i} style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em', marginTop: i === 0 ? 0 : '14px', marginBottom: '6px' }}>{row.section}</div>
              ) : (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid #2a3040' }}>
                  <kbd style={{ background: '#131920', border: '1px solid #374151', borderRadius: '6px', padding: '3px 10px', fontSize: '12px', color: '#14b8a6', fontFamily: 'system-ui', fontWeight: '600', whiteSpace: 'nowrap' }}>{row.key}</kbd>
                  <span style={{ fontSize: '13px', color: '#d1d5db', textAlign: 'right', maxWidth: '60%' }}>{row.desc}</span>
                </div>
              )
            )}
          </div>
        </div>
      )}

      {showCopyWeek && (
        <CopyWeekModal
          sourceMondayStr={fmt(getMonday(viewDate))}
          toast={toast}
          onClose={() => setShowCopyWeek(false)}
          onDone={() => { setShowCopyWeek(false); load(false); }}
        />
      )}

      {completingPlan && (
        <CompletePlanModal
          plan={completingPlan}
          toast={toast}
          onClose={() => setCompletingPlan(null)}
          onDone={(data) => { setCompletingPlan(null); load(false); offerGapClose(data?.close_gap); }}
        />
      )}

    </div>
  );
}

// ── Machine status dot ────────────────────────────────────────

function MachineStatusDot({ machine }) {
  const [show, setShow] = useState(false);
  const isMaintenance = machine.status === 'maintenance';
  const isIndefinite  = machine.maintenance_indefinite;
  const color = !isMaintenance ? '#22c55e' : isIndefinite ? '#ef4444' : '#f59e0b';
  const label = !isMaintenance
    ? 'Available'
    : isIndefinite
      ? 'In maintenance — indefinite'
      : `In maintenance until ${machine.maintenance_until || ''}`;
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <span
        style={{ width: '7px', height: '7px', borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0, cursor: 'default' }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      />
      {show && (
        <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', background: '#0d1117', border: '1px solid #2a3040', borderRadius: '6px', padding: '4px 8px', fontSize: '10px', color: '#f0f2f5', whiteSpace: 'nowrap', zIndex: 20, pointerEvents: 'none' }}>
          {label}
        </span>
      )}
    </span>
  );
}

// ── Plan span card (multi-day job block for week view) ────────

// What the bar under a clicked run says and offers (the director, 21 Oct
// 2026): the run in words, then Complete (or Reopen), Edit, Move…, Delete --
// each doing what its hover icon did, Move… opening the Move window.
function runBarProps({ rep, scheduled, planTotalsMap, isDirector, onComplete, onUncomplete, onEdit, onDelete, onMoveFrom, close }) {
  const jobs = (rep.plan_id ? scheduled.filter(j => j.plan_id === rep.plan_id) : [rep])
    .filter(j => j.status !== 'cancelled')
    .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
  const totals = rep.plan_id ? planTotalsMap?.[rep.plan_id] : null;
  const totalQty = totals?.total_target ?? jobs.reduce((n, j) => n + (j.target_qty || 0), 0);
  const made = totals?.total_made ?? madeOnce(jobs);
  const isAllCompleted = jobs.length > 0 && jobs.every(j => j.status === 'completed');
  const hasStarted = jobs.some(j => j.status === 'started' && j.job?.status === 'running');
  const canReopen = isDirector && jobs.some(j => j.director_completed);
  const week = rep.order_line?.order?.delivery_week;
  const title = [week ? `KW ${week}` : (!rep.order_line_id && !rep.order_number ? 'No order' : null), rep.parts?.name || rep.part_name || 'Run']
    .filter(Boolean).join(' · ');
  const first = jobs[0]?.scheduled_date || rep.scheduled_date;
  const last = jobs[jobs.length - 1]?.scheduled_date || rep.scheduled_date;
  // Pieces, each kept whole when the bar breaks on a narrow screen.
  const detail = [fmtSpan(first, last), `${(totalQty || 0).toLocaleString()} pcs`, made > 0 ? `${made.toLocaleString()} made` : null,
    isAllCompleted ? 'done' : hasStarted ? 'running now' : null].filter(Boolean);
  const act = (fn) => () => { close(); fn(); };
  const actions = !isDirector ? [] : [
    !isAllCompleted && { key: 'done', label: 'Complete', icon: 'fa-check', tone: '#10b981', disabled: hasStarted,
      title: hasStarted ? 'A shift is still running — end it in Today first' : 'Mark the run complete',
      onClick: act(() => onComplete({ planId: rep.plan_id, partName: rep.parts?.name || 'Job', totalQty, goodPartsMade: made })) },
    canReopen && { key: 'reopen', label: 'Reopen', icon: 'fa-rotate-left', tone: '#f59e0b', title: 'Revert the completion',
      onClick: act(() => onUncomplete(rep.plan_id)) },
    { key: 'edit', label: 'Edit', icon: 'fa-pen', tone: '#60a5fa', onClick: act(() => onEdit(rep)) },
    onMoveFrom && { key: 'move', label: 'Move…', icon: 'fa-arrows-left-right', tone: '#14b8a6',
      title: 'Move this run and everything after it on this machine: to a date, by working days, or to another machine',
      onClick: act(() => onMoveFrom({ planId: rep.plan_id || null, jobId: rep.id })) },
    { key: 'del', label: 'Delete', icon: 'fa-trash', tone: '#ef4444', onClick: act(() => onDelete(rep.id)) },
  ].filter(Boolean);
  return { title, detail, actions };
}

function PlanSpanCard({ span, isDirector, isDraggable, colours, dragging, filterText, onNavigateWeek, onDismissNew, planTotalsMap, justMoved = null, urgencyDays = { red: 2, amber: 7 } }) {
  const rep      = span.jobs[0];
  const partColourName = rep.parts?.colour || null;
  const partHex  = partColourName ? (colours.find(c => c.name === partColourName)?.hex || null) : null;
  const mbHex    = getMbHex(rep.recipe, colours);
  const mbText   = mbHex ? getMbTextColor(mbHex) : null;
  const titleColor = '#f0f2f5';
  const { bg, border } = partColourBg(partHex);
  const [hoverBtn, setHoverBtn] = useState(null);
  const hov = id => ({ onMouseEnter: () => setHoverBtn(id), onMouseLeave: () => setHoverBtn(null) });

  const isDimmed   = filterText && !span.jobs.some(j => jobMatchesFilter(j, filterText));
  const isDragging = dragging?.job?.id === rep.id;

  // Plan completion state
  const isAllCompleted    = span.jobs.every(j => j.status === 'completed');
  // scheduled_jobs.status stays 'started' even after the operator ends the job in Today
  // (jobs.status flips to 'completed' but the schedule row isn't updated).
  // Only treat a day as truly running when its linked job is still 'running'.

  // Use full plan totals (all weeks) so every span slice shows the same numbers
  const planTotals    = planTotalsMap?.[rep.plan_id];
  const totalQty      = planTotals?.total_target ?? span.totalQty;
  const goodPartsMade = planTotals?.total_made   ?? madeOnce(span.jobs);
  const progressPct   = totalQty > 0 ? Math.min(100, (goodPartsMade / totalQty) * 100) : 0;

  const orderLine = rep.order_line?.order;
  const todayStr  = fmt(new Date());
  const dueBadge  = (() => {
    if (!orderLine?.delivery_week) return null;
    const fri = weekToFriday(orderLine.delivery_week, orderLine.delivery_year);
    if (fri < todayStr) return { label: `Overdue · Wk ${orderLine.delivery_week}`, color: '#ef4444', overdue: true };
    const [fy, fm, fd] = fri.split('-').map(Number);
    const hardDL    = fmt(addDays(new Date(fy, fm - 1, fd), -urgencyDays.red));
    const primaryDL = fmt(addDays(new Date(fy, fm - 1, fd), -urgencyDays.amber));
    const lastDay = span.jobs[span.jobs.length - 1].scheduled_date;
    const color = lastDay > hardDL ? '#ef4444' : lastDay > primaryDL ? '#f59e0b' : null;
    return color ? { label: `Due Wk ${orderLine.delivery_week}`, color } : null;
  })();

  const isOverdue = dueBadge?.overdue === true;
  const urgencyOverlay = !isOverdue && (dueBadge?.color === '#ef4444'
    ? 'rgba(239,68,68,0.18)' : dueBadge?.color === '#f59e0b'
    ? 'rgba(245,158,11,0.14)' : null);

  // Why the engine put this run where it is. All of this is worked out while
  // proposing and recorded at commit; without surfacing it the reasoning is lost
  // the moment a proposal is accepted, and the board becomes unexplainable.
  // Capped at two badges — cards are narrow, and the tooltip carries the detail.
  const whyBadges = (() => {
    const out = [];
    const order   = rep.order_line?.order;
    const warning = rep.order_line?.schedule_warning;
    const wType   = rep.order_line?.schedule_warning_type;

    // Derive from where the run actually sits rather than trusting the stored
    // flag alone: that covers work dragged onto another machine by hand, and
    // works on runs committed before the flag existed. The flag then only
    // decides the wording — engine choice vs. manual move.
    const primaryId = rep.parts?.primary_machine_id;
    const offPrimary = primaryId != null && rep.machine_id != null
      && Number(rep.machine_id) !== Number(primaryId);
    const enginePlaced = span.jobs.some(j => j.is_alternative_machine);

    // Which shifts of the day this run occupies. Silent at slot 1 only,
    // which is every run on a single-shift board.
    const slots = [...new Set(span.jobs.map(j => j.shift_slot || 1))].sort();
    if (slots.length > 0 && !(slots.length === 1 && slots[0] === 1)) {
      const worked = slots.map(s => SHIFT_LABEL[s] || `shift ${s}`);
      out.push({
        key: 'slot',
        label: worked.length === 1 ? worked[0] : `${worked.length} shifts`,
        color: '#14b8a6',
        title: worked.length === 1
          ? `Runs on the ${worked[0]} shift only — the rest of the day is another job.`
          : `Runs across the ${worked.join(' and ')} shifts.`,
      });
    }

    if (offPrimary || enginePlaced) {
      const partName = rep.parts?.name || 'This part';
      out.push({
        key: 'alt', label: 'Alt machine', color: '#f59e0b',
        title: enginePlaced
          ? `${partName} is on a fallback machine — the scheduler placed it here because its primary machine was busy or unavailable.`
          : `${partName} is not on its primary machine — it was moved here.`,
      });
    }
    if (warning) {
      // The recorded type is authoritative. Matching the text is only a fallback
      // for rows written before schedule_warning_type existed — new warnings
      // should never rely on it, so rewording a message can't break the colour.
      const isGrace       = wType ? wType === 'grace_period'
                                  : /grace period/i.test(warning);
      const isMaintenance = wType ? wType === 'maintenance_delay'
                                  : /maintenance/i.test(warning);
      out.push({
        key:   'warn',
        label: isGrace ? 'No slack' : isMaintenance ? 'Deferred' : 'Note',
        color: isGrace ? '#f59e0b' : isMaintenance ? '#38bdf8' : '#9ca3af',
        title: warning,
      });
    }
    if (order && !order.delivery_week && order.schedule_intent === 'filler') {
      out.push({
        key: 'filler', label: 'Filler', color: '#a78bfa',
        title: 'Buffer work with no delivery date — only placed in slots that dated work did not want.',
      });
    }
    // A run is one piece (11 Oct 2026). One laid around another run before that
    // rule is drawn as its pieces, and says so first.
    if (span.inPieces) {
      out.unshift({
        key: 'pieces', label: 'In pieces', color: '#f87171',
        title: `${rep.parts?.name || 'This run'} was laid around another run on this machine, so it is in pieces — each one a mould change. `
          + 'To make it whole: drag it to where it should run. The whole run moves and is laid in one piece; if those days are taken you get the usual choices (push the others later, first free spot…). '
          + 'Or undo the order that placed it and schedule that order again — orders are now planned with every run in one piece.',
      });
    }
    return out.slice(0, 2);
  })();

  const greenOverlay = 'rgba(16,185,129,0.15)';
  const cardBg = isAllCompleted
    ? `linear-gradient(${greenOverlay},${greenOverlay}),${bg}`
    : isOverdue
    ? `repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(239,68,68,0.25) 5px, rgba(239,68,68,0.25) 7px),${bg}`
    : urgencyOverlay ? `linear-gradient(${urgencyOverlay},${urgencyOverlay}),${bg}` : bg;
  const isHeld = span.jobs.every(j => j.status === 'held');
  const holdExpiry = isHeld ? rep.hold_expires_at : null;
  const holdNote   = isHeld ? rep.hold_note : null;
  const cardBorder = isHeld ? '#14b8a6' : isAllCompleted ? '#10b981' : isOverdue ? '#dc2626' : (dueBadge?.color || border);
  const cardShadow = isAllCompleted ? '0 0 0 1px rgba(16,185,129,0.25), 0 2px 10px rgba(16,185,129,0.12)' : undefined;

  const tl = span.cameFromBefore  ? '0' : '8px';
  const tr = span.continuesAfter  ? '0' : '8px';
  const borderRadius = `${tl} ${tr} ${tr} ${tl}`;

  const remaining = totalQty > 0 ? Math.max(0, totalQty - goodPartsMade) : null;
  // Kept when the board was put back to an earlier point, although it was put
  // on after it (the director, 6 Oct 2026).
  const isNew = span.jobs.some(j => j.new_since_revert);

  return (
    <div className="mo-runcard"
      style={{
        flex: 1,
        borderRadius,
        background: cardBg,
        border: `${isOverdue ? '2px' : urgencyOverlay ? '3px' : '1px'} ${isHeld ? 'dashed' : 'solid'} ${cardBorder}`,
        // Ringed if the last undo or redo moved it. Teal rather than a status
        // colour: this is "look here", not "something is wrong".
        boxShadow: (justMoved && span.jobs.some(j => justMoved.has(j.id)))
          ? '0 0 0 2px #14b8a6, 0 0 14px rgba(20,184,166,0.45)'
          : cardShadow,
        padding: '0 10px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        cursor: isDraggable ? 'grab' : 'pointer',
        opacity: isDimmed ? 0.15 : isDragging ? 0.4 : 1,
        userSelect: 'none',
        minWidth: 0,
        overflow: 'hidden',
        position: 'relative',
        transition: 'opacity 0.15s',
        pointerEvents: 'all',
      }}
    >
      {span.cameFromBefore && (
        <button onClick={e => { e.stopPropagation(); onNavigateWeek(-1); }} {...hov('prev')} style={{ background: hoverBtn === 'prev' ? 'rgba(255,255,255,0.15)' : 'none', border: `1px solid ${hoverBtn === 'prev' ? 'rgba(255,255,255,0.25)' : 'transparent'}`, borderRadius: '4px', padding: '2px 4px', cursor: 'pointer', fontSize: '10px', color: titleColor, flexShrink: 0, lineHeight: 1, transition: 'background 0.15s, border-color 0.15s' }} title="Go to previous week">◀</button>
      )}
      <div style={{ flex: 1, minWidth: '40px', overflow: 'hidden' }}>
        <div style={{ fontSize: '12px', fontWeight: '700', color: isAllCompleted ? '#6ee7b7' : titleColor, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
          {rep.parts?.name || rep.part_name || '—'}
        </div>
        <div style={{ fontSize: '9px', color: mbText || '#9ca3af', marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {isAllCompleted
            // Which order it was made for stays on a finished run, first, so a
            // narrow card in the → view still shows it (the director, 15 Oct
            // 2026); the green Done badge already says it is done.
            // A run put on the board without an order says so, rather than nothing.
            ? `${orderLine?.delivery_week ? `KW ${orderLine.delivery_week} · ` : !rep.order_line_id && !rep.order_number ? 'No order · ' : ''}${goodPartsMade.toLocaleString()} / ${totalQty.toLocaleString()} pcs`
            // The order week first, so a narrow card keeps it; pieces after,
            // where there is room (the director, 21 Oct 2026).
            : [orderLine?.delivery_week ? `KW ${orderLine.delivery_week}` : null, `${totalQty.toLocaleString()} pcs`,
               span.jobs.length > 1 ? `${span.jobs.length}d` : null,
               remaining !== null && goodPartsMade > 0 ? `${remaining.toLocaleString()} left` : null].filter(Boolean).join(' · ')
          }
        </div>
      </div>
      {rep.shifts > 1 && (
        <span style={{ fontSize: '8px', fontWeight: '700', padding: '1px 6px', borderRadius: '3px', background: 'rgba(139,92,246,0.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.35)', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
          <i className="fa-solid fa-moon" style={{ fontSize: '7px' }} />{rep.shifts}×
        </span>
      )}
      {isNew && (
        <div role="button" tabIndex={isDirector ? 0 : -1}
          onClick={e => { if (!isDirector || !onDismissNew) return; e.stopPropagation(); onDismissNew(rep.plan_id, span.jobs.map(j => j.id)); }}
          onKeyDown={e => { if (isDirector && onDismissNew && e.key === 'Enter') { e.stopPropagation(); onDismissNew(rep.plan_id, span.jobs.map(j => j.id)); } }}
          title={'Put on the board after the point the board was put back to, and kept.' + (isDirector ? ' Click to dismiss.' : '')}
          style={{ fontSize: '8px', fontWeight: '800', padding: '1px 5px', borderRadius: '3px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '3px',
            letterSpacing: '0.05em', cursor: isDirector ? 'pointer' : 'default',
            background: 'rgba(20,184,166,0.2)', color: '#5eead4', border: '1px solid rgba(20,184,166,0.6)' }}>
          NEW{isDirector && <i className="fa-solid fa-xmark" style={{ fontSize: '7px', opacity: 0.75 }} />}
        </div>
      )}
      {!isAllCompleted && dueBadge && (
        <span style={{ fontSize: '8px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '3px',
          background: isOverdue ? 'rgba(239,68,68,0.22)' : 'rgba(255,255,255,0.06)',
          color: dueBadge.color,
          border: `1px solid ${isOverdue ? 'rgba(239,68,68,0.55)' : `${dueBadge.color}44`}`,
        }}>
          {isOverdue && <i className="fa-solid fa-circle-exclamation" style={{ fontSize: '7px' }} />}
          {dueBadge.label}
        </span>
      )}
      {isHeld && (
        <span title={`Capacity held${holdNote ? ` for ${holdNote}` : ''}${holdExpiry ? ` until ${holdExpiry}` : ''}. No order exists yet.`}
          style={{ fontSize: '8px', fontWeight: '700', padding: '1px 6px', borderRadius: '3px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'rgba(20,184,166,0.15)', color: '#5eead4', border: '1px solid rgba(20,184,166,0.45)' }}>
          <i className="fa-solid fa-lock" style={{ fontSize: '7px' }} />HELD{holdNote ? ` · ${holdNote}` : ''}
        </span>
      )}
      {!isAllCompleted && !isHeld && whyBadges.map(w => (
        <span key={w.key} title={w.title}
          style={{ fontSize: '8px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', flexShrink: 0,
            display: 'inline-flex', alignItems: 'center', gap: '3px', cursor: 'help',
            background: `${w.color}1f`, color: w.color, border: `1px solid ${w.color}55` }}>
          {w.label}
        </span>
      ))}
      {isAllCompleted && (
        // A one-day card is narrow: the tick alone, so the order week has room.
        <span title="Done" style={{ fontSize: '8px', fontWeight: '700', padding: span.jobs.length > 1 ? '1px 6px' : '1px 3px', borderRadius: '3px', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '3px', background: 'rgba(16,185,129,0.15)', color: '#6ee7b7', border: '1px solid rgba(16,185,129,0.35)' }}>
          <i className="fa-solid fa-circle-check" style={{ fontSize: '7px' }} />{span.jobs.length > 1 ? ' Done' : null}
        </span>
      )}
      {/* Complete, Edit, Move…, Delete: in the bar a click on the card opens
          (21 Oct 2026), no longer icons appearing on hover. */}
      {span.continuesAfter && (
        <button onClick={e => { e.stopPropagation(); onNavigateWeek(1); }} {...hov('next')} style={{ background: hoverBtn === 'next' ? 'rgba(255,255,255,0.15)' : 'none', border: `1px solid ${hoverBtn === 'next' ? 'rgba(255,255,255,0.25)' : 'transparent'}`, borderRadius: '4px', padding: '2px 4px', cursor: 'pointer', fontSize: '10px', color: titleColor, flexShrink: 0, lineHeight: 1, transition: 'background 0.15s, border-color 0.15s' }} title="Go to next week">▶</button>
      )}
      {/* Progress bar — thin strip at bottom showing good parts vs plan target */}
      {totalQty > 0 && (goodPartsMade > 0 || isAllCompleted) && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '2px', background: 'rgba(255,255,255,0.06)', borderRadius: `0 0 ${tr} ${tl}` }}>
          <div style={{ width: `${progressPct}%`, height: '100%', background: isAllCompleted ? '#10b981' : 'rgba(16,185,129,0.6)', borderRadius: 'inherit', transition: 'width 0.3s ease' }} />
        </div>
      )}
    </div>
  );
}

// ── Complete plan modal (director marks entire plan done from schedule) ──

// Also used by Orders, from a line's status (15 Oct 2026).
export function CompletePlanModal({ plan, toast, onClose, onDone }) {
  const [totalQty, setTotalQty]           = useState(plan.totalQty || 0);
  const [goodPartsMade, setGoodPartsMade] = useState(plan.goodPartsMade || 0);
  const [fetching, setFetching]           = useState(!!plan.planId);
  const [goodParts, setGoodParts]         = useState('');
  const [saving, setSaving]               = useState(false);
  // How much of it ran (the director, 11 Oct 2026): all of it, or up to a
  // day -- the days after it stay planned, as a run of their own.
  const [plannedDays, setPlannedDays]     = useState([]);   // [{ date, qty }] still planned
  const [lastDayRan, setLastDayRan]       = useState('');   // '' = all of it ran
  const today = fmt(new Date());
  const daysSoFar = plannedDays.filter(d => d.date <= today);
  const laterDays = plannedDays.filter(d => d.date > (lastDayRan || '9999-12-31'));
  // Finished early: its days after today come off (15 Oct 2026).
  const laterThanToday = plannedDays.filter(d => d.date > today);
  const dayWord = ds => new Date(ds + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

  function chooseLastDay(ds) {
    setLastDayRan(ds);
    const upTo = ds ? plannedDays.filter(d => d.date <= ds) : plannedDays;
    const qty = upTo.reduce((s, d) => s + d.qty, 0);
    setGoodParts(String((ds ? qty : Math.max(0, totalQty - goodPartsMade)) || ''));
  }

  // Fetch the complete plan (all days, not just the visible slice) to get true totals
  useEffect(() => {
    if (!plan.planId) return;
    api.get(`/api/v1/schedule?plan_id=${plan.planId}`)
      .then(r => {
        const allJobs = r.data || [];
        const qty  = allJobs.reduce((s, j) => s + (j.target_qty || 0), 0);
        const made = madeOnce(allJobs);
        setTotalQty(qty);
        setGoodPartsMade(made);
        setGoodParts(String(Math.max(0, qty - made) || ''));
        setPlannedDays(allJobs.filter(j => j.status === 'scheduled')
          .map(j => ({ date: j.scheduled_date, qty: j.target_qty || 0 }))
          .sort((a, b) => a.date.localeCompare(b.date)));
      })
      .catch(() => {
        // Fall back to span-visible values
        setGoodParts(String(Math.max(0, totalQty - goodPartsMade) || ''));
      })
      .finally(() => setFetching(false));
  }, [plan.planId]); // eslint-disable-line

  const remaining = Math.max(0, totalQty - goodPartsMade);

  function confirm() {
    setSaving(true);
    api.post(`/api/v1/schedule/plan/${plan.planId}/complete`, {
      good_parts_count: parseInt(goodParts) || 0,
      ...(lastDayRan ? { last_day_ran: lastDayRan } : {}),
    })
      .then(r => {
        const done = r?.data?.orders_completed || [];
        const kept = r?.data?.kept_planned || 0;
        const off = r?.data?.days_off || 0;
        // Its pallets on the truck, for a part sold as it is (2 Nov 2026).
        const truck = (r?.data?.truck_done || []).map(x => ` ${x.pallets} pallet${x.pallets === 1 ? '' : 's'} of ${x.line} on the ${x.order} truck ${x.pallets === 1 ? 'is' : 'are'} now done.`).join('');
        toast.success((done.length
          ? `Job marked complete — and ${done.join(', ')} ${done.length === 1 ? 'is' : 'are'} now completed: every line is done.`
          : kept
            ? `Marked complete up to ${dayWord(lastDayRan)}. The ${kept} later day${kept === 1 ? '' : 's'} stay planned, as a run of their own.`
            : off
              ? `Job marked complete. It finished early: its ${off} day${off === 1 ? '' : 's'} after today came off the board.`
              : 'Job marked complete') + truck, (done.length || kept || off || truck) ? 8000 : undefined);
        onDone(r?.data);
      })
      .catch(err => { toast.error(err?.response?.data?.error || 'Failed to complete job'); setSaving(false); });
  }

  const ov = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 };
  const md = { background: '#1a2030', border: '1px solid #2a3040', borderRadius: '16px', width: '90%', maxWidth: '400px', overflow: 'hidden' };

  return (
    <div style={ov} onClick={onClose}>
      <div style={md} onClick={e => e.stopPropagation()}>
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #2a3040', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '700', color: '#f0f2f5' }}>Mark Job Complete</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{plan.partName}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '16px', padding: '0 0 0 8px' }}>✕</button>
        </div>
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {fetching ? (
            <div style={{ fontSize: '12px', color: '#6b7280', textAlign: 'center', padding: '8px 0' }}>Loading plan totals…</div>
          ) : totalQty > 0 && (
            <div style={{ background: '#0f1117', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#6b7280', display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <span>Plan target: <strong style={{ color: '#f0f2f5' }}>{totalQty.toLocaleString()} pcs</strong></span>
              {goodPartsMade > 0 && <span>Already produced: <strong style={{ color: '#f0f2f5' }}>{goodPartsMade.toLocaleString()} pcs</strong></span>}
              <span>Remaining to log: <strong style={{ color: '#6ee7b7' }}>{remaining.toLocaleString()} pcs</strong></span>
            </div>
          )}
          {!fetching && plannedDays.length > 1 && (
            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: '6px' }}>How much of it ran?</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px', color: '#d1d5db' }}>
                <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: 'pointer' }}>
                  <input type="radio" checked={!lastDayRan} onChange={() => chooseLastDay('')} style={{ marginTop: '3px' }} />
                  <span>All of it — it's finished<span style={{ display: 'block', fontSize: '11px', color: '#6b7280' }}>{laterThanToday.length && daysSoFar.length
                    ? `Its days up to today are closed; the ${laterThanToday.length} day${laterThanToday.length === 1 ? '' : 's'} after today come off the board, and you can bring the runs behind it forward.`
                    : 'Every planned day is closed, and the board is free from there.'}</span></span>
                </label>
                <label style={{ display: 'flex', gap: '8px', alignItems: 'flex-start', cursor: daysSoFar.length ? 'pointer' : 'default', opacity: daysSoFar.length ? 1 : 0.5 }}
                  title={daysSoFar.length ? undefined : 'None of its days is today or earlier, so none of it can have run yet.'}>
                  <input type="radio" disabled={!daysSoFar.length} checked={!!lastDayRan}
                    onChange={() => chooseLastDay(daysSoFar[daysSoFar.length - 1].date)} style={{ marginTop: '3px' }} />
                  <span>
                    It ran up to{' '}
                    <select value={lastDayRan || (daysSoFar.length ? daysSoFar[daysSoFar.length - 1].date : '')}
                      disabled={!daysSoFar.length}
                      onChange={e => chooseLastDay(e.target.value)}
                      style={{ background: '#0f1117', border: '1px solid #374151', borderRadius: '6px', color: '#f0f2f5', fontSize: '12px', padding: '2px 6px', fontFamily: 'system-ui' }}>
                      {daysSoFar.map(d => <option key={d.date} value={d.date}>{dayWord(d.date)}</option>)}
                    </select>
                    <span style={{ display: 'block', fontSize: '11px', color: '#6b7280' }}>
                      {lastDayRan && laterDays.length
                        ? `The ${laterDays.length} later day${laterDays.length === 1 ? '' : 's'} (${laterDays.reduce((s, d) => s + d.qty, 0).toLocaleString()} pcs) stay planned, as a run of their own.`
                        : 'The days after it stay planned, as a run of their own.'}
                    </span>
                  </span>
                </label>
              </div>
            </div>
          )}
          <div>
            <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280', marginBottom: '6px' }}>{lastDayRan ? `Good parts produced up to ${dayWord(lastDayRan)} *` : 'Good parts produced (remaining) *'}</div>
            <input
              type="number"
              value={goodParts}
              onChange={e => setGoodParts(e.target.value)}
              placeholder="0"
              autoFocus
              style={{ width: '100%', background: '#0f1117', border: '1px solid #374151', borderRadius: '8px', color: '#f0f2f5', fontSize: '28px', fontWeight: '700', textAlign: 'center', padding: '14px', fontFamily: 'system-ui', boxSizing: 'border-box' }}
              onKeyDown={e => e.key === 'Enter' && !saving && !fetching && confirm()}
            />
          </div>
        </div>
        <div style={{ padding: '0 20px 20px', display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid #374151', borderRadius: '8px', color: '#9ca3af', padding: '8px 18px', cursor: 'pointer', fontSize: '13px', fontFamily: 'system-ui' }}>Cancel</button>
          <button onClick={confirm} disabled={saving || fetching} style={{ background: (saving || fetching) ? 'rgba(16,185,129,0.4)' : 'rgba(16,185,129,0.2)', border: '1px solid rgba(16,185,129,0.5)', borderRadius: '8px', color: '#6ee7b7', padding: '8px 22px', cursor: (saving || fetching) ? 'default' : 'pointer', fontSize: '13px', fontWeight: '600', fontFamily: 'system-ui', transition: 'background 0.15s' }}>
            {saving ? 'Saving…' : 'Confirm Complete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Bulk reschedule modal ─────────────────────────────────────

// ── Copy Week Modal ───────────────────────────────────────────

// The order the machines are shown in, top to bottom (the director, 2 Nov
// 2026: "more like one time thing, with changes only when machines are being
// changed"). Saved once for everyone; every list of machines follows it.
function MachineOrderModal({ machines, toast, onClose, onSaved }) {
  const [list, setList] = useState(machines);
  const [saving, setSaving] = useState(false);
  const changed = list.some((m, i) => m.id !== machines[i]?.id);
  const move = (i, by) => setList(l => {
    const j = i + by;
    if (j < 0 || j >= l.length) return l;
    const next = [...l];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  async function save() {
    setSaving(true);
    try {
      const order = list.map(m => m.id);
      await api.put('/api/v1/machines/order', { order });
      toast.success('Machine order saved');
      onSaved(order);
    } catch (err) {
      toast.error(err.response?.data?.error || 'The order could not be saved just now.');
      setSaving(false);
    }
  }
  const arrow = (disabled) => ({ background: 'none', border: '1px solid #2a3040', borderRadius: '6px', color: disabled ? '#374151' : '#9ca3af',
    width: '28px', height: '26px', cursor: disabled ? 'default' : 'pointer', fontSize: '11px' });
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '12px', padding: '18px', width: '100%', maxWidth: '400px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui', color: '#f0f2f5' }}>
        <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '4px' }}>Machine order</div>
        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
          Top to bottom, as the board shows them. Saved for everyone; a new machine goes at the bottom until you place it.
        </div>
        <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {list.map((m, i) => (
            <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: '#161b22', border: '1px solid #2a3040', borderRadius: '8px', padding: '6px 8px' }}>
              <span style={{ width: '18px', textAlign: 'right', fontSize: '11px', color: '#6b7280' }}>{i + 1}</span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontWeight: 700, fontSize: '13px' }}>{machineName(m)}</span>
                {m.display_name && m.name && m.display_name !== m.name && (
                  <span style={{ fontSize: '11px', color: '#6b7280', marginLeft: '6px' }}>{m.name}</span>
                )}
              </span>
              <button aria-label={`Move ${machineName(m)} up`} disabled={i === 0} onClick={() => move(i, -1)} style={arrow(i === 0)}>
                <i className="fa-solid fa-chevron-up" />
              </button>
              <button aria-label={`Move ${machineName(m)} down`} disabled={i === list.length - 1} onClick={() => move(i, 1)} style={arrow(i === list.length - 1)}>
                <i className="fa-solid fa-chevron-down" />
              </button>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '14px' }}>
          <button onClick={onClose} style={{ background: 'none', border: '1px solid #374151', color: '#9ca3af', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={!changed || saving}
            style={{ background: changed ? '#14b8a6' : '#1f2937', border: 'none', color: changed ? '#0f172a' : '#6b7280', borderRadius: '8px', padding: '7px 14px', fontSize: '13px', fontWeight: 700, cursor: changed && !saving ? 'pointer' : 'default' }}>
            {saving ? 'Saving…' : 'Save order'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CopyWeekModal({ sourceMondayStr, toast, onClose, onDone }) {
  const shiftStr  = (s, n) => fmt(addDays(new Date(s + 'T12:00:00'), n));
  const fmtDate   = s => new Date(s + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  const weekRange = m => `${fmtDate(m)} – ${fmtDate(shiftStr(m, 6))}`;

  const TARGETS = [
    { key: 'next',  label: 'Next week',       weeks: 1  },
    { key: '+2',    label: '+2 weeks',         weeks: 2  },
    { key: '+4',    label: '+4 weeks',         weeks: 4  },
    { key: 'empty', label: 'First empty week', weeks: null },
  ];

  const [activeTarget, setActiveTarget] = useState('next');
  const [preview, setPreview]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [selected, setSelected]         = useState(new Set());
  const [submitting, setSubmitting]     = useState(false);

  async function fetchPreview(targetKey) {
    setLoading(true);
    const body = { source_monday: sourceMondayStr, dry_run: true };
    if (targetKey === 'empty') {
      body.find_first_empty = true;
    } else {
      const weeks = TARGETS.find(t => t.key === targetKey)?.weeks || 1;
      body.target_monday = shiftStr(sourceMondayStr, weeks * 7);
    }
    try {
      const r = await api.post('/api/v1/schedule/copy-week', body);
      setPreview(r.data);
      setSelected(new Set((r.data.plans || []).filter(p => !p.has_conflict).map(p => p.plan_key)));
    } catch {
      toast.error('Failed to load preview');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchPreview('next'); }, []); // eslint-disable-line

  function handleTargetChange(key) {
    setActiveTarget(key);
    fetchPreview(key);
  }

  function toggle(key) {
    setSelected(prev => { const s = new Set(prev); s.has(key) ? s.delete(key) : s.add(key); return s; });
  }

  async function handleCopy() {
    if (!selected.size || !preview?.target_monday) return;
    setSubmitting(true);
    try {
      const r = await api.post('/api/v1/schedule/copy-week', {
        source_monday: sourceMondayStr,
        target_monday: preview.target_monday,
        dry_run: false,
        selected_plan_keys: [...selected],
      });
      toast.success(`${r.data.created} job${r.data.created !== 1 ? 's' : ''} copied to week of ${fmtDate(preview.target_monday)}`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Copy failed');
      setSubmitting(false);
    }
  }

  // Group plans by machine
  const byMachine = [];
  if (preview?.plans) {
    const order = [], map = {};
    for (const p of preview.plans) {
      if (!map[p.machine_id]) { map[p.machine_id] = { name: p.machine_name, plans: [] }; order.push(p.machine_id); }
      map[p.machine_id].plans.push(p);
    }
    for (const id of order) byMachine.push({ id, ...map[id] });
  }

  const totalCount    = preview?.plans?.length || 0;
  const selectedCount = selected.size;
  const conflictsIncluded = (preview?.plans || []).filter(p => p.has_conflict && selected.has(p.plan_key)).length;
  const canCopy = !submitting && selectedCount > 0 && !!preview?.target_monday;
  const sl = { fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.07em' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'system-ui' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '12px', width: '540px', maxWidth: '95vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>

        {/* Header */}
        <div style={{ padding: '20px 24px 0', borderBottom: '1px solid #2a3040' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#f0f2f5' }}>Copy Week Forward</div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '20px', lineHeight: 1, flexShrink: 0 }}>×</button>
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280', minHeight: '18px', marginBottom: '14px' }}>
            {loading
              ? (activeTarget === 'empty' ? 'Searching for first empty week…' : 'Loading preview…')
              : preview?.no_empty_found
              ? 'No empty week found in 26 weeks — showing next week as fallback'
              : preview?.target_monday
              ? `${totalCount} job${totalCount !== 1 ? 's' : ''} · ${weekRange(preview.target_monday)}`
              : 'No scheduled jobs in this week'}
          </div>

          {/* Target selector */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
            {TARGETS.map(t => {
              const isActive = activeTarget === t.key;
              const isSearching = isActive && t.key === 'empty' && loading;
              return (
                <button key={t.key} onClick={() => handleTargetChange(t.key)} disabled={loading} style={{
                  flex: 1, padding: '7px 4px', borderRadius: '8px', fontFamily: 'system-ui', fontSize: '11px', fontWeight: '700',
                  cursor: loading ? 'default' : 'pointer',
                  background: isActive ? 'rgba(167,139,250,0.18)' : '#131920',
                  border: `1px solid ${isActive ? 'rgba(167,139,250,0.6)' : '#2a3040'}`,
                  color: isActive ? '#a78bfa' : '#6b7280',
                  transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
                }}>
                  {isSearching && <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '9px' }} />}
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '8px 0' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#4b5563', fontSize: '13px' }}>
              {activeTarget === 'empty' ? 'Scanning weeks ahead for a free slot…' : 'Loading…'}
            </div>
          ) : totalCount === 0 ? (
            <div style={{ padding: '40px 24px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
              No <em>scheduled</em> jobs in this week. Started or completed jobs are not copied.
            </div>
          ) : byMachine.map(({ id, name, plans }) => (
            <div key={id}>
              <div style={{ padding: '10px 24px 4px', ...sl }}>{name}</div>
              {plans.map(p => {
                const isSelected = selected.has(p.plan_key);
                const isPartial  = p.in_source_week < p.days;
                return (
                  <label key={p.plan_key} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '10px 24px', cursor: 'pointer',
                    background: isSelected ? 'rgba(167,139,250,0.06)' : 'transparent',
                  }}>
                    <input type="checkbox" checked={isSelected} onChange={() => toggle(p.plan_key)}
                      style={{ width: '15px', height: '15px', accentColor: '#a78bfa', flexShrink: 0, cursor: 'pointer', marginTop: '2px' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#f0f2f5' }}>{p.part_name || '—'}</span>
                        {p.days > 1 && (
                          <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', background: 'rgba(167,139,250,0.12)', color: '#a78bfa', border: '1px solid rgba(167,139,250,0.3)' }}>
                            {p.days}d
                          </span>
                        )}
                        {isPartial && (
                          <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', background: 'rgba(99,102,241,0.10)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)' }} title={`${p.in_source_week} of ${p.days} days fall in this week — full plan will be copied`}>
                            spans weeks
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '3px' }}>
                        {fmtDate(p.target_start)}{p.days > 1 ? ` → ${fmtDate(p.target_end)}` : ''}
                        {p.total_qty > 0 ? ` · ${p.total_qty.toLocaleString()} pcs` : ''}
                      </div>
                    </div>
                    {p.has_conflict && (
                      <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 7px', borderRadius: '4px', background: 'rgba(245,158,11,0.12)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)', whiteSpace: 'nowrap', flexShrink: 0, marginTop: '2px' }}>
                        ⚠ Conflict
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        {!loading && totalCount > 0 && (
          <div style={{ padding: '14px 24px', borderTop: '1px solid #2a3040', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <div style={{ fontSize: '12px', color: '#6b7280' }}>
              {selectedCount} of {totalCount} selected
              {conflictsIncluded > 0 && (
                <span style={{ marginLeft: '8px', color: '#f59e0b' }}>
                  · {conflictsIncluded} conflict{conflictsIncluded !== 1 ? 's' : ''} included
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={onClose} style={{ background: 'none', border: '1px solid #2a3040', borderRadius: '8px', color: '#9ca3af', padding: '8px 16px', cursor: 'pointer', fontFamily: 'system-ui', fontSize: '13px', fontWeight: '600' }}>
                Cancel
              </button>
              <button onClick={handleCopy} disabled={!canCopy} style={{
                background: canCopy ? '#a78bfa' : '#2a3040', border: 'none', borderRadius: '8px',
                color: canCopy ? '#0f172a' : '#6b7280', padding: '8px 18px',
                cursor: canCopy ? 'pointer' : 'default', fontFamily: 'system-ui', fontSize: '13px', fontWeight: '700',
                transition: 'background 0.15s',
              }}>
                {submitting ? 'Copying…' : `Copy ${selectedCount} item${selectedCount !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Week view ─────────────────────────────────────────────────

function WeekView({ machines, scheduled, viewDate, TODAY, holidays, jobsFor, justMoved = null, shiftWindowFor = () => null, downFor = () => null, onMachineDown, expandShifts = true, prevJobOnMachine, nextJobOnMachine, getChangeover, getPurge = () => null, isDirector, onEdit, onDelete, onMove, onDuplicate, onSwap, colours, onLiveView, jobPlanTotals, planTotalsMap, filterText, onNavigateWeek, allDeliveryWeekSet, toast, pendingPlacement, onEdgeDrop, onClearPendingPlacement, onInsertConflict, onPendingPlace, onMoveInsertBefore, onResizeEnd, onBulkReschedule, onMoveFrom, onInsertAfter, onNonWorkingDrop, compactMachines, toggleCompact, weekSpan = 1, dispatchDates = {}, onDispatchReload, onComplete, onUncomplete, onDismissNew, onVisibleWeekChange, scrollIntent, centerTodaySeq = 0, staffingSeq = 0, onDropHints, urgencyDays = { red: 2, amber: 7 }, showCompleted = true }) {
  const navigate  = useNavigate();
  const windowWidth = useWindowWidth();
  const isPhone     = windowWidth <= PHONE_WIDTH;
  const machineCol  = machineColFor(windowWidth);
  const weekStart = getMonday(viewDate);
  const days = Array.from({ length: 7 * weekSpan }, (_, i) => addDays(weekStart, i));

  // ── The column model (spec §11) ─────────────────────────────────────────
  //
  // The board used to have one column per day, which was right while a
  // machine-day held one job. With shift windows a day subdivides
  // HORIZONTALLY into shift-width sub-columns -- deliberately not stacked
  // bands, because stacking implies parallel and shifts are sequential. Time
  // keeps flowing one way across the whole board and span cards simply cross
  // more columns, so the existing subgrid extends rather than breaks.
  //
  // Everything below is therefore expressed in columns, never in days. With
  // every day at one slot the arithmetic collapses to exactly what it was,
  // which is how this lands without moving a pixel until a window says
  // otherwise.
  // How many shifts a machine works on a day. Absent a window, one.
  const shiftsOnMachineDay = (machineId, ds) =>
    Math.min(3, Math.max(1, ((shiftWindowFor(machineId, ds) || {}).shifts) || 1));

  // How many distinct shifts are actually occupied on a machine-day.
  //
  // Built once rather than scanned per cell: a four-week board asks this a few
  // hundred times and the answer never changes between renders.
  const slotsInUse = useMemo(() => {
    const m = new Map();
    for (const j of scheduled) {
      if (j.status === 'cancelled') continue;
      const k = `${j.machine_id}|${j.scheduled_date}`;
      if (!m.has(k)) m.set(k, new Set());
      m.get(k).add(j.shift_slot || 1);
    }
    return m;
  }, [scheduled]);

  // How many parts a machine-day is drawn in.
  //
  // Whichever is greater: the shifts genuinely in use, or -- when expanded --
  // the shifts the machine is declared to work. So a run occupying a morning
  // and an afternoon stays divided even condensed, because hiding one behind
  // the other is never the right answer; while a declared but half-empty
  // two-shift day only takes the extra width when the toggle is on.
  const divisionsOn = (machineId, ds) => {
    const used = slotsInUse.get(`${machineId}|${ds}`);
    const occupied = used ? used.size : 1;
    return Math.max(occupied, expandShifts ? shiftsOnMachineDay(machineId, ds) : 1);
  };

  // A day is as wide as the busiest machine on it, so a three-shift day is
  // visibly three times a one-shift day and the heavy days read at a glance
  // (§11, "width is proportional to capacity"). Days with nothing declared stay
  // exactly one column wide -- expanding the whole board to reveal detail on
  // five days is what this avoids.
  const daySlots = days.map(d => {
    const ds = fmt(d);
    return machines.reduce((mx, m) => Math.max(mx, divisionsOn(m.id, ds)), 1);
  });

  const dayColStart = [];
  {
    let acc = 0;
    for (const n of daySlots) { dayColStart.push(acc); acc += n; }
  }
  const totalCols = dayColStart.length ? dayColStart[dayColStart.length - 1] + daySlots[daySlots.length - 1] : 0;

  // 1-based, inclusive start and exclusive end, in subgrid coordinates.
  const colStartOf = i => (dayColStart[i] ?? i) + 1;
  const colEndOf   = i => (dayColStart[i] ?? i) + (daySlots[i] ?? 1) + 1;
  const colSpanOf  = (from, to) => `${colStartOf(from)} / ${colEndOf(to)}`;

  // A run's columns, taking account of which shifts it actually occupies.
  //
  // A machine is only subdivided on a day when it works as many shifts as the
  // day is wide. Otherwise the run takes the whole day -- which is §11's "divide
  // a cell only when the slots have different occupants, or a free one", and
  // keeps a machine that is not in the window from being sliced up to match one
  // that is.
  // Where one shift of a machine-day begins, in columns.
  //
  // A machine may be divided into fewer parts than the day is wide -- two runs
  // on a machine sharing a day that some other machine works three shifts. So
  // the share is proportional rather than one-column-per-shift, which keeps
  // every case honest and needs no special-casing.
  const slotEdge = (machineId, dayIdx, slot) => {
    const ds = fmt(days[dayIdx]);
    const parts = Math.max(1, divisionsOn(machineId, ds));
    const width = Math.max(1, daySlots[dayIdx]);
    const s = Math.min(parts, Math.max(0, slot));
    return colStartOf(dayIdx) + Math.floor((s * width) / parts);
  };

  const runCols = (machineId, startIdx, startSlot, endIdx, endSlot) => {
    const from = slotEdge(machineId, startIdx, (startSlot || 1) - 1);
    const to   = slotEdge(machineId, endIdx, endSlot || 1);
    return `${from} / ${Math.max(to, from + 1)}`;
  };
  const scrollContainerRef = useRef(null);
  const scrollIntervalRef  = useRef(null);
  const weekSpanRef        = useRef(weekSpan); weekSpanRef.current = weekSpan;
  const [dragging, setDragging] = useState(null); // { job, fromDate, fromMachineId, isPlan }
  const [dragOver, setDragOver] = useState(null); // { machineId, dateStr }
  const altDragRef = useRef(false);               // true when Alt held during drag (check on drop)
  const [isDuplicating, setIsDuplicating] = useState(false); // for visual feedback only
  const [insertAfterHoverId, setInsertAfterHoverId] = useState(null); // plan_id or job id of span whose right-edge is hovered

  function getDraggedPlanLength(job) {
    if (job.plan_id) {
      return scheduled.filter(j => j.plan_id === job.plan_id && j.status === 'scheduled').length || 1;
    }
    return 1;
  }

  function getNonWorkingBlockLen(dateStr) {
    let count = 0;
    let d = new Date(dateStr + 'T12:00:00');
    for (let safety = 0; safety < 500; safety++) {
      const ds = fmt(d);
      if (!isWeekendStr(ds) && !holidays[ds]) break;
      count++;
      d = addDays(d, 1);
    }
    return Math.max(count, 1);
  }

  // Show copy cursor system-wide while Alt+dragging
  useEffect(() => {
    if (!dragging || !isDuplicating) return;
    document.body.style.cursor = 'copy';
    return () => { document.body.style.cursor = ''; };
  }, [dragging, isDuplicating]);

  // After an edge navigation, stay pressed against the edge we were pushing so
  // the newly revealed week comes into view instead of scrolling out from under
  // the cursor. -1 = pin left, 1 = pin right, null = leave scroll alone.
  const pendingScrollEdge = useRef(null);

  const [dragOverEdge, setDragOverEdge] = useState(null); // 'prev' | 'next' | null

  // What every visible cell would say if the card landed there.
  //
  // Asked once, when the card is picked up -- not as the mouse moves. Seven
  // machines across four weeks is close to two hundred questions, and asking
  // them per dragover would make the board stutter for no gain: the answers
  // cannot change while a card is in the air.
  //
  // Conflicts the board already draws for itself are dropped on arrival.
  // Weekends are cross-hatched and ineligible machines tinted red long before
  // this existed, and saying the same thing twice in two colours is worse than
  // not saying it at all.
  const [dropHints, setDropHints] = useState(null);   // Map "machineId|date" -> { severity, says }

  const daysKey     = days.length ? `${fmt(days[0])}+${days.length}` : '';
  const machinesKey = machines.map(m => m.id).join(',');

  useEffect(() => {
    const job = dragging?.job;
    if (!job) { setDropHints(null); onDropHints?.(null); return; }

    let dropped = false;
    const cells = [];
    for (const m of machines) for (const d of days) cells.push({ machine_id: m.id, date: fmt(d) });

    api.post('/api/v1/conflicts/preview', {
      action: 'move',
      part_id: job.part_id || null,
      mould_id: job.mould_id || null,
      job_id:  dragging.isPlan ? null : job.id,
      plan_id: dragging.isPlan ? (job.plan_id || null) : null,
      from: { machine_id: dragging.fromMachineId, date: dragging.fromDate },
      cells,
    })
      .then(r => {
        if (dropped) return;
        const map = new Map();
        for (const c of (r.data?.results || [])) {
          if (c.ok) continue;
          if (c.rule === 'non_working_day') continue;   // already cross-hatched
          if (c.severity === 'note') continue;          // a note records; it does not ask
          map.set(`${c.machine_id}|${c.date}`, { severity: c.severity, says: c.says });
        }
        setDropHints(map);
        onDropHints?.(map);
      })
      .catch(() => { /* a hint that fails to load is not worth interrupting a drag for */ });

    return () => { dropped = true; };
    // Keyed on what the window actually IS, not on the arrays describing it.
    // `days` is rebuilt every render and `machines` may be too, so depending on
    // them directly would re-fire this on every mouse movement during a drag --
    // one request per dragover, which is the exact cost asking once was meant
    // to avoid.
  }, [dragging, machinesKey, daysKey]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [edgeFilling, setEdgeFilling] = useState(null); // 'prev' | 'next' | null — countdown animation
  const [edgeFillDuration, setEdgeFillDuration] = useState(500); // ms — matches the nav delay
  const [weekSlideAnim, setWeekSlideAnim] = useState(null); // 'from-right' | 'from-left' | null
  const edgeNavTimer = useRef(null);
  // Stable refs so document-level effects can read current prop/state without re-registering
  const onEdgeDropRef = useRef(onEdgeDrop);         onEdgeDropRef.current = onEdgeDrop;
  const onNavigateWeekRef = useRef(onNavigateWeek); onNavigateWeekRef.current = onNavigateWeek;
  const onClearPendingRef = useRef(onClearPendingPlacement); onClearPendingRef.current = onClearPendingPlacement;
  const draggingRef = useRef(dragging);             draggingRef.current = dragging;
  const resizingRef = useRef(null); // { side, spanKey, startX, colWidth, startEndIdx|startStartIdx, planSpan, machineId, currentDelta }
  const touchRef = useRef(null); // touch drag: { job, fromDate, fromMachineId, isPlan, startX, startY, active, ghostEl, offsetX, offsetY, timer }
  const [resizeDeltaMap, setResizeDeltaMap] = useState({}); // { [spanKey]: deltaCol } right-side live preview
  const [resizeStartDeltaMap, setResizeStartDeltaMap] = useState({}); // { [spanKey]: deltaCol } left-side live preview
  // The run clicked: its bar shows (21 Oct 2026), and ← → nudge it where it can move.
  const [selectedSpan, setSelectedSpan] = useState(null); // { jobId, planId, canNudge }
  const [dispatchPopover, setDispatchPopover] = useState(null); // { date, orders, x, y } | null

  // Days on screen where a shift has more work than people (Staffing, 28 Sep):
  // date -> the worst shift that day. Asked again whenever the board reloads.
  const [staffingDays, setStaffingDays] = useState(null);
  const staffFrom = days.length ? fmt(days[0]) : null;
  const staffTo   = days.length ? fmt(days[days.length - 1]) : null;
  const seesStaffing = useAuth().can('see_staffing');
  useEffect(() => {
    if (!staffFrom || !seesStaffing) return;
    let live = true;
    api.get(`/api/v1/staffing/days?from=${staffFrom}&to=${staffTo}`)
      .then(r => {
        if (!live) return;
        const rank = { unknown: 1, tight: 2, needs_call: 3, short: 4 };
        const m = new Map();
        // A shift the director covered on its window (9 Oct 2026) ranks below
        // any he has not: the marker says what still needs an answer.
        const weight = d => (d.covered ? 0.5 : (rank[d.verdict] || 0));
        for (const d of r.data?.days || []) {
          const cur = m.get(d.date);
          if (!cur || weight(d) > weight(cur)) m.set(d.date, d);
        }
        setStaffingDays(m);
      })
      .catch(() => { if (live) setStaffingDays(null); });
    return () => { live = false; };
  }, [staffFrom, staffTo, scheduled, staffingSeq, seesStaffing]);
  const [dispatchSaving, setDispatchSaving] = useState(false);

  // Scroll today's column to the horizontal center of the viewport -- when the
  // board opens, when the span changes, and when Today is pressed. Not when the
  // week arrows (or a card's ◀ ▶) move the dates: until 25 Sep it re-centred
  // on every date change, so on a wide span, where today stays in range for
  // many clicks, each arrow click moved the dates and then scrolled straight
  // back to today. Left alone, the scroll stays put and the board shifts by
  // exactly the week the arrow moved it.
  useEffect(() => {
    // Not while an edge navigation is handing the view forward — re-centring on
    // today there drags the scroll back to the start of the span, so the user
    // has to cross the whole grid again to reach the edge for the next step.
    if (pendingScrollEdge.current != null) return;
    const el = scrollContainerRef.current;
    if (!el || weekSpan <= 1) return;
    const todayStr = fmt(new Date());
    const todayIdx = days.findIndex(d => fmt(d) === todayStr);
    if (todayIdx < 0) return;
    const colWidth = (el.scrollWidth - machineColFor(window.innerWidth)) / days.length;
    const todayCenter = machineColFor(window.innerWidth) + todayIdx * colWidth + colWidth / 2;
    el.scrollLeft = todayCenter - el.clientWidth / 2;
  }, [weekSpan, centerTodaySeq]); // eslint-disable-line

  // Track which week is at the left edge as the user scrolls horizontally
  const [scrollWeek, setScrollWeek] = useState(null); // null = use currWeek
  const daysRef = useRef(days);
  daysRef.current = days;
  // After any move, say which week is now at the left edge. The scroll stays
  // where it was, so that is not necessarily the first week of the range.
  useEffect(() => {
    const el = scrollContainerRef.current;
    const d = daysRef.current;
    if (!el || weekSpan <= 1 || !d.length) { setScrollWeek(null); onVisibleWeekChange?.(null); return; }
    const colWidth = (el.scrollWidth - machineColFor(window.innerWidth)) / d.length;
    const idx = Math.max(0, Math.min(Math.floor(el.scrollLeft / colWidth), d.length - 1));
    const { week } = isoWeekOf(d[idx]);
    setScrollWeek(week);
    onVisibleWeekChange?.(week);
  }, [viewDate, weekSpan, centerTodaySeq]); // eslint-disable-line
  useEffect(() => {
    if (weekSpan <= 1) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const handleScroll = () => {
      const d = daysRef.current;
      const colWidth = (el.scrollWidth - machineColFor(window.innerWidth)) / d.length;
      const idx = Math.max(0, Math.min(Math.floor(el.scrollLeft / colWidth), d.length - 1));
      const { week } = isoWeekOf(d[idx]);
      setScrollWeek(week);
      onVisibleWeekChange?.(week);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [weekSpan, onVisibleWeekChange]); // eslint-disable-line

  // Center a specific date column when the parent requests it (e.g. delivery week badge click)
  useEffect(() => {
    if (!scrollIntent) return;
    const el = scrollContainerRef.current;
    if (!el) return;
    const idx = daysRef.current.findIndex(d => fmt(d) === scrollIntent.date);
    if (idx < 0) return;
    const colWidth = (el.scrollWidth - machineColFor(window.innerWidth)) / daysRef.current.length;
    el.scrollTo({ left: (machineColFor(window.innerWidth) + idx * colWidth + colWidth / 2) - el.clientWidth / 2, behavior: 'smooth' });
  }, [scrollIntent]); // eslint-disable-line

  // Machine eligibility for the job currently being dragged
  const draggedEligible = dragging
    ? [dragging.job.parts?.primary_machine_id, ...(dragging.job.parts?.secondary_machine_ids || [])]
        .filter(Boolean).map(Number)
    : null;
  const dragHasEligibility = draggedEligible !== null && draggedEligible.length > 0;

  // Machine eligibility for the pending cross-week placement
  const pendingEligible = pendingPlacement
    ? [pendingPlacement.parts?.primary_machine_id, ...(pendingPlacement.parts?.secondary_machine_ids || [])]
        .filter(Boolean).map(Number)
    : null;
  const pendingHasEligibility = pendingEligible !== null && pendingEligible.length > 0;

  // Snap-to-gap: find the nearest working day on or after a given date
  function getSnapStart(dateStr) {
    let cur = new Date(dateStr + 'T12:00:00');
    for (let i = 0; i < 14; i++) {
      const ds = fmt(cur);
      if (!isWeekendStr(ds) && !holidays[ds]) return ds;
      cur = addDays(cur, 1);
    }
    return dateStr;
  }

  // Number of days in the plan being dragged (1 for single jobs)
  const planDaysCount = dragging
    ? (dragging.isPlan
        ? (scheduled.filter(j => j.plan_id === dragging.job.plan_id && j.status === 'scheduled').length || 1)
        : 1)
    : 0;

  // Full landing footprint: all dates the plan would occupy if dropped at dragOver position
  const snapFootprint = React.useMemo(() => { // eslint-disable-line react-hooks/exhaustive-deps
    // A backlog line being placed (20 Oct 2026): its estimated days on the
    // machine under the pointer.
    const backlogDays = !dragging && pendingPlacement?.backlog && dragOver ? (pendingPlacement.days_on?.[dragOver.machineId] || 1) : 0;
    if ((!dragging && !backlogDays) || !dragOver) return [];
    const snapStart = getSnapStart(dragOver.dateStr);
    return Array.from({ length: backlogDays || planDaysCount }, (_, i) =>
      getNthWorkingDay(new Date(snapStart + 'T12:00:00'), i, holidays)
    );
  }, [dragging, dragOver, planDaysCount, holidays, pendingPlacement]); // eslint-disable-line react-hooks/exhaustive-deps

  // Membership is checked once per day cell on every dragover. As an array that
  // is a linear scan per cell — fine at one week, wasteful across six months.
  const snapFootprintSet = React.useMemo(() => new Set(snapFootprint), [snapFootprint]);

  // Preview footprint when hovering over a non-working day cell (amber = calendar, teal = rest on working days)
  const overrideFootprint = React.useMemo(() => { // eslint-disable-line react-hooks/exhaustive-deps
    if (!dragging || !dragOver || isDuplicating) return { calendar: new Set(), working: new Set() };
    const hoverDs = dragOver.dateStr;
    if (!isWeekendStr(hoverDs) && !holidays[hoverDs]) return { calendar: new Set(), working: new Set() };
    const blockLen = getNonWorkingBlockLen(hoverDs);
    const calDays = Math.min(blockLen, planDaysCount);
    const calendar = new Set();
    const working = new Set();
    let d = new Date(hoverDs + 'T12:00:00');
    for (let i = 0; i < calDays; i++) { calendar.add(fmt(d)); d = addDays(d, 1); }
    let added = 0;
    for (let s = 0; s < 500 && added < planDaysCount - calDays; s++) {
      const ds = fmt(d);
      if (!isWeekendStr(ds) && !holidays[ds]) { working.add(ds); added++; }
      d = addDays(d, 1);
    }
    return { calendar, working };
  }, [dragging, dragOver, planDaysCount, isDuplicating, holidays]); // eslint-disable-line react-hooks/exhaustive-deps

  const { week: currWeek } = isoWeekOf(weekStart);
  const visibleDeliveryWeeks = Array.from({ length: weekSpan }, (_, i) => {
    const { week: w, year: y } = isoWeekOf(addDays(weekStart, i * 7));
    return { week: w, key: `${y}-${String(w).padStart(2, '0')}` };
  }).filter(({ key }) => allDeliveryWeekSet.has(key));
  const isDeliveryWeek = visibleDeliveryWeeks.length > 0;

  // Cascade preview: simulate which jobs would be displaced and where they'd land.
  // Runs whenever dragOver changes so the ghost tiles update in real time.
  // Also covers pending placement (cross-week) where dragging=null.
  // Ghost cards: where the runs in the way would go if they were pushed.
  //
  // Asked of the server's one cascade (POST /schedule/make-room/preview) --
  // the same code "Put it here · push what is in the way later" runs -- so the
  // ghosts cannot show one thing and the drop do another. Until 25 Sep this
  // was simulated here, from only the weeks on screen and with its own idea of
  // how far to push. Asked only once the pointer rests on a cell, remembered
  // per cell for the rest of the drag, and a late answer for a cell already
  // left is thrown away.
  const [cascadePreview, setCascadePreview] = useState([]);
  const previewCache = useRef(new Map());
  const previewSeq   = useRef(0);
  const previewJob   = dragging?.job || pendingPlacement;
  const previewMid   = dragOver?.machineId;
  const previewDate  = snapFootprint[0] || dragOver?.dateStr;
  useEffect(() => {
    if (!previewJob) previewCache.current.clear();
    if (!previewJob || !previewMid || !previewDate) { setCascadePreview([]); return; }
    const anchor = !dragging && pendingPlacement ? 'first' : 'grabbed';
    const key = `${previewJob.id}|${previewMid}|${previewDate}|${anchor}`;
    const seq = ++previewSeq.current;
    const show = (moves, parts) => {
      if (seq !== previewSeq.current) return;
      setCascadePreview(moves.map(m => {
        const job = scheduled.find(j => j.id === m.id)
          || { id: m.id, machine_id: m.machine_id, parts: { name: parts[m.id] || 'A run' } };
        return { job, fromDate: job.scheduled_date || null, toDate: m.scheduled_date };
      }));
    };
    const cached = previewCache.current.get(key);
    if (cached) { show(cached.moves, cached.parts); return; }
    const timer = setTimeout(async () => {
      try {
        const { data } = previewJob.backlog
          // Not on the board yet: its days laid from here, and what they push.
          ? await api.post('/api/v1/schedule/make-room/preview-new', {
              machine_id: previewMid, start: previewDate, days: previewJob.days_on?.[previewMid] || 1,
            })
          : await api.post('/api/v1/schedule/make-room/preview', {
              plan_id: previewJob.plan_id || null, job_id: previewJob.id,
              job_original_date: previewJob.scheduled_date, target_date: previewDate,
              target_machine_id: previewMid, anchor,
            });
        const parts = {};
        for (const sh of data.shifted || []) for (const id of sh.ids || []) parts[id] = sh.part;
        previewCache.current.set(key, { moves: data.moves || [], parts });
        show(data.moves || [], parts);
      } catch (_) {
        if (seq === previewSeq.current) setCascadePreview([]);
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [previewJob, previewMid, previewDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 1: document-level dragover → track cursor X during HTML5 drag
  useEffect(() => {
    if (!dragging) return;
    let currentSide = null;
    const handle = (e) => {
      const newSide = e.clientX < 72 ? 'prev' : e.clientX > window.innerWidth - 72 ? 'next' : null;
      if (newSide === currentSide) return;
      clearTimeout(edgeNavTimer.current); edgeNavTimer.current = null;
      clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null;
      setEdgeFilling(null); setDragOverEdge(null);
      currentSide = newSide;
      if (!newSide) return;
      const dir = newSide === 'prev' ? -1 : 1;
      setDragOverEdge(newSide);

      // Hand the job to the next week and enter placing mode.
      const startNav = () => {
        setEdgeFillDuration(500); setEdgeFilling(newSide);
        const captured = draggingRef.current;
        if (!captured) return;
        edgeNavTimer.current = setTimeout(() => {
          edgeNavTimer.current = null; currentSide = null;
          setEdgeFilling(null); setDragOverEdge(null);
          setWeekSlideAnim(dir > 0 ? 'from-right' : 'from-left');
          setTimeout(() => setWeekSlideAnim(null), 500);
          setDragging(null); setDragOver(null);
          pendingScrollEdge.current = dir;
          onEdgeDropRef.current(captured.job, dir);
        }, 500);
      };

      if (weekSpanRef.current > 1) {
        // Multi-week: scroll the grid first. Once there is no grid left to
        // scroll, escalate into the same countdown the single-week view uses,
        // so the gesture carries on instead of dead-ending at the last column.
        scrollIntervalRef.current = setInterval(() => {
          const sc = scrollContainerRef.current;
          if (!sc) return;
          if (!atScrollEnd(sc, dir)) { sc.scrollBy({ left: dir * 120 }); return; }
          clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null;
          startNav();
        }, 80);
      } else {
        startNav();
      }
    };
    document.addEventListener('dragover', handle);
    return () => {
      document.removeEventListener('dragover', handle);
      clearTimeout(edgeNavTimer.current);
      clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null;
      setEdgeFilling(null); setDragOverEdge(null);
    };
  }, [dragging]); // eslint-disable-line react-hooks/exhaustive-deps

  // Phase 2: document-level mousemove + dragover → track cursor X during pending placement
  // dragover fires while user is still physically holding the mouse (after Phase 1 cleared dragging state)
  // mousemove fires after the user has released the mouse button
  useEffect(() => {
    if (!pendingPlacement || dragging) return;
    let currentSide = null;
    const handle = (e) => {
      const newSide = e.clientX < 72 ? 'prev' : e.clientX > window.innerWidth - 72 ? 'next' : null;
      if (newSide === currentSide) return;
      clearTimeout(edgeNavTimer.current); edgeNavTimer.current = null;
      clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null;
      setEdgeFilling(null); setDragOverEdge(null);
      currentSide = newSide;
      if (!newSide) return;
      const dir = newSide === 'prev' ? -1 : 1;
      setDragOverEdge(newSide);

      // Slower than phase 1 — the job is already parked, so there is no rush and
      // a long hold often just means the director has not decided yet.
      const startNav = () => {
        setEdgeFillDuration(1000); setEdgeFilling(newSide);
        edgeNavTimer.current = setTimeout(() => {
          edgeNavTimer.current = null; currentSide = null;
          setEdgeFilling(null); setDragOverEdge(null);
          setWeekSlideAnim(dir > 0 ? 'from-right' : 'from-left');
          setTimeout(() => setWeekSlideAnim(null), 500);
          pendingScrollEdge.current = dir;
          onNavigateWeekRef.current(dir);
        }, 1000);
      };

      if (weekSpanRef.current > 1) {
        scrollIntervalRef.current = setInterval(() => {
          const sc = scrollContainerRef.current;
          if (!sc) return;
          if (!atScrollEnd(sc, dir)) { sc.scrollBy({ left: dir * 120 }); return; }
          clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null;
          startNav();
        }, 80);
      } else {
        startNav();
      }
    };
    document.addEventListener('mousemove', handle);
    document.addEventListener('dragover', handle);
    return () => {
      document.removeEventListener('mousemove', handle);
      document.removeEventListener('dragover', handle);
      clearTimeout(edgeNavTimer.current);
      clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null;
      setEdgeFilling(null); setDragOverEdge(null);
    };
  }, [pendingPlacement, dragging]); // eslint-disable-line react-hooks/exhaustive-deps

  // Land against the edge we were pushing, so the next step is one hold away
  // rather than another full traverse of the span. Declared after the centring
  // effect above so it wins, and applied synchronously — the column count does
  // not change on a navigation, so the layout is already final here and a
  // deferred write would show a frame at the wrong position.
  useEffect(() => {
    if (pendingScrollEdge.current == null) return;
    const dir = pendingScrollEdge.current;
    pendingScrollEdge.current = null;
    // Re-read the ref on every attempt. React swaps this node as the drag
    // overlay above it mounts/unmounts, and a write to the old detached node is
    // silently lost while its replacement starts at scrollLeft 0. Assign an
    // over-large value and let the browser clamp it, rather than computing the
    // maximum against a layout that may not be final yet. Retry for a few
    // frames until a write actually sticks.
    let frames = 0;
    const pin = () => {
      const sc = scrollContainerRef.current;
      const live = sc && sc.clientWidth > 0;
      if (live) sc.scrollLeft = dir < 0 ? 0 : sc.scrollWidth;
      const stuck = live && (dir < 0 ? sc.scrollLeft === 0 : sc.scrollLeft > 0);
      if (stuck || ++frames >= 6) {
        // Release the centre-on-today suppression only once we are done, so it
        // cannot re-centre midway through the retry window.
        pendingScrollEdge.current = null;
        return;
      }
      requestAnimationFrame(pin);
    };
    pin();
  }, [viewDate]);

  // ESC cancels both active drag and pending placement
  useEffect(() => {
    if (!dragging && !pendingPlacement) return;
    const handle = (e) => {
      if (e.key !== 'Escape') return;
      clearTimeout(edgeNavTimer.current); edgeNavTimer.current = null;
      setEdgeFilling(null); setDragOverEdge(null);
      setDragging(null); setDragOver(null);
      onClearPendingRef.current();
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [dragging, pendingPlacement]); // eslint-disable-line react-hooks/exhaustive-deps

  // ESC deselects keyboard-selected span (when not in drag/pending mode)
  useEffect(() => {
    if (!selectedSpan || dragging || pendingPlacement) return;
    const handle = (e) => { if (e.key === 'Escape') setSelectedSpan(null); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [selectedSpan, dragging, pendingPlacement]);

  // ← → nudge selected span by ±1 working day
  useEffect(() => {
    if (!selectedSpan || !selectedSpan.canNudge || dragging || pendingPlacement) return;
    const handle = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      const job = scheduled.find(j => j.id === selectedSpan.jobId);
      if (!job) return;
      const newDate = e.key === 'ArrowRight'
        ? shiftByWorkingDays(job.scheduled_date, 1, holidays)
        : shiftBackByWorkingDays(job.scheduled_date, 1, holidays);
      if (newDate === job.scheduled_date) return;
      onMove(job, newDate, job.machine_id, true);
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [selectedSpan, scheduled, dragging, pendingPlacement, holidays, onMove]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!machines.length) return <EmptyState msg="No machines found. Add machines in Admin → Machines first." />;

  return (
    <>
    {/* Pending cross-week placement banner */}
    {pendingPlacement && (
      <div style={{ background: 'rgba(20,184,166,0.08)', border: '1px solid rgba(20,184,166,0.3)', borderRadius: '8px', padding: '10px 16px', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontFamily: 'system-ui' }}>
        <i className="fa-solid fa-hand-pointer" style={{ color: '#14b8a6', fontSize: '14px', flexShrink: 0 }} />
        <span style={{ flex: 1 }}>
          <span style={{ color: '#9ca3af' }}>Placing </span>
          <strong style={{ color: '#f0f2f5' }}>{pendingPlacement.parts?.name || 'Job'}</strong>
          {pendingPlacement.backlog
            ? <span style={{ color: '#9ca3af' }}> for {pendingPlacement.backlog.order.number} ({pendingPlacement.backlog.missing.toLocaleString('en-GB')} pcs) — drop it or click a day on a highlighted machine: the job form opens filled in, and says what it would push</span>
            : <span style={{ color: '#9ca3af' }}> — click any highlighted slot to place, or hover the edge to navigate further</span>}
        </span>
        <button onClick={onClearPendingPlacement} style={{ background: 'none', border: '1px solid #374151', borderRadius: '6px', color: '#6b7280', fontSize: '11px', fontWeight: '600', padding: '3px 10px', cursor: 'pointer', fontFamily: 'system-ui' }}>
          Cancel
        </button>
      </div>
    )}
    {/* The bar under the clicked run (21 Oct 2026). It replaced the keyboard
        hint that used to float at the bottom; the menu's Keyboard shortcuts
        explains the arrows. */}
    {selectedSpan && !dragging && !pendingPlacement && (() => {
      const rep = scheduled.find(j => j.id === selectedSpan.jobId);
      if (!rep) return null;
      const close = () => setSelectedSpan(null);
      const bar = runBarProps({ rep, scheduled, planTotalsMap, isDirector,
        onComplete, onUncomplete, onEdit, onDelete, onMoveFrom, close });
      return <RunBar anchor={`[data-run-card="${rep.id}"]`} {...bar} onClose={close} />;
    })()}
    {/* Fixed prev/next week edge zones:
        Phase 1 (dragging)         — drag events, 500 ms → end drag + enter pending placement + navigate
        Phase 2 (pendingPlacement) — mouse events, 2 000 ms → navigate further, keep pending placement */}
    <style>{`
      @keyframes edgeFill { from { transform: scaleX(0); } to { transform: scaleX(1); } }
      ${RUN_FADE_RULE}
      @keyframes slideInFromRight { from { transform: translateX(48px); opacity: 0.5; } to { transform: translateX(0); opacity: 1; } }
      @keyframes slideInFromLeft  { from { transform: translateX(-48px); opacity: 0.5; } to { transform: translateX(0); opacity: 1; } }
    `}</style>
    {(dragging || pendingPlacement) && (
      <>
        {['prev', 'next'].map(side => {
          const active = dragOverEdge === side;
          const filling = edgeFilling === side;
          const dir = side === 'prev' ? -1 : 1;
          return (
            <div
              key={side}
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
              onDrop={e => {
                e.preventDefault();
                clearTimeout(edgeNavTimer.current); edgeNavTimer.current = null;
                setEdgeFilling(null); setDragOverEdge(null);
                if (draggingRef.current) {
                  // User released before the 500ms timer — navigate + enter pending placement
                  const job = draggingRef.current.job;
                  setDragging(null); setDragOver(null);
                  setWeekSlideAnim(dir > 0 ? 'from-right' : 'from-left');
                  setTimeout(() => setWeekSlideAnim(null), 500);
                  onEdgeDropRef.current(job, dir);
                } else if (pendingPlacement) {
                  // Phase 1 already fired; user released at the edge — auto-place at first free slot on same machine
                  const machineId = pendingPlacement.machine_id;
                  const freeDay = days.find(d => {
                    const ds = fmt(d);
                    return !isWeekendStr(ds) && !holidays[ds] &&
                      !scheduled.some(s => s.machine_id === machineId && s.scheduled_date === ds &&
                        s.id !== pendingPlacement.id && s.status !== 'cancelled');
                  });
                  if (freeDay) {
                    onPendingPlace(pendingPlacement, fmt(freeDay), machineId);
                  }
                  // If no free slot: keep in pending placement so user can pick one
                }
              }}
              style={{
                position: 'fixed', top: 0, bottom: 0, [side === 'prev' ? 'left' : 'right']: 0, width: '72px',
                zIndex: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px',
                background: active ? 'rgba(20,184,166,0.18)' : 'rgba(20,30,45,0.88)',
                [side === 'prev' ? 'borderRight' : 'borderLeft']: `3px solid rgba(20,184,166,${active ? '0.7' : '0.3'})`,
                transition: 'background 0.15s, border-color 0.15s',
                backdropFilter: 'blur(6px)',
                overflow: 'hidden',
                pointerEvents: 'auto',
              }}
            >
              <i className={`fa-solid fa-chevron-${side === 'prev' ? 'left' : 'right'}`} style={{ fontSize: active ? '22px' : '16px', color: '#14b8a6', opacity: active ? 1 : 0.55, transition: 'all 0.15s' }} />
              <span style={{ fontSize: '9px', fontWeight: '700', color: '#14b8a6', opacity: active ? 1 : 0.55, textTransform: 'uppercase', letterSpacing: '0.05em', transition: 'all 0.15s' }}>
                {side === 'prev' ? 'Prev' : 'Next'}<br/>week
              </span>
              {filling && (
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '3px', background: '#14b8a6', transformOrigin: 'left', animation: `edgeFill ${edgeFillDuration}ms linear forwards` }} />
              )}
            </div>
          );
        })}
      </>
    )}
    {/* Delivery weeks badge is now rendered in the parent control bar */}
    <div ref={scrollContainerRef} style={{ overflowX: 'auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: `${machineCol}px repeat(${totalCols}, minmax(110px, 1fr))`, gridTemplateRows: `auto ${machines.map(m => compactMachines.has(m.id) ? '28px' : 'minmax(72px, auto)').join(' ')}`, gap: '3px', minWidth: `${machineCol + 110 * totalCols}px`, animation: weekSlideAnim ? `slideIn${weekSlideAnim === 'from-right' ? 'FromRight' : 'FromLeft'} 0.5s ease-out` : undefined }}>

        {/* Header row */}
        <div style={stickCell(4, false)}>
        <div style={{ ...st.hdrCell, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', background: '#111827' }}>
          <span style={{ fontSize: '22px', fontWeight: '800', color: isDeliveryWeek ? '#fbbf24' : '#c8cbd2', letterSpacing: '-0.02em', lineHeight: 1 }}>{scrollWeek ?? currWeek}</span>
          <span style={{ fontSize: '8px', fontWeight: '700', color: isDeliveryWeek ? '#fbbf24' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.1em' }}>wk</span>
        </div>
        </div>
        {days.map((day, i) => {
          const ds        = fmt(day);
          const isToday   = ds === TODAY;
          const holiday   = holidays[ds];
          const isWknd    = isWeekendStr(ds);
          const nonWork   = holiday || isWknd;
          const isWkStart = i > 0 && i % 7 === 0;
          const { week: dayWk } = isWkStart ? isoWeekOf(day) : { week: null };

          const dispatchEntry = dispatchDates[ds];
          const isDispatch = !!dispatchEntry;
          const staffDay = staffingDays && staffingDays.get(ds);
          const isConfirmedDispatch = dispatchEntry?.type === 'confirmed';
          return (
            <div key={ds} style={{
              // +1 for the machine-label column on the left.
              gridColumn: `${colStartOf(i) + 1} / ${colEndOf(i) + 1}`,
              ...st.hdrCell, textAlign: 'center',
              backgroundImage: nonWork && !isToday ? crossHatch(!!holiday) : undefined,
              backgroundColor: isToday
                ? 'rgba(20,184,166,0.12)'
                : nonWork ? '#131920'
                : isDispatch ? 'rgba(251,191,36,0.08)' : '#1a2030',
              border: isToday
                ? '1px solid rgba(20,184,166,0.35)'
                : isDispatch ? `1px ${isConfirmedDispatch ? 'solid' : 'dashed'} rgba(251,191,36,0.35)`
                : '1px solid transparent',
              borderRadius: '6px',
              borderLeft: isWkStart ? '2px solid rgba(20,184,166,0.3)' : undefined,
            }}>
              {isWkStart && (
                <div style={{ fontSize: '8px', fontWeight: '800', color: '#14b8a6', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '1px', opacity: 0.8 }}>Wk {dayWk}</div>
              )}
              <div style={{ fontSize: '10px', fontWeight: '700', color: isToday ? '#14b8a6' : (holiday || isWknd) ? '#4b5563' : isDispatch ? '#fbbf24' : '#6b7280', textTransform: 'uppercase' }}>
                {DAY_LABELS[i % 7]}
              </div>
              <div style={{ fontSize: '12px', color: isToday ? '#14b8a6' : (holiday || isWknd) ? '#4b5563' : isDispatch ? '#fbbf24' : '#9ca3af', marginTop: '1px' }}>
                {day.getDate()} {day.toLocaleDateString('en-GB', { month: 'short' })}
              </div>
              {holiday && (
                <div style={{ fontSize: '9px', color: '#fbbf24', marginTop: '2px', lineHeight: 1.2, opacity: 0.85 }}>{holiday}</div>
              )}
              {/* More work than people on a shift that day: what the work needs
                  against what the people give, readable without dragging
                  anything. Shown only when there is something to say. */}
              {staffDay && staffDay.covered && (
                <div title={`${staffDay.says} Covered by ${staffDay.covered.by || 'you'}: ${staffDay.covered.says}`}
                  style={{ marginTop: '3px', fontSize: '9px', fontWeight: '700', lineHeight: 1.4, display: 'inline-block', borderRadius: '4px', padding: '1px 5px',
                    color: '#9ca3af', background: 'rgba(156,163,175,0.12)', cursor: 'help' }}>
                  <i className="fa-solid fa-shield-halved" style={{ marginRight: '3px' }} />
                  covered
                </div>
              )}
              {staffDay && !staffDay.covered && (
                <div title={staffDay.says}
                  style={{ marginTop: '3px', fontSize: '9px', fontWeight: '700', lineHeight: 1.4, display: 'inline-block', borderRadius: '4px', padding: '1px 5px',
                    color: staffDay.verdict === 'short' ? '#fca5a5' : staffDay.verdict === 'unknown' ? '#9ca3af' : '#fcd34d',
                    background: staffDay.verdict === 'short' ? 'rgba(239,68,68,0.14)' : staffDay.verdict === 'unknown' ? 'rgba(156,163,175,0.12)' : 'rgba(245,158,11,0.13)',
                    cursor: 'help' }}>
                  <i className="fa-solid fa-people-group" style={{ marginRight: '3px' }} />
                  {staffDay.verdict === 'unknown' ? '?' : `${staffDay.need}/${staffDay.have} h`}
                </div>
              )}
              {dispatchEntry && (
                <div
                  onClick={e => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    setDispatchPopover({ date: ds, orders: dispatchEntry.orders || [], x: rect.left, y: rect.bottom + 6 });
                  }}
                  style={{ marginTop: '3px', fontSize: '9px', fontWeight: '700', color: '#f59e0b',
                    background: 'rgba(251,191,36,0.12)', borderRadius: '4px', padding: '1px 5px',
                    border: `1px ${isConfirmedDispatch ? 'solid' : 'dashed'} rgba(251,191,36,0.5)`,
                    display: 'inline-flex', alignItems: 'center', gap: '2px', lineHeight: 1.4,
                    cursor: 'pointer' }}>
                  <i className="fa-solid fa-truck" />
                </div>
              )}
            </div>
          );
        })}

        {/* Machine rows */}
        {machines.map((machine, mi) => {
          const isCompact = compactMachines.has(machine.id);
          const isLastRow = mi === machines.length - 1;
          const workingDays = days.filter(d => { const ds = fmt(d); return !isWeekendStr(ds) && !holidays[ds]; });
          const occupiedDays = workingDays.filter(d => jobsFor(machine.id, fmt(d)).some(j => j.status !== 'cancelled')).length;
          const utilPct = workingDays.length > 0 ? occupiedDays / workingDays.length : 0;
          const utilColor = utilPct === 0 ? '#2a3040' : utilPct <= 0.6 ? '#14b8a6' : utilPct <= 0.85 ? '#f59e0b' : '#ef4444';
          const showInelig = (dragging && dragHasEligibility && !draggedEligible.includes(machine.id)) ||
            (pendingPlacement && pendingHasEligibility && !pendingEligible.includes(machine.id));
          const chevronBtnStyle = { background: 'none', border: 'none', color: '#4b5563', cursor: 'pointer', fontSize: '10px', padding: '2px 4px', borderRadius: '4px', lineHeight: 1, transition: 'color 0.15s', flexShrink: 0 };
          // Bulk reschedule, down days, compact: beside the name, or on a phone
          // on their own row under it (12 Oct 2026).
          const machineButtons = (
            <>
                {isDirector && !dragging && !pendingPlacement && (
                  <button
                    onClick={() => onBulkReschedule(machine)}
                    title={`Bulk reschedule ${machineName(machine)}`}
                    style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#4b5563', cursor: 'pointer', fontSize: '10px', padding: '2px 4px', borderRadius: '4px', lineHeight: 1, transition: 'color 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#f59e0b'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = '#4b5563'; }}
                  >
                    <i className="fa-solid fa-wrench" />
                  </button>
                )}
                {!dragging && !pendingPlacement && onMachineDown && (
                  <button
                    onClick={() => onMachineDown(machine)}
                    title={isDirector ? `Down days for ${machineName(machine)}: a service, a fitter's visit` : `Report ${machineName(machine)} down`}
                    style={{ background: 'none', border: 'none', color: '#4b5563', cursor: 'pointer', fontSize: '10px', padding: '2px 4px', borderRadius: '4px', lineHeight: 1, transition: 'color 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = '#4b5563'; }}
                  >
                    <i className="fa-solid fa-power-off" />
                  </button>
                )}
                {!dragging && !pendingPlacement && (
                  <button onClick={() => toggleCompact(machine.id)} title="Compact row" style={{ ...chevronBtnStyle, marginLeft: isDirector ? '0' : 'auto' }} onMouseEnter={e => { e.currentTarget.style.color = '#9ca3af'; }} onMouseLeave={e => { e.currentTarget.style.color = '#4b5563'; }}>
                    <i className="fa-solid fa-chevron-up" style={{ fontSize: '9px' }} />
                  </button>
                )}
            </>
          );
          return (
          <React.Fragment key={machine.id}>
            {isCompact ? (
              <div style={stickCell(3, true, !isLastRow)}>
              <div style={{ ...st.machineLabel, flex: 1, minWidth: 0, flexDirection: 'row', padding: '0 8px', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '10px', fontWeight: '700', color: '#9ca3af', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>{machineName(machine)}</span>
                <button onClick={() => toggleCompact(machine.id)} title="Expand row" style={chevronBtnStyle} onMouseEnter={e => { e.currentTarget.style.color = '#9ca3af'; }} onMouseLeave={e => { e.currentTarget.style.color = '#4b5563'; }}>
                  <i className="fa-solid fa-chevron-down" style={{ fontSize: '9px' }} />
                </button>
              </div>
              </div>
            ) : (
            <div style={stickCell(3, true, !isLastRow)}>
            <div style={{ ...st.machineLabel, flex: 1, minWidth: 0, ...(isPhone ? { padding: '6px 7px' } : {}),
              background: showInelig ? 'rgba(239,68,68,0.04)' : '#1e2329',
              borderRight: showInelig ? '2px solid rgba(239,68,68,0.25)' : undefined,
            }}>
              <div style={{ fontSize: isPhone ? '11px' : '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px', lineHeight: 1.25 }}>
                {machineName(machine)}
                <MachineStatusDot machine={machine} />
                {(dragging || pendingPlacement) && (() => {
                  const eligible = dragging ? draggedEligible : pendingEligible;
                  const hasElig  = dragging ? dragHasEligibility : pendingHasEligibility;
                  return hasElig
                    ? eligible.includes(machine.id)
                      ? <span title="Compatible machine" style={{ fontSize: '9px', color: '#14b8a6', marginLeft: '2px' }}>✓</span>
                      : <span title="Part cannot run on this machine" style={{ fontSize: '11px', marginLeft: '2px' }}>🚫</span>
                    : <span title="No machine restrictions set for this part" style={{ fontSize: '9px', color: '#f59e0b', marginLeft: '2px' }}>⚠</span>;
                })()}
                {!isPhone && machineButtons}
              </div>
              {isPhone && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginTop: '3px' }}>{machineButtons}</div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px' }}>
                <div style={{ flex: 1, height: '3px', background: '#2a3040', borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${utilPct * 100}%`, background: utilColor, borderRadius: '2px', transition: 'width 0.3s' }} />
                </div>
                <span style={{ fontSize: '9px', color: utilColor, fontWeight: '700', whiteSpace: 'nowrap', minWidth: '28px', textAlign: 'right' }}>
                  {occupiedDays}/{workingDays.length}d
                </span>
              </div>
              {machine.tonnes && !isPhone && <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>{machine.tonnes}T</div>}
              {/* Cascade preview: count of displaced jobs pushed beyond the visible week */}
              {(dragging || pendingPlacement) && (() => {
                const n = cascadePreview.filter(d => d.job.machine_id === machine.id && days.findIndex(day => fmt(day) === d.toDate) < 0).length;
                return n > 0 ? (
                  <div style={{ fontSize: '9px', color: '#f59e0b', fontWeight: '700', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '3px' }}>
                    <i className="fa-solid fa-arrow-right" style={{ fontSize: '8px' }} />
                    {n} pushed out of view
                  </div>
                ) : null;
              })()}
            </div>
            </div>
            )} {/* end isCompact label ternary */}

            {/* Compact mode: thin coloured bar per day/span */}
            {isCompact && (() => {
              const planMap = {};
              days.forEach((day, dayIdx) => {
                const ds = fmt(day);
                jobsFor(machine.id, ds).forEach(job => {
                  if (!showCompleted && job.status === 'completed') return;
                  const key = job.plan_id || `solo-${job.id}-${ds}`;
                  if (!planMap[key]) planMap[key] = [];
                  planMap[key].push({ job, ds, dayIdx });
                });
              });
              const compactSpans = Object.values(planMap).map(entries => {
                entries.sort((a, b) => a.dayIdx - b.dayIdx);
                const startIdx = entries[0].dayIdx;
                const endIdx   = entries[entries.length - 1].dayIdx;
                const jobs = entries.map(e => e.job);
                const rep  = jobs[0];
                const prevJob = prevJobOnMachine(machine.id, entries[0].ds);
                const nextJob = nextJobOnMachine(machine.id, entries[entries.length - 1].ds);
                const cameFromBefore = prevJob?.plan_id && prevJob.plan_id === rep.plan_id;
                const continuesAfter = nextJob?.plan_id && nextJob.plan_id === rep.plan_id;
                const partHex = rep.parts?.colour ? (colours.find(c => c.name === rep.parts.colour)?.hex || null) : null;
                const { bg, border } = partColourBg(partHex);
                const ol = rep.order_line?.order;
                const todayStr2 = fmt(new Date());
                let urgencyOverlay = null;
                let isOverdue = false;
                if (ol?.delivery_week) {
                  const fri = weekToFriday(ol.delivery_week, ol.delivery_year);
                  if (fri < todayStr2) {
                    isOverdue = true;
                  } else {
                    const [fy, fm, fd] = fri.split('-').map(Number);
                    const hardDL    = fmt(addDays(new Date(fy, fm - 1, fd), -urgencyDays.red));
                    const primaryDL = fmt(addDays(new Date(fy, fm - 1, fd), -urgencyDays.amber));
                    const lastDay   = jobs[jobs.length - 1].scheduled_date;
                    if (lastDay > hardDL) urgencyOverlay = 'rgba(239,68,68,0.18)';
                    else if (lastDay > primaryDL) urgencyOverlay = 'rgba(245,158,11,0.14)';
                  }
                }
                const cardBg = isOverdue
                  ? `repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(239,68,68,0.25) 5px, rgba(239,68,68,0.25) 7px),${bg}`
                  : urgencyOverlay ? `linear-gradient(${urgencyOverlay},${urgencyOverlay}),${bg}` : bg;
                const cardBorder = isOverdue ? '#dc2626' : urgencyOverlay ? (urgencyOverlay.startsWith('rgba(239') ? '#ef4444' : '#f59e0b') : border;
                const tl = cameFromBefore ? '0' : '3px';
                const tr = continuesAfter ? '0' : '3px';
                const totalQty = jobs.reduce((acc, j) => acc + (j.target_qty || 0), 0);
                return { startIdx, endIdx, rep, jobs, totalQty, cardBg, cardBorder, urgencyOverlay, isOverdue, tl, tr };
              });
              return (
                <div style={{ gridColumn: '2 / -1', display: 'grid', gridTemplateColumns: 'subgrid', gridTemplateRows: '1fr', height: '100%', gap: '3px' }}>
                  {days.map((day, dayIdx) => {
                    const ds = fmt(day);
                    const nonWork = isWeekendStr(ds) || !!holidays[ds];
                    return (
                      <div key={ds} style={{ gridColumn: colSpanOf(dayIdx, dayIdx), gridRow: 1, background: nonWork ? '#131920' : (dispatchDates[ds] ? 'rgba(251,191,36,0.05)' : '#1e2329'), border: '1px solid #2a3040', borderRadius: '3px', backgroundImage: nonWork ? crossHatch(!!holidays[ds]) : undefined }} />
                    );
                  })}
                  {compactSpans.map(s => (
                    <div
                      key={s.rep.plan_id || `solo-${s.rep.id}`}
                      onClick={isDirector ? e => { e.stopPropagation(); onEdit(s.rep); } : undefined}
                      style={{ gridColumn: colSpanOf(s.startIdx, s.endIdx), gridRow: 1, background: s.cardBg, border: `${s.isOverdue ? '2px' : s.urgencyOverlay ? '2px' : '1px'} solid ${s.cardBorder}`, borderRadius: `${s.tl} ${s.tr} ${s.tr} ${s.tl}`, cursor: isDirector ? 'pointer' : 'default', zIndex: 1, display: 'flex', alignItems: 'center', gap: '5px', padding: '0 6px', overflow: 'hidden' }}
                    >
                      <span style={{ fontSize: '10px', fontWeight: '700', color: '#f0f2f5', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0, letterSpacing: '-0.01em' }}>
                        {s.rep.parts?.name || '—'}
                      </span>
                      {s.rep.order_line?.order?.delivery_week && (
                        <span style={{ fontSize: '8px', fontWeight: '700', color: 'rgba(240,242,245,0.75)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                          KW{s.rep.order_line.order.delivery_week}
                        </span>
                      )}
                      {s.jobs.length > 1 && s.totalQty > 0 && (
                        <span style={{ fontSize: '8px', color: 'rgba(240,242,245,0.5)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                          {s.totalQty.toLocaleString()} pcs
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Unified wrapper: background cells + plan spans in the same CSS grid */}
            {!isCompact && (() => {
              // Build plan spans: group jobs by plan_id across all days in this week
              const planMap = {};
              days.forEach((day, dayIdx) => {
                const ds = fmt(day);
                jobsFor(machine.id, ds).forEach(job => {
                  if (!showCompleted && job.status === 'completed') return;
                  const key = job.plan_id || `solo-${job.id}-${ds}`;
                  if (!planMap[key]) planMap[key] = [];
                  planMap[key].push({ job, ds, dayIdx });
                });
              });

              // A run is drawn as one card only if nothing else sits on the
              // machine inside it. One laid around another run (9 Oct 2026; now
              // refused when it is planned) is drawn as its pieces, so no card is
              // ever drawn over another.
              const pieces = [];
              Object.entries(planMap).forEach(([key, entries]) => {
                entries.sort((a, b) => a.dayIdx - b.dayIdx);
                const all = [[entries[0]]];
                for (let i = 1; i < entries.length; i++) {
                  let between = false;
                  for (let d = entries[i - 1].dayIdx + 1; d < entries[i].dayIdx && !between; d++) {
                    const ds = fmt(days[d]);
                    between = jobsFor(machine.id, ds).some(j => (j.plan_id || `solo-${j.id}-${ds}`) !== key);
                  }
                  if (between) all.push([]);
                  all[all.length - 1].push(entries[i]);
                }
                all.forEach((p, n) => pieces.push({ entries: p, piece: n, count: all.length }));
              });

              const spans = pieces.map(({ entries, piece, count }) => {
                const startIdx = entries[0].dayIdx;
                const endIdx   = entries[entries.length - 1].dayIdx;
                const jobs     = entries.map(e => e.job);
                // Which shift it starts on and which it ends on. A run can begin
                // in the afternoon and finish in the morning three days later,
                // and both ends have to be drawn where they actually are.
                const startSlot = Math.min(...entries.filter(e => e.dayIdx === startIdx).map(e => e.job.shift_slot || 1));
                const endSlot   = Math.max(...entries.filter(e => e.dayIdx === endIdx).map(e => e.job.shift_slot || 1));
                const totalQty = jobs.reduce((s, j) => s + (j.target_qty || 0), 0);
                const prevJob  = prevJobOnMachine(machine.id, entries[0].ds);
                const nextJob  = nextJobOnMachine(machine.id, entries[entries.length - 1].ds);
                const cameFromBefore  = prevJob && prevJob.plan_id && prevJob.plan_id === jobs[0].plan_id;
                const continuesAfter  = nextJob && nextJob.plan_id && nextJob.plan_id === jobs[0].plan_id;
                return {
                  key: (entries[0].job.plan_id || `solo-${entries[0].job.id}`) + (piece ? `#${piece}` : ''),
                  jobs, startIdx, endIdx, startSlot, endSlot, totalQty, cameFromBefore, continuesAfter, prevJob,
                  inPieces: count > 1, piece, pieces: count,
                };
              });

              // No lanes here. Stacking two runs vertically was the wrong answer
              // -- §11 rejects it in one line: "stacking implies parallel, and
              // shifts are sequential." Two runs sharing a machine-day sit side by
              // side in the day's sub-columns, and time keeps flowing one way
              // across the whole board.

              const coveredBySpan = new Set(
                spans.flatMap(s =>
                  s.jobs.map(j => days.findIndex(d => fmt(d) === j.scheduled_date)).filter(i => i >= 0)
                )
              );
              // Machine eligibility for pending cross-week placement — must be in machine-row
              // scope so both days.map and spans.map can access it.
              const pendingMachineOk = !pendingHasEligibility || (pendingEligible || []).includes(machine.id);

              return (
                <div style={{
                  gridColumn: '2 / -1',
                  display: 'grid',
                  gridTemplateColumns: 'subgrid',
                  gridTemplateRows: '1fr',
                  height: '100%',
                }}>
                  {/* Layer 0: day background cells (drag-drop targets, non-work styling) */}
                  {days.map((day, dayIdx) => {
                    const ds          = fmt(day);
                    const isToday     = ds === TODAY;
                    const holiday     = holidays[ds];
                    const isWknd      = isWeekendStr(ds);
                    const nonWork     = holiday || isWknd;
                    const isDispatch  = !nonWork && !!dispatchDates[ds];
                    const machineIneligible = dragging && dragHasEligibility && !draggedEligible.includes(machine.id);
                    // Snap-to-gap: this cell is highlighted if it's part of the plan's landing footprint
                    const inSnap = dragOver?.machineId === machine.id && !machineIneligible
                      && snapFootprintSet.has(ds);
                    const isInFootprint = inSnap &&
                      (snapFootprint[0] !== dragging?.fromDate || machine.id !== dragging?.fromMachineId);
                    const isDropTarget = isInFootprint; // kept for backward-compat naming
                    const isOverrideCalendar = !isDropTarget && dragOver?.machineId === machine.id && overrideFootprint.calendar.has(ds);
                    const isOverrideWorking  = !isDropTarget && dragOver?.machineId === machine.id && overrideFootprint.working.has(ds);
                    const isEmpty = !coveredBySpan.has(dayIdx);
                    return (
                      <div
                        key={ds}
                        data-touch-machine={machine.id}
                        data-touch-date={ds}
                        // The reason belongs here rather than on the tint overlay.
                        // That overlay is pointer-events:none so it cannot swallow
                        // a drop -- which also means it never receives hover, so a
                        // title on it would never appear. The cell does receive it.
                        title={(dropHints && dropHints.get(`${machine.id}|${ds}`)?.says)
                          || (downFor(machine.id, ds) ? `${machineName(machine)} is down: ${downFor(machine.id, ds).reason}${downFor(machine.id, ds).note ? ` (${downFor(machine.id, ds).note})` : ''}` : undefined)}
                        onDragOver={e => {
                          e.preventDefault();
                          if (dragging) {
                            const alt = e.altKey;
                            e.dataTransfer.dropEffect = alt ? 'copy' : 'move';
                            if (alt !== altDragRef.current) { altDragRef.current = alt; setIsDuplicating(alt); }
                            setDragOver({ machineId: machine.id, dateStr: ds });
                          } else if (pendingPlacement && !nonWork) {
                            e.dataTransfer.dropEffect = pendingMachineOk ? 'move' : 'none';
                            if (pendingMachineOk) setDragOver({ machineId: machine.id, dateStr: ds });
                          }
                        }}
                        onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null); }}
                        onMouseEnter={() => { if (pendingPlacement && !nonWork && pendingMachineOk) setDragOver({ machineId: machine.id, dateStr: ds }); }}
                        onMouseLeave={() => { if (pendingPlacement && dragOver?.machineId === machine.id && dragOver?.dateStr === ds) setDragOver(null); }}
                        onDrop={e => {
                          e.preventDefault();
                          setDragOver(null);
                          // Pending placement: user released on this cell while still in HTML5 drag (after Phase 1)
                          // A backlog line may land on a taken day too: the job form
                          // it opens says what saving would push (20 Oct 2026).
                          if (!dragging && pendingPlacement && !nonWork && (isEmpty || pendingPlacement.backlog) && pendingMachineOk) {
                            onPendingPlace(pendingPlacement, ds, machine.id);
                            return;
                          }
                          if (dragging && (snapFootprint[0] !== dragging.fromDate || machine.id !== dragging.fromMachineId)) {
                            if (machineIneligible) {
                              const partId = dragging.job.parts?.id;
                              toast.error(t => (
                                <span>
                                  <strong>{dragging.job.parts?.name || 'This part'}</strong> cannot run on <strong>{machineName(machine)}</strong> — not in its eligible machines.{' '}
                                  {partId && <button onClick={() => { toast.dismiss(t.id); navigate(`/admin/parts/${partId}`); }} style={{ marginLeft: 6, padding: '2px 8px', fontSize: '0.8em', background: '#14b8a6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Edit part →</button>}
                                </span>
                              ), { duration: 6000 });
                              setDragging(null);
                              return;
                            }
                            const targetDs = snapFootprint[0] || ds;
                            if (altDragRef.current || e.altKey) {
                              onDuplicate(dragging.job, targetDs, machine.id);
                            } else if (onNonWorkingDrop && (isWeekendStr(ds) || !!holidays[ds])) {
                              // Use ds (the actual cell date), not the snapped targetDs
                              onNonWorkingDrop(dragging.job, ds, machine.id, getDraggedPlanLength(dragging.job), getNonWorkingBlockLen(ds));
                            } else {
                              onMove(dragging.job, targetDs, machine.id, true);
                            }
                          }
                          altDragRef.current = false; setIsDuplicating(false);
                          setDragging(null);
                        }}
                        onClick={e => {
                          if (selectedSpan && !pendingPlacement) { setSelectedSpan(null); }
                          if (pendingPlacement && !nonWork) {
                            if (!pendingMachineOk) {
                              toast.error(`${pendingPlacement.parts?.name || 'This part'} cannot run on ${machineName(machine)}`);
                              return;
                            }
                            if (isEmpty) {
                              onPendingPlace(pendingPlacement, ds, machine.id);
                            } else {
                              const conflictJob = scheduled.find(j =>
                                j.machine_id === machine.id &&
                                j.scheduled_date === ds &&
                                j.status !== 'cancelled' &&
                                j.id !== pendingPlacement.id
                              );
                              if (conflictJob) {
                                onInsertConflict({
                                  job: pendingPlacement,
                                  targetDate: ds,
                                  machineId: machine.id,
                                  conflictPartName: conflictJob.parts?.name || 'Existing job',
                                  conflictPlanId: conflictJob.plan_id || null,
                                  clickX: e.clientX,
                                  clickY: e.clientY,
                                });
                                onClearPendingPlacement();
                              }
                            }
                          } else if (isEmpty && isDirector && !nonWork && !pendingPlacement) {
                            onEdit({ scheduled_date: ds, machine_id: machine.id });
                          }
                        }}
                        style={{
                          gridColumn: colSpanOf(dayIdx, dayIdx),
                          gridRow: 1,
                          zIndex: 0,
                          backgroundImage: (isDropTarget || isOverrideCalendar || isOverrideWorking) ? undefined : (nonWork ? crossHatch(!!holiday) : undefined),
                          backgroundColor: isDropTarget
                            ? 'rgba(20,184,166,0.18)'
                            : isOverrideCalendar ? 'rgba(245,158,11,0.18)'
                            : isOverrideWorking  ? 'rgba(20,184,166,0.12)'
                            : (pendingPlacement && !nonWork && pendingMachineOk && isEmpty)
                              ? 'rgba(20,184,166,0.07)'
                              : machineIneligible && !nonWork ? 'rgba(239,68,68,0.05)'
                              : isToday ? 'rgba(20,184,166,0.04)'
                              : isDispatch ? 'rgba(251,191,36,0.05)'
                              : nonWork ? '#131920' : '#1e2329',
                          border: `1px solid ${isDropTarget ? '#14b8a6' : isOverrideCalendar ? 'rgba(245,158,11,0.7)' : isOverrideWorking ? 'rgba(20,184,166,0.5)' : (pendingPlacement && !nonWork && pendingMachineOk && isEmpty) ? 'rgba(20,184,166,0.4)' : machineIneligible && !nonWork ? 'rgba(239,68,68,0.14)' : isToday ? 'rgba(20,184,166,0.2)' : '#2a3040'}`,
                          boxShadow: isDropTarget ? 'inset 0 0 0 1px rgba(20,184,166,0.3)' : isOverrideCalendar ? 'inset 0 0 0 1px rgba(245,158,11,0.25)' : (pendingPlacement && !nonWork && pendingMachineOk && !isEmpty) ? 'inset 0 0 0 2px rgba(99,102,241,0.35)' : undefined,
                          borderRadius: '6px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          position: 'relative',
                          cursor: (pendingPlacement && !nonWork && pendingMachineOk) ? 'copy' : undefined,
                          transition: 'border-color 0.1s, background-color 0.1s',
                        }}
                      >
                        {/* What would happen if the card landed here. Only ever
                            visible while one is in the air, and drawn beneath
                            the shift-window stripe so a declared window still
                            reads over the top of it.

                            Amber keeps the meaning it already has on this board:
                            you may drop, but you will be asked something. Red is
                            the narrower claim that there is genuinely no room. */}
                        {(() => {
                          const hint = dropHints && dropHints.get(`${machine.id}|${ds}`);
                          if (!hint) return null;
                          const blocking = hint.severity === 'blocker';
                          return (
                            <div
                              
                              style={{
                                position: 'absolute', inset: 0, borderRadius: '6px',
                                pointerEvents: 'none', zIndex: 1,
                                background: blocking ? 'rgba(239,68,68,0.13)' : 'rgba(245,158,11,0.10)',
                                boxShadow: `inset 0 0 0 1px ${blocking ? 'rgba(239,68,68,0.45)' : 'rgba(245,158,11,0.35)'}`,
                              }}
                            />
                          );
                        })()}
                        {/* A declared shift window. Drawn as an overlay rather
                            than folded into the background, which already
                            carries drop state, today, dispatch and non-working.
                            One stripe per extra shift, echoing the stacked bars
                            the capacity check uses for the same idea. */}
                        {/* A down day (migration 064): this machine only. */}
                        {(() => {
                          const dn = downFor(machine.id, ds);
                          if (!dn) return null;
                          return (
                            <div style={{ position: 'absolute', inset: 0, borderRadius: '6px', pointerEvents: 'none', zIndex: 1,
                                          background: 'repeating-linear-gradient(135deg, rgba(239,68,68,0.10) 0 6px, rgba(239,68,68,0.02) 6px 12px)',
                                          boxShadow: 'inset 0 0 0 1px rgba(239,68,68,0.35)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: 3 }}>
                              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#f87171' }}>Down · {dn.reason}</span>
                            </div>
                          );
                        })()}
                        {(() => {
                          const sw = shiftWindowFor(machine.id, ds);
                          if (!sw) return null;
                          const extra = Math.max(1, sw.shifts - 1);
                          return (
                            <div title={`${sw.shifts} shifts${sw.reason ? ` — ${sw.reason}` : ''}`}
                              style={{ position: 'absolute', top: 2, left: 3, right: 3, display: 'flex',
                                       flexDirection: 'column', gap: 1, pointerEvents: 'none', zIndex: 1 }}>
                              {Array.from({ length: extra }, (_, k) => (
                                <div key={k} style={{ height: 3, borderRadius: 2,
                                       background: '#14b8a6', opacity: k === 0 ? 0.85 : 0.5 }} />
                              ))}
                            </div>
                          );
                        })()}
                        {isEmpty && isDirector && !nonWork && !pendingPlacement && (
                          <button onClick={e => { e.stopPropagation(); onEdit({ scheduled_date: ds, machine_id: machine.id }); }} style={st.addCell}>+</button>
                        )}
                        {!nonWork && pendingPlacement && pendingMachineOk && isEmpty && (
                          <span style={{ fontSize: '24px', color: '#14b8a6', pointerEvents: 'none', fontWeight: '300', lineHeight: '1' }}>+</span>
                        )}
                        {isDuplicating && isInFootprint && (
                          <span style={{ fontSize: '11px', fontWeight: '700', color: '#14b8a6', background: 'rgba(20,184,166,0.2)', borderRadius: '4px', padding: '1px 5px', pointerEvents: 'none', zIndex: 2 }}>⊕</span>
                        )}
                      </div>
                    );
                  })}

                  {/* Layer 1: plan spans */}
                  {spans.map(span => {
                    const spanRep = span.jobs[0];
                    const spanCanDrag = isDirector
                      && !span.cameFromBefore
                      && !span.jobs.some(j => j.status === 'cancelled' || j.status === 'completed' || (j.status === 'started' && j.job?.status === 'running'));
                    // Continuation spans (cameFromBefore) can't be dragged but CAN receive drops —
                    // this fixes cross-week drops in 2w/3w/4w views where the target span may
                    // start before the visible window.
                    const isDroppable = isDirector
                      && !span.jobs.some(j => j.status === 'started' || j.status === 'completed' || j.status === 'cancelled');
                    const isThisSpanDragging = dragging?.job?.id === spanRep?.id;
                    const isSwapTarget = !isThisSpanDragging &&
                      dragging != null &&
                      dragOver?.machineId === machine.id &&
                      span.jobs.some(j => j.scheduled_date === dragOver?.dateStr) &&
                      (!dragHasEligibility || draggedEligible.includes(machine.id));

                    // Resize: compute effective end/start columns during live drag
                    const resizeDelta = resizeDeltaMap[span.key] || 0;
                    const effectiveEndIdx = resizeDelta !== 0
                      ? Math.max(span.startIdx, Math.min(7 * weekSpan - 1, span.endIdx + resizeDelta))
                      : span.endIdx;
                    const resizeStartDelta = resizeStartDeltaMap[span.key] || 0;
                    const effectiveStartIdx = resizeStartDelta !== 0
                      ? Math.max(0, Math.min(span.endIdx, span.startIdx + resizeStartDelta))
                      : span.startIdx;

                    // Show resize handle only for fully-scheduled plans with a real plan_id
                    const canResize = isDirector
                      && !span.continuesAfter
                      && !!span.jobs[0]?.plan_id
                      && !span.jobs.some(j => j.status !== 'scheduled');
                    const canResizeLeft = isDirector
                      && !span.cameFromBefore
                      && !!span.jobs[0]?.plan_id
                      && !span.jobs.some(j => j.status !== 'scheduled');

                    const getTargetDs = e => {
                      const rect = e.currentTarget.getBoundingClientRect();
                      const rawFrac = (e.clientX - rect.left) / rect.width;
                      const dayCount = span.endIdx - span.startIdx + 1;
                      const frac = Math.max(0, Math.min(1, rawFrac));
                      const offset = Math.min(Math.floor(frac * dayCount), dayCount - 1);
                      return fmt(days[span.startIdx + offset]);
                    };

                    const isSelected = selectedSpan?.jobId === spanRep.id;

                    const co = !span.cameFromBefore && span.prevJob
                      ? getChangeover(span.prevJob, span.jobs[0])
                      : null;
                    const coLabel = co
                      ? (co.total >= 60 ? `${Math.floor(co.total/60)}h${co.total%60?`${co.total%60}m`:''}` : `${co.total}m`)
                      : null;
                    const coTip = co ? `Changeover: ${co.fromRef||'—'} → ${co.toRef||'—'} · ${co.total} min` : null;
                    const pg = !span.cameFromBefore && span.prevJob ? getPurge(span.prevJob, span.jobs[0]) : null;
                    const pgTip = !pg ? null : pg.kind === 'material'
                      ? `Material change: ${pg.from} → ${pg.to}. The barrel is purged before the new material.`
                      : `Colour purge: ${pg.from} → ${pg.to}. Lighter after darker in the colour order (Admin → Settings).`;

                    return (
                      <div
                        key={span.key}
                        draggable={spanCanDrag}
                        data-run-card={spanRep.id}
                        onClick={(!pendingPlacement && !dragging) ? e => {
                          e.stopPropagation();
                          setSelectedSpan(isSelected ? null : { jobId: spanRep.id, planId: spanRep.plan_id || null, canNudge: spanCanDrag });
                        } : undefined}
                        onDragStart={spanCanDrag ? e => {
                          e.stopPropagation();
                          e.dataTransfer.effectAllowed = 'copyMove';
                          e.dataTransfer.setData('text/plain', String(spanRep.id));
                          setSelectedSpan(null);
                          altDragRef.current = false;
                          setIsDuplicating(false);
                          setDragging({ job: spanRep, fromDate: spanRep.scheduled_date, fromMachineId: spanRep.machine_id, isPlan: span.jobs.length > 1 });
                        } : undefined}
                        onDragEnd={() => { setDragging(null); setDragOver(null); altDragRef.current = false; setIsDuplicating(false); }}
                        onDragOver={isDroppable ? e => {
                          if (!dragging) return;
                          e.preventDefault();
                          const alt = e.altKey;
                          e.dataTransfer.dropEffect = alt ? 'copy' : 'move';
                          if (alt !== altDragRef.current) { altDragRef.current = alt; setIsDuplicating(alt); }
                          setDragOver({ machineId: machine.id, dateStr: getTargetDs(e) });
                        } : undefined}
                        onDrop={isDroppable ? e => {
                          e.preventDefault();
                          setDragOver(null);
                          if (!dragging) return;
                          // Prefer snapFootprint[0] (last advertised drag-over position) over
                          // re-reading the cursor — at the instant of release the cursor can
                          // drift out of the left-edge zone and return the wrong date.
                          const targetDs = snapFootprint[0] || getTargetDs(e);
                          if (dragging.fromDate !== targetDs || dragging.fromMachineId !== machine.id) {
                            if (dragHasEligibility && !draggedEligible.includes(machine.id)) {
                              const partId = dragging.job.parts?.id;
                              toast.error(t => (
                                <span>
                                  <strong>{dragging.job.parts?.name || 'This part'}</strong> cannot run on <strong>{machineName(machine)}</strong> — not in its eligible machines.{' '}
                                  {partId && <button onClick={() => { toast.dismiss(t.id); navigate(`/admin/parts/${partId}`); }} style={{ marginLeft: 6, padding: '2px 8px', fontSize: '0.8em', background: '#14b8a6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Edit part →</button>}
                                </span>
                              ), { duration: 6000 });
                              setDragging(null);
                              return;
                            }
                            const rawDs = getTargetDs(e);
                            if (altDragRef.current || e.altKey) {
                              onDuplicate(dragging.job, targetDs, machine.id);
                            } else if (onNonWorkingDrop && (isWeekendStr(rawDs) || !!holidays[rawDs])) {
                              onNonWorkingDrop(dragging.job, rawDs, machine.id, getDraggedPlanLength(dragging.job), getNonWorkingBlockLen(rawDs));
                            } else {
                              onMove(dragging.job, targetDs, machine.id, true);
                            }
                          }
                          altDragRef.current = false; setIsDuplicating(false);
                          setDragging(null);
                        } : undefined}
                        data-touch-machine={machine.id}
                        data-touch-date={spanRep.scheduled_date}
                        onTouchStart={spanCanDrag ? e => {
                          if (e.touches.length !== 1 || pendingPlacement || dragging) return;
                          const touch = e.touches[0];
                          const startX = touch.clientX;
                          const startY = touch.clientY;
                          const cardEl = e.currentTarget;
                          const rect = cardEl.getBoundingClientRect();
                          touchRef.current = {
                            job: spanRep,
                            fromDate: spanRep.scheduled_date,
                            fromMachineId: spanRep.machine_id,
                            isPlan: span.jobs.length > 1,
                            startX, startY,
                            active: false,
                            ghostEl: null,
                            offsetX: startX - rect.left,
                            offsetY: startY - rect.top,
                            timer: setTimeout(() => {
                              const tr = touchRef.current;
                              if (!tr) return;
                              tr.active = true;
                              const ghost = cardEl.cloneNode(true);
                              Object.assign(ghost.style, {
                                position: 'fixed',
                                left: `${rect.left}px`,
                                top: `${rect.top}px`,
                                width: `${rect.width}px`,
                                height: `${rect.height}px`,
                                opacity: '0.85',
                                pointerEvents: 'none',
                                zIndex: '9999',
                                transform: 'scale(1.05)',
                                boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                margin: '0',
                                transition: 'transform 0.1s',
                              });
                              document.body.appendChild(ghost);
                              tr.ghostEl = ghost;
                              setSelectedSpan(null);
                              setDragging({ job: spanRep, fromDate: spanRep.scheduled_date, fromMachineId: spanRep.machine_id, isPlan: span.jobs.length > 1 });
                            }, 300),
                          };
                        } : undefined}
                        onTouchMove={spanCanDrag ? e => {
                          const tr = touchRef.current;
                          if (!tr) return;
                          const touch = e.touches[0];
                          if (!tr.active) {
                            if (Math.hypot(touch.clientX - tr.startX, touch.clientY - tr.startY) > 5) {
                              clearTimeout(tr.timer);
                              touchRef.current = null;
                            }
                            return;
                          }
                          if (tr.ghostEl) {
                            tr.ghostEl.style.left = `${touch.clientX - tr.offsetX}px`;
                            tr.ghostEl.style.top = `${touch.clientY - tr.offsetY}px`;
                          }
                          const under = document.elementFromPoint(touch.clientX, touch.clientY);
                          const cell = under?.closest?.('[data-touch-machine]');
                          if (cell) {
                            setDragOver({ machineId: Number(cell.dataset.touchMachine), dateStr: cell.dataset.touchDate });
                          } else {
                            setDragOver(null);
                          }
                        } : undefined}
                        onTouchEnd={spanCanDrag ? e => {
                          const tr = touchRef.current;
                          touchRef.current = null;
                          if (!tr) return;
                          clearTimeout(tr.timer);
                          if (tr.ghostEl) tr.ghostEl.remove();
                          if (!tr.active) return;
                          const touch = e.changedTouches[0];
                          const under = document.elementFromPoint(touch.clientX, touch.clientY);
                          const cell = under?.closest?.('[data-touch-machine]');
                          if (cell) {
                            const targetMachineId = Number(cell.dataset.touchMachine);
                            const rawTouchDs = cell.dataset.touchDate;
                            const targetDs = getSnapStart(rawTouchDs);
                            const targetMachine = machines.find(m => m.id === targetMachineId);
                            if (targetDs !== tr.fromDate || targetMachineId !== tr.fromMachineId) {
                              const eligibles = [tr.job.parts?.primary_machine_id, ...(tr.job.parts?.secondary_machine_ids || [])].filter(Boolean).map(Number);
                              if (eligibles.length > 0 && !eligibles.includes(targetMachineId)) {
                                const partId = tr.job.parts?.id;
                                toast.error(t => (
                                  <span>
                                    <strong>{tr.job.parts?.name || 'This part'}</strong> cannot run on <strong>{machineName(targetMachine)}</strong> — not in its eligible machines.{' '}
                                    {partId && <button onClick={() => { toast.dismiss(t.id); navigate(`/admin/parts/${partId}`); }} style={{ marginLeft: 6, padding: '2px 8px', fontSize: '0.8em', background: '#14b8a6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>Edit part →</button>}
                                  </span>
                                ), { duration: 6000 });
                              } else if (onNonWorkingDrop && (isWeekendStr(rawTouchDs) || !!holidays[rawTouchDs])) {
                                onNonWorkingDrop(tr.job, rawTouchDs, targetMachineId, getDraggedPlanLength(tr.job), getNonWorkingBlockLen(rawTouchDs));
                              } else {
                                onMove(tr.job, targetDs, targetMachineId, true);
                              }
                            }
                          }
                          setDragging(null);
                          setDragOver(null);
                        } : undefined}
                        onTouchCancel={spanCanDrag ? () => {
                          const tr = touchRef.current;
                          touchRef.current = null;
                          if (!tr) return;
                          clearTimeout(tr.timer);
                          if (tr.ghostEl) tr.ghostEl.remove();
                          if (tr.active) { setDragging(null); setDragOver(null); }
                        } : undefined}
                        style={{
                          gridColumn: runCols(machine.id, effectiveStartIdx, span.startSlot, effectiveEndIdx, span.endSlot),
                          gridRow: 1,
                          zIndex: isSwapTarget ? 2 : 1,
                          padding: '4px',
                          display: 'flex',
                          alignItems: 'stretch',
                          position: 'relative',
                          cursor: spanCanDrag ? 'grab' : undefined,
                          touchAction: spanCanDrag ? 'none' : undefined,
                          pointerEvents: (isThisSpanDragging || !!pendingPlacement) ? 'none' : (spanCanDrag || canResize || canResizeLeft || isDroppable ? 'auto' : 'none'),
                          outline: isSwapTarget ? '2px solid #6366f1' : isSelected ? '2px solid #818cf8' : undefined,
                          borderRadius: (isSwapTarget || isSelected) ? '10px' : undefined,
                          boxShadow: isSelected ? '0 0 0 3px rgba(99,102,241,0.2)' : undefined,
                        }}
                      >
                        {/* Insert-after drop zone: visible right-edge handle while dragging a different span */}
                        {dragging && !isDuplicating && !isThisSpanDragging && spanCanDrag && onInsertAfter && (
                          <div
                            style={{
                              position: 'absolute', right: 0, top: 0, bottom: 0, width: '14px',
                              zIndex: 15, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: insertAfterHoverId === span.key
                                ? 'rgba(20,184,166,0.75)'
                                : 'rgba(20,184,166,0.12)',
                              borderRadius: '0 8px 8px 0',
                              transition: 'background 0.1s',
                              cursor: 'copy',
                            }}
                            title="Insert after this plan"
                            onDragOver={e => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; setInsertAfterHoverId(span.key); }}
                            onDragLeave={e => { e.stopPropagation(); setInsertAfterHoverId(null); }}
                            onDrop={e => {
                              e.preventDefault();
                              e.stopPropagation();
                              setInsertAfterHoverId(null);
                              if (dragging) onInsertAfter(dragging.job, spanRep, machine.id);
                              setDragging(null);
                            }}
                          >
                            {insertAfterHoverId === span.key && (
                              <i className="fa-solid fa-arrow-right" style={{ fontSize: '9px', color: '#fff', pointerEvents: 'none' }} />
                            )}
                          </div>
                        )}
                        {/* Pending-placement click capture: children have pointer-events:auto by
                            default even when the span tile has none, so we need an explicit
                            overlay that sits on top and handles the occupied-slot click. */}
                        {!!pendingPlacement && (
                          <div
                            style={{
                              position: 'absolute', inset: 0, zIndex: 20,
                              pointerEvents: 'all',
                              cursor: pendingMachineOk ? 'copy' : 'default',
                            }}
                            onDragOver={e => {
                              e.preventDefault();
                              e.dataTransfer.dropEffect = pendingMachineOk ? 'move' : 'none';
                              if (pendingMachineOk) setDragOver({ machineId: machine.id, dateStr: getTargetDs(e) });
                            }}
                            onMouseMove={e => {
                              if (pendingMachineOk) setDragOver({ machineId: machine.id, dateStr: getTargetDs(e) });
                            }}
                            onMouseLeave={() => { if (pendingPlacement) setDragOver(null); }}
                            onDrop={pendingMachineOk ? e => {
                              e.preventDefault();
                              const targetDate = getTargetDs(e);
                              const conflictJob = scheduled.find(j =>
                                j.machine_id === machine.id &&
                                j.scheduled_date === targetDate &&
                                j.status !== 'cancelled' &&
                                j.id !== pendingPlacement.id
                              );
                              if (conflictJob) {
                                onInsertConflict({
                                  job: pendingPlacement,
                                  targetDate,
                                  machineId: machine.id,
                                  conflictPartName: conflictJob.parts?.name || 'Existing job',
                                  conflictPlanId: conflictJob.plan_id || null,
                                  clickX: e.clientX,
                                  clickY: e.clientY,
                                });
                                onClearPendingPlacement();
                              } else {
                                onPendingPlace(pendingPlacement, targetDate, machine.id);
                              }
                            } : undefined}
                            onClick={e => {
                              if (!pendingMachineOk) {
                                toast.error(`${pendingPlacement.parts?.name || 'This part'} cannot run on ${machineName(machine)}`);
                                return;
                              }
                              const targetDate = getTargetDs(e);
                              const conflictJob = scheduled.find(j =>
                                j.machine_id === machine.id &&
                                j.scheduled_date === targetDate &&
                                j.status !== 'cancelled' &&
                                j.id !== pendingPlacement.id
                              );
                              if (conflictJob) {
                                onInsertConflict({
                                  job: pendingPlacement,
                                  targetDate,
                                  machineId: machine.id,
                                  conflictPartName: conflictJob.parts?.name || 'Existing job',
                                  conflictPlanId: conflictJob.plan_id || null,
                                  clickX: e.clientX,
                                  clickY: e.clientY,
                                });
                                onClearPendingPlacement();
                              }
                            }}
                          />
                        )}
                        {/* Changeover stripe — stitched to the left edge of the card */}
                        {co && (
                          <div
                            style={{
                              width: '14px', flexShrink: 0, alignSelf: 'stretch',
                              background: 'rgba(251,191,36,0.85)', borderRadius: '2px',
                              marginRight: '4px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              overflow: 'hidden', cursor: 'default',
                            }}
                            title={coTip}
                          >
                            <span style={{
                              fontSize: '8px', fontWeight: '800', color: '#422006',
                              writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                              letterSpacing: '0.02em', whiteSpace: 'nowrap',
                            }}>
                              {coLabel}
                            </span>
                          </div>
                        )}
                        {/* Purge stripe: another base material, or lighter after darker */}
                        {pg && (
                          <div
                            style={{
                              width: '14px', flexShrink: 0, alignSelf: 'stretch',
                              background: 'rgba(167,139,250,0.85)', borderRadius: '2px',
                              marginRight: '4px',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              overflow: 'hidden', cursor: 'default',
                            }}
                            title={pgTip}
                          >
                            <span style={{
                              fontSize: '8px', fontWeight: '800', color: '#2e1065',
                              writingMode: 'vertical-rl', transform: 'rotate(180deg)',
                              letterSpacing: '0.02em', whiteSpace: 'nowrap',
                            }}>
                              purge
                            </span>
                          </div>
                        )}
                        {isSwapTarget && (
                          <div style={{
                            position: 'absolute', inset: 0,
                            background: 'rgba(99,102,241,0.12)',
                            borderRadius: '8px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            pointerEvents: 'none', zIndex: 4,
                          }}>
                            <span style={{ fontSize: '10px', color: '#a5b4fc', fontWeight: '700', background: 'rgba(20,18,50,0.88)', borderRadius: '4px', padding: '2px 7px', letterSpacing: '0.04em' }}>
                              ⇄ swap
                            </span>
                          </div>
                        )}
                        {/* Its days nobody works on it, faded (21 Oct 2026). */}
                        {(() => {
                          const runDays = new Set(span.jobs.map(j => j.scheduled_date));
                          const [c0, c1] = String(runCols(machine.id, effectiveStartIdx, span.startSlot, effectiveEndIdx, span.endSlot)).split('/').map(x => Number(x.trim()));
                          const off = [];
                          for (let d = effectiveStartIdx; d <= effectiveEndIdx; d++) {
                            const ds = fmt(days[d]);
                            if (runDays.has(ds) || !(isWeekendStr(ds) || holidays[ds] || downFor(machine.id, ds))) continue;
                            off.push([colStartOf(d) - c0, colEndOf(d) - c0]);
                          }
                          return fadeStretches(off, c1 - c0, { gap: 3, inset: 4, radius: 8 }).map(f => <div key={f.key} aria-hidden="true" style={f.style} />);
                        })()}
                        <PlanSpanCard
                          span={span}
                          justMoved={justMoved}
                          isDirector={isDirector}
                          isDraggable={spanCanDrag}
                          colours={colours}
                          dragging={dragging}
                          filterText={filterText}
                          onNavigateWeek={onNavigateWeek}
                          onDismissNew={onDismissNew}
                          planTotalsMap={planTotalsMap}
                          urgencyDays={urgencyDays}
                        />
                        {isDuplicating && isThisSpanDragging && (
                          <div style={{ position: 'absolute', top: '4px', right: '6px', fontSize: '10px', fontWeight: '700', color: '#0f172a', background: '#14b8a6', borderRadius: '4px', padding: '1px 6px', pointerEvents: 'none', zIndex: 20, letterSpacing: '0.02em' }}>⊕ copy</div>
                        )}
                        {canResizeLeft && (
                          <div
                            title="Drag left to prepend days, drag right to trim from start"
                            style={{
                              position: 'absolute', left: co ? '19px' : '1px', top: '50%', transform: 'translateY(-50%)',
                              width: '10px', height: '32px',
                              cursor: 'col-resize',
                              zIndex: 10,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              borderRadius: '6px 0 0 6px',
                            }}
                            onPointerDown={e => {
                              e.stopPropagation();
                              e.preventDefault();
                              e.currentTarget.setPointerCapture(e.pointerId);
                              const spanRect = e.currentTarget.parentElement.getBoundingClientRect();
                              const numCols = span.endIdx - span.startIdx + 1;
                              const colWidth = numCols > 0 ? spanRect.width / numCols : 110;
                              // Precompute leftmost allowed column (block at first occupied cell)
                              let minColIdx = 0;
                              for (let ci = span.startIdx - 1; ci >= 0; ci--) {
                                const d = fmt(days[ci]);
                                if (isWeekendStr(d) || holidays[d]) continue;
                                const occ = scheduled.find(j =>
                                  j.machine_id === machine.id && j.scheduled_date === d &&
                                  j.status !== 'cancelled' &&
                                  (span.jobs[0]?.plan_id ? j.plan_id !== span.jobs[0].plan_id : j.id !== span.jobs[0]?.id)
                                );
                                if (occ) { minColIdx = ci + 1; break; }
                              }
                              resizingRef.current = {
                                side: 'left', spanKey: span.key, startX: e.clientX, colWidth,
                                startStartIdx: span.startIdx, minColIdx,
                                planSpan: span, machineId: machine.id, currentDelta: 0,
                                startScrollLeft: scrollContainerRef.current?.scrollLeft || 0,
                              };
                            }}
                            onPointerMove={e => {
                              if (!resizingRef.current || resizingRef.current.side !== 'left' || resizingRef.current.spanKey !== span.key) return;
                              const { startX, colWidth, startStartIdx, minColIdx, startScrollLeft } = resizingRef.current;
                              const scrollAdjust = (scrollContainerRef.current?.scrollLeft || 0) - startScrollLeft;
                              const rawDelta = Math.round((e.clientX - startX + scrollAdjust) / colWidth);
                              const maxExtendLeft = startStartIdx - minColIdx;
                              const maxShrinkLeft = span.endIdx - startStartIdx;
                              const rawClamped = Math.max(-maxExtendLeft, Math.min(maxShrinkLeft, rawDelta));
                              // Snap past weekends/holidays so the handle never lands on a non-working day
                              let snappedIdx = startStartIdx + rawClamped;
                              if (rawClamped < 0) { // extending left: snap backward past non-working days
                                while (snappedIdx > 0 && snappedIdx < days.length && (isWeekendStr(fmt(days[snappedIdx])) || holidays[fmt(days[snappedIdx])])) snappedIdx--;
                                snappedIdx = Math.max(minColIdx, snappedIdx);
                              } else if (rawClamped > 0) { // shrinking from left: snap forward
                                while (snappedIdx < span.endIdx && snappedIdx < days.length && (isWeekendStr(fmt(days[snappedIdx])) || holidays[fmt(days[snappedIdx])])) snappedIdx++;
                              }
                              const clamped = snappedIdx - startStartIdx;
                              resizingRef.current.currentDelta = clamped;
                              setResizeStartDeltaMap(prev =>
                                prev[span.key] === clamped ? prev : { ...prev, [span.key]: clamped }
                              );
                              // Auto-scroll container near left/right edges during resize (multi-week only)
                              const sc = scrollContainerRef.current;
                              if (sc && weekSpan > 1) {
                                const rect = sc.getBoundingClientRect();
                                const EDGE = 70;
                                if (e.clientX < rect.left + EDGE && !scrollIntervalRef.current) {
                                  scrollIntervalRef.current = setInterval(() => sc.scrollBy({ left: -100 }), 60);
                                } else if (e.clientX > rect.right - EDGE && !scrollIntervalRef.current) {
                                  scrollIntervalRef.current = setInterval(() => sc.scrollBy({ left: 100 }), 60);
                                } else if (e.clientX >= rect.left + EDGE && e.clientX <= rect.right - EDGE) {
                                  clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null;
                                }
                              }
                            }}
                            onPointerUp={e => {
                              if (!resizingRef.current || resizingRef.current.side !== 'left' || resizingRef.current.spanKey !== span.key) return;
                              const { startStartIdx, planSpan, machineId: mId, currentDelta } = resizingRef.current;
                              resizingRef.current = null;
                              clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null;
                              setResizeStartDeltaMap({});
                              if (currentDelta === 0) return;
                              const newStartIdx = Math.max(0, Math.min(span.endIdx, startStartIdx + currentDelta));
                              if (newStartIdx < startStartIdx) {
                                // Extending left — prepend new dates
                                const newDates = [];
                                for (let ci = newStartIdx; ci < startStartIdx; ci++) {
                                  const d = fmt(days[ci]);
                                  if (!isWeekendStr(d) && !holidays[d]) newDates.push(d);
                                }
                                if (newDates.length > 0) onResizeEnd({ type: 'prepend', span: planSpan, machineId: mId, newDates });
                              } else {
                                // Shrinking from left — remove days before new start
                                const cutoffDate = fmt(days[newStartIdx]);
                                onResizeEnd({ type: 'shrink-left', span: planSpan, cutoffDate });
                              }
                            }}
                          >
                            <div style={{
                              width: '3px', height: '18px', borderRadius: '2px',
                              background: resizeStartDelta !== 0 ? '#14b8a6' : 'rgba(255,255,255,0.25)',
                              transition: 'background 0.15s',
                            }} />
                          </div>
                        )}
                        {canResize && (
                          <div
                            title="Drag to extend or shrink plan"
                            style={{
                              position: 'absolute', right: '1px', top: '50%', transform: 'translateY(-50%)',
                              width: '10px', height: '32px',
                              cursor: 'col-resize',
                              zIndex: 10,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              borderRadius: '0 6px 6px 0',
                            }}
                            onPointerDown={e => {
                              e.stopPropagation();
                              e.preventDefault();
                              e.currentTarget.setPointerCapture(e.pointerId);
                              const spanRect = e.currentTarget.parentElement.getBoundingClientRect();
                              const numCols = span.endIdx - span.startIdx + 1;
                              const colWidth = numCols > 0 ? spanRect.width / numCols : 110;
                              resizingRef.current = {
                                side: 'right', spanKey: span.key, startX: e.clientX, colWidth,
                                startEndIdx: span.endIdx, planSpan: span, machineId: machine.id,
                                currentDelta: 0,
                                startScrollLeft: scrollContainerRef.current?.scrollLeft || 0,
                              };
                            }}
                            onPointerMove={e => {
                              if (!resizingRef.current || resizingRef.current.side !== 'right' || resizingRef.current.spanKey !== span.key) return;
                              const { startX, colWidth, startEndIdx, startScrollLeft } = resizingRef.current;
                              const scrollAdjust = (scrollContainerRef.current?.scrollLeft || 0) - startScrollLeft;
                              const rawDelta = Math.round((e.clientX - startX + scrollAdjust) / colWidth);
                              const maxExtend = (7 * weekSpan - 1) - startEndIdx;
                              const maxShrink = span.startIdx - startEndIdx;
                              const rawClamped = Math.max(maxShrink, Math.min(maxExtend, rawDelta));
                              // Snap past weekends/holidays so the handle never lands on a non-working day
                              let snappedIdx = startEndIdx + rawClamped;
                              if (rawClamped > 0) { // extending right: snap forward past non-working days
                                const limit = 7 * weekSpan - 1;
                                while (snappedIdx <= limit && snappedIdx < days.length && (isWeekendStr(fmt(days[snappedIdx])) || holidays[fmt(days[snappedIdx])])) snappedIdx++;
                                snappedIdx = Math.min(limit, snappedIdx);
                              } else if (rawClamped < 0) { // shrinking from right: snap backward
                                while (snappedIdx > span.startIdx && snappedIdx < days.length && (isWeekendStr(fmt(days[snappedIdx])) || holidays[fmt(days[snappedIdx])])) snappedIdx--;
                              }
                              const clamped = snappedIdx - startEndIdx;
                              resizingRef.current.currentDelta = clamped;
                              setResizeDeltaMap(prev =>
                                prev[span.key] === clamped ? prev : { ...prev, [span.key]: clamped }
                              );
                              // Auto-scroll container near left/right edges during resize (multi-week only)
                              const sc = scrollContainerRef.current;
                              if (sc && weekSpan > 1) {
                                const rect = sc.getBoundingClientRect();
                                const EDGE = 70;
                                if (e.clientX < rect.left + EDGE && !scrollIntervalRef.current) {
                                  scrollIntervalRef.current = setInterval(() => sc.scrollBy({ left: -100 }), 60);
                                } else if (e.clientX > rect.right - EDGE && !scrollIntervalRef.current) {
                                  scrollIntervalRef.current = setInterval(() => sc.scrollBy({ left: 100 }), 60);
                                } else if (e.clientX >= rect.left + EDGE && e.clientX <= rect.right - EDGE) {
                                  clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null;
                                }
                              }
                            }}
                            onPointerUp={e => {
                              if (!resizingRef.current || resizingRef.current.side !== 'right' || resizingRef.current.spanKey !== span.key) return;
                              const { startEndIdx, planSpan, machineId: mId, currentDelta } = resizingRef.current;
                              resizingRef.current = null;
                              clearInterval(scrollIntervalRef.current); scrollIntervalRef.current = null;
                              setResizeDeltaMap({});
                              if (currentDelta === 0) return;
                              const newEndIdx = Math.max(span.startIdx, Math.min(7 * weekSpan - 1, startEndIdx + currentDelta));
                              if (newEndIdx > startEndIdx) {
                                const newDates = [];
                                for (let ci = startEndIdx + 1; ci <= newEndIdx; ci++) {
                                  const d = fmt(days[ci]);
                                  if (!isWeekendStr(d) && !holidays[d]) newDates.push(d);
                                }
                                if (newDates.length > 0) onResizeEnd({ type: 'extend', span: planSpan, machineId: mId, newDates });
                              } else {
                                const cutoffDate = fmt(days[newEndIdx]);
                                onResizeEnd({ type: 'shrink', span: planSpan, cutoffDate });
                              }
                            }}
                          >
                            <div style={{
                              width: '3px', height: '18px', borderRadius: '2px',
                              background: resizeDelta !== 0 ? '#14b8a6' : 'rgba(255,255,255,0.25)',
                              transition: 'background 0.15s',
                            }} />
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Layer 2: cascade preview ghosts — show where displaced jobs would land */}
                  {(dragging || pendingPlacement) && cascadePreview
                    .filter(d => d.job.machine_id === machine.id)
                    .map(({ job, fromDate, toDate }) => {
                      const toColIdx   = days.findIndex(d => fmt(d) === toDate);
                      const fromColIdx = days.findIndex(d => fmt(d) === fromDate);
                      const partHex = job.parts?.colour
                        ? (colours.find(c => c.name === job.parts.colour)?.hex || '#f59e0b')
                        : '#f59e0b';
                      return (
                        <React.Fragment key={`ghost-${job.id}`}>
                          {/* Destination ghost — where the job will land */}
                          {toColIdx >= 0 && (
                            <div style={{
                              gridColumn: colSpanOf(toColIdx, toColIdx),
                              gridRow: 1, zIndex: 1, padding: '4px',
                              pointerEvents: 'none',
                            }}>
                              <div style={{
                                height: '100%', borderRadius: '8px',
                                border: `2px dashed ${partHex}`,
                                background: `${partHex}18`,
                                display: 'flex', flexDirection: 'column',
                                alignItems: 'flex-start', justifyContent: 'flex-end',
                                padding: '4px 6px', gap: '1px', opacity: 0.8,
                              }}>
                                <i className="fa-solid fa-arrow-right" style={{ fontSize: '8px', color: partHex }} />
                                <span style={{ fontSize: '10px', color: partHex, fontWeight: '600', lineHeight: '1.2', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                                  {job.parts?.name || 'Job'}
                                </span>
                              </div>
                            </div>
                          )}
                          {/* Origin hatch — where the job is vacating from */}
                          {fromColIdx >= 0 && (
                            <div style={{
                              gridColumn: colSpanOf(fromColIdx, fromColIdx),
                              gridRow: 1, zIndex: 2, padding: '4px',
                              pointerEvents: 'none',
                            }}>
                              <div style={{
                                height: '100%', borderRadius: '8px',
                                background: 'repeating-linear-gradient(135deg, transparent, transparent 5px, rgba(245,158,11,0.12) 5px, rgba(245,158,11,0.12) 10px)',
                                border: '1px dashed rgba(245,158,11,0.35)',
                              }} />
                            </div>
                          )}
                        </React.Fragment>
                      );
                    })
                  }

                  {/* Back-zone drop targets: one per span, placed in the previous column.
                      Only visible when that span is actively being dragged. These are NOT
                      children of the drag source so browsers reliably fire drop on them. */}
                  {spans.map(span => {
                    if (!dragging || dragging.job.id !== span.jobs[0].id) return null;
                    if (span.startIdx === 0) return null;
                    const prevDs = fmt(days[span.startIdx - 1]);
                    if (isWeekendStr(prevDs) || holidays[prevDs]) return null;
                    return (
                      <div
                        key={span.key + '-bz'}
                        style={{ gridColumn: colSpanOf(span.startIdx - 1, span.startIdx - 1), gridRow: 1, zIndex: 3 }}
                        onDragOver={e => {
                          e.preventDefault();
                          setDragOver({ dateStr: prevDs, machineId: machine.id });
                        }}
                        onDrop={e => {
                          e.preventDefault();
                          setDragOver(null);
                          if (dragging) onMove(dragging.job, prevDs, machine.id, true);
                          setDragging(null);
                        }}
                      />
                    );
                  })}
                </div>
              );
            })()}
          </React.Fragment>
          );
        })}
      </div>
    </div>

    {/* Dispatch date popover */}
    {dispatchPopover && (
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 900 }}
        onMouseDown={() => setDispatchPopover(null)}
      >
        <div
          onMouseDown={e => e.stopPropagation()}
          style={{
            position: 'fixed', left: Math.min(dispatchPopover.x, window.innerWidth - 316), top: dispatchPopover.y,
            background: '#1e2329', border: '1px solid rgba(20,184,166,0.35)',
            borderRadius: '10px', padding: '12px 14px', minWidth: '240px', maxWidth: 'min(300px, calc(100vw - 32px))',
            zIndex: 901, boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          }}
        >
          <div style={{ fontSize: '11px', fontWeight: '700', color: '#14b8a6', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <i className="fa-solid fa-truck" /> Dispatch {new Date(dispatchPopover.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
          </div>
          {dispatchPopover.orders.length === 0 ? (
            <div style={{ fontSize: '12px', color: '#6b7280' }}>No orders found</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {dispatchPopover.orders.map(order => (
                <DispatchOrderRow
                  key={order.id}
                  order={order}
                  saving={dispatchSaving}
                  onSave={async (orderId, newDate) => {
                    setDispatchSaving(true);
                    try {
                      await api.put(`/api/v1/orders/${orderId}`, { dispatch_date: newDate });
                      setDispatchPopover(null);
                      if (onDispatchReload) onDispatchReload();
                    } catch (e) {
                      // keep popover open on error
                    } finally {
                      setDispatchSaving(false);
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    )}
    </>
  );
}

// ── Day view ──────────────────────────────────────────────────

function DayView({ machines, viewDate, TODAY, holidays, jobsFor, prevJobOnMachine, getChangeover, getPurge = () => null, isDirector, onEdit, onDelete, onMove, colours, onLiveView, jobPlanTotals, filterText, onComplete, onUncomplete, scheduled, urgencyDays = { red: 2, amber: 7 } }) {
  const ds      = fmt(viewDate);
  const isToday = ds === TODAY;
  const holiday = holidays[ds];
  const isWknd  = isWeekendStr(ds);
  const nonWork = holiday || isWknd;
  const [dragging, setDragging] = useState(null); // { job, fromMachineId }
  const [dragOver, setDragOver] = useState(null); // machineId

  if (!machines.length) return <EmptyState msg="No machines found." />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
      {/* Day header */}
      <div style={{
        backgroundImage: nonWork ? crossHatch(!!holiday) : undefined,
        backgroundColor: nonWork ? (holiday ? 'rgba(251,191,36,0.04)' : '#141920') : (isToday ? 'rgba(20,184,166,0.1)' : '#1a2030'),
        border: `1px solid ${isToday ? 'rgba(20,184,166,0.35)' : holiday ? 'rgba(251,191,36,0.25)' : '#2a3040'}`,
        borderRadius: '6px', padding: '10px 14px', marginBottom: '4px',
        display: 'flex', alignItems: 'center', gap: '10px',
      }}>
        <div style={{ fontSize: '13px', fontWeight: '700', color: isToday ? '#14b8a6' : holiday ? '#fbbf24' : '#f0f2f5' }}>
          {viewDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
        {holiday && (
          <span style={{ fontSize: '11px', color: '#fbbf24', background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: '5px', padding: '2px 8px' }}>
            {holiday}
          </span>
        )}
        {isWknd && !holiday && (
          <span style={{ fontSize: '11px', color: '#4b5563', background: 'rgba(255,255,255,0.04)', border: '1px solid #2a3040', borderRadius: '5px', padding: '2px 8px' }}>
            Weekend
          </span>
        )}
      </div>

      {machines.map(machine => {
        const jobs        = jobsFor(machine.id, ds);
        const co          = getChangeover(prevJobOnMachine(machine.id, ds), jobs[0]);
        const isDropTarget = dragging != null && dragOver === machine.id && dragging.fromMachineId !== machine.id;
        return (
          <div key={machine.id} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '3px' }}>
            <div style={st.machineLabel}>
              <div style={{ fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '5px' }}>
                {machineName(machine)}
                <MachineStatusDot machine={machine} />
              </div>
              {machine.tonnes && <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>{machine.tonnes}T</div>}
            </div>
            <div
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(machine.id); }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null); }}
              onDrop={e => {
                e.preventDefault();
                setDragOver(null);
                if (dragging && dragging.fromMachineId !== machine.id) onMove(dragging.job, ds, machine.id);
                setDragging(null);
              }}
              style={{
                backgroundImage: isDropTarget ? undefined : (nonWork ? crossHatch(!!holiday) : undefined),
                backgroundColor: isDropTarget ? 'rgba(20,184,166,0.1)' : nonWork ? '#131920' : '#1e2329',
                border: `1px solid ${isDropTarget ? '#14b8a6' : '#2a3040'}`,
                borderRadius: '6px', padding: '8px', display: 'flex', flexDirection: 'column', gap: '4px', minHeight: '70px',
                transition: 'border-color 0.1s, background-color 0.1s',
              }}>
              {co && <ChangeoverBadge info={co} />}
              <PurgeBadge info={getPurge(prevJobOnMachine(machine.id, ds), jobs[0])} />
              {jobs.map((job, ji) => {
                const canDrag = isDirector && job.status !== 'started' && job.status !== 'completed' && job.status !== 'cancelled';
                const planJobs = job.plan_id && scheduled ? scheduled.filter(j => j.plan_id === job.plan_id) : [job];
                const planAllDone = planJobs.every(j => j.status === 'completed');
                const planHasStarted = planJobs.some(j => j.status === 'started' && j.job?.status === 'running');
                const planHasDirectorDone = planJobs.some(j => j.director_completed);
                const planTotalQty = planJobs.reduce((s, j) => s + (j.target_qty || 0), 0);
                const planGoodParts = madeOnce(planJobs);
                return (
                  <React.Fragment key={job.id}>
                    {ji > 0 && <ChangeoverBadge info={getChangeover(jobs[ji - 1], job)} />}
                    {ji > 0 && <PurgeBadge info={getPurge(jobs[ji - 1], job)} />}
                    <div
                      draggable={canDrag}
                      onDragStart={canDrag ? e => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', String(job.id));
                        setDragging({ job, fromMachineId: machine.id });
                      } : undefined}
                      onDragEnd={() => { setDragging(null); setDragOver(null); }}
                      style={{ opacity: dragging?.job.id === job.id ? 0.4 : (filterText && !jobMatchesFilter(job, filterText) ? 0.15 : 1), cursor: canDrag ? 'grab' : 'default', transition: 'opacity 0.15s' }}
                    >
                      <JobCard job={job} isDirector={isDirector} onEdit={onEdit} onDelete={onDelete} colours={colours} detailed onLiveView={onLiveView} planTotals={jobPlanTotals[job.id]} urgencyDays={urgencyDays} />
                      {isDirector && !planAllDone && !planHasStarted && (
                        <button onClick={() => onComplete({ planId: job.plan_id || job.id, partName: job.parts?.name || 'Job', totalQty: planTotalQty, goodPartsMade: planGoodParts })}
                          style={{ marginTop: '4px', width: '100%', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: '6px', color: '#6ee7b7', fontSize: '11px', fontWeight: '600', padding: '5px 10px', cursor: 'pointer', fontFamily: 'system-ui' }}>
                          <i className="fa-solid fa-check" style={{ marginRight: '5px', fontSize: '9px' }} />Mark Job Complete
                        </button>
                      )}
                      {isDirector && planHasDirectorDone && planAllDone && (
                        <button onClick={() => onUncomplete(job.plan_id || job.id)}
                          style={{ marginTop: '4px', width: '100%', background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '6px', color: '#fbbf24', fontSize: '11px', fontWeight: '600', padding: '5px 10px', cursor: 'pointer', fontFamily: 'system-ui' }}>
                          <i className="fa-solid fa-rotate-left" style={{ marginRight: '5px', fontSize: '9px' }} />Revert Completion
                        </button>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
              {!jobs.length && isDirector && !nonWork && (
                <button onClick={() => onEdit({ scheduled_date: ds, machine_id: machine.id })} style={{ ...st.addCell, minHeight: '40px', fontSize: '13px' }}>+ Add job</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Dispatch order row (inside dispatch popover) ──────────────

function DispatchOrderRow({ order, saving, onSave }) {
  const [editing, setEditing] = React.useState(false);
  const inputRef = React.useRef(null);
  const label = [order.order_number, order.customer].filter(Boolean).join(' · ') || `Order #${order.id}`;
  const currentDate = order.dispatch_date;

  function commit() { onSave(order.id, inputRef.current?.value || null); }

  if (editing) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <div style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '2px' }}>{label}</div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <input
            ref={inputRef}
            type="date"
            defaultValue={currentDate || ''}
            autoFocus
            disabled={saving}
            onKeyDown={e => { if (e.key === 'Escape') setEditing(false); if (e.key === 'Enter') commit(); }}
            style={{ flex: 1, background: '#161b22', border: '1px solid rgba(20,184,166,0.4)', borderRadius: '6px', color: '#f0f2f5', padding: '4px 8px', fontSize: '12px', fontFamily: 'system-ui' }}
          />
          <button
            disabled={saving}
            onClick={commit}
            style={{ background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.5 : 1 }}>
            ✓
          </button>
          <button
            onClick={() => setEditing(false)}
            style={{ background: 'none', border: '1px solid #2a3040', borderRadius: '6px', color: '#6b7280', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}>
            ✕
          </button>
        </div>
        {currentDate && (
          <button
            onClick={() => onSave(order.id, null)}
            style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: '10px', cursor: 'pointer', textAlign: 'left', padding: '0', marginTop: '2px' }}>
            Clear confirmed date
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      onClick={() => setEditing(true)}
      style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 8px', background: 'rgba(255,255,255,0.03)', borderRadius: '6px', cursor: 'pointer', gap: '8px' }}
    >
      <span style={{ fontSize: '12px', color: '#d1d5db', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ fontSize: '10px', color: currentDate ? '#14b8a6' : '#4b5563', flexShrink: 0, border: `1px ${currentDate ? 'solid' : 'dashed'} ${currentDate ? 'rgba(20,184,166,0.4)' : '#2a3040'}`, borderRadius: '4px', padding: '1px 5px' }}>
        {currentDate ? new Date(currentDate + 'T12:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'suggested'}
      </span>
    </div>
  );
}

// ── Month span card ───────────────────────────────────────────

function MonthSpanCard({ span, colours, filterText, selected = false, onSelect, urgencyDays = { red: 2, amber: 7 } }) {
  const rep = span.jobs[0];
  const partColourName = rep.parts?.colour || null;
  const partHex = partColourName ? (colours.find(c => c.name === partColourName)?.hex || null) : null;
  const { bg, border } = partColourBg(partHex);
  const isDimmed = filterText && !span.jobs.some(j => jobMatchesFilter(j, filterText));

  const orderLine = rep.order_line?.order;
  const dueBadge = (() => {
    if (!orderLine?.delivery_week) return null;
    const fri = weekToFriday(orderLine.delivery_week, orderLine.delivery_year);
    const [fy, fm, fd] = fri.split('-').map(Number);
    const hardDL    = fmt(addDays(new Date(fy, fm - 1, fd), -urgencyDays.red));
    const primaryDL = fmt(addDays(new Date(fy, fm - 1, fd), -urgencyDays.amber));
    const lastDay = span.jobs[span.jobs.length - 1].scheduled_date;
    const color = lastDay > hardDL ? '#ef4444' : lastDay > primaryDL ? '#f59e0b' : null;
    return color ? { color } : null;
  })();

  const urgencyOverlay = dueBadge?.color === '#ef4444'
    ? 'rgba(239,68,68,0.18)' : dueBadge?.color === '#f59e0b'
    ? 'rgba(245,158,11,0.14)' : null;
  const cardBg     = urgencyOverlay ? `linear-gradient(${urgencyOverlay},${urgencyOverlay}),${bg}` : bg;
  const cardBorder = dueBadge?.color || border;

  const tl = span.cameFromBefore ? '0' : '4px';
  const tr = span.continuesAfter ? '0' : '4px';

  return (
    <div
      className="mo-runcard" data-run-card={rep.id}
      onClick={e => { e.stopPropagation(); onSelect(rep.id); }}
      title={`${rep.parts?.name || '—'}${span.totalQty ? ` · ${span.totalQty.toLocaleString()} pcs` : ''}`}
      style={{
        height: '100%', background: cardBg, border: `${urgencyOverlay ? '3px' : '1px'} solid ${cardBorder}`,
        borderRadius: `${tl} ${tr} ${tr} ${tl}`,
        outline: selected ? '2px solid #818cf8' : undefined,
        display: 'flex', alignItems: 'center', gap: '3px', padding: '0 5px',
        cursor: 'pointer',
        opacity: isDimmed ? 0.12 : 1, overflow: 'hidden',
        fontFamily: 'system-ui', transition: 'opacity 0.15s',
      }}
    >
      {span.cameFromBefore && <span style={{ fontSize: '9px', color: '#f0f2f5', flexShrink: 0, opacity: 0.8 }}>◀</span>}
      <span style={{ fontSize: '9px', color: '#f0f2f5', fontWeight: '600', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {rep.parts?.name || '—'}
      </span>
      {span.totalQty > 0 && (
        <span style={{ fontSize: '8px', color: 'rgba(240,242,245,0.55)', flexShrink: 0 }}>{span.totalQty.toLocaleString()}</span>
      )}
      {orderLine?.delivery_week && (
        <span style={{ fontSize: '8px', color: dueBadge?.color || 'rgba(240,242,245,0.45)', background: dueBadge?.color ? dueBadge.color + '22' : 'rgba(240,242,245,0.08)', border: `1px solid ${dueBadge?.color ? dueBadge.color + '55' : 'rgba(240,242,245,0.15)'}`, borderRadius: '2px', padding: '0 3px', flexShrink: 0, fontWeight: '600' }}>
          KW {orderLine.delivery_week}
        </span>
      )}
      {span.continuesAfter && <span style={{ fontSize: '9px', color: '#f0f2f5', flexShrink: 0, opacity: 0.8 }}>▶</span>}
    </div>
  );
}

// ── Month view ────────────────────────────────────────────────

function MonthView({ scheduled, viewDate, TODAY, holidays, downFor = () => null, isDirector, onEdit, onDelete, onComplete, onUncomplete, onMoveFrom, planTotalsMap, onDayClick, colours, filterText, allDeliveryWeekSet, urgencyDays = { red: 2, amber: 7 }, showCompleted = true }) {
  // The run clicked, with its bar -- as on the week view (21 Oct 2026).
  const [selected, setSelected] = useState(null);   // a job id
  useEffect(() => {
    if (!selected) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setSelected(null); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [selected]);
  const selectedRep = selected ? scheduled.find(j => j.id === selected) : null;
  const calDays  = getMonthCalDays(viewDate);
  const curMonth = viewDate.getMonth();

  const byDate = {};
  scheduled.forEach(j => {
    if (!byDate[j.scheduled_date]) byDate[j.scheduled_date] = [];
    byDate[j.scheduled_date].push(j);
  });

  const weekGroups = [];
  for (let i = 0; i < calDays.length; i += 7) weekGroups.push(calDays.slice(i, i + 7));

  return (
    <div>
      <style>{RUN_FADE_RULE}</style>
      {selectedRep && (() => {
        const close = () => setSelected(null);
        const bar = runBarProps({ rep: selectedRep, scheduled, planTotalsMap, isDirector,
          onComplete, onUncomplete, onEdit, onDelete, onMoveFrom, close });
        return <RunBar anchor={`[data-run-card="${selectedRep.id}"]`} {...bar} onClose={close} />;
      })()}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', marginBottom: '2px' }}>
        {DAY_LABELS.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', padding: '6px 0' }}>{d}</div>
        ))}
      </div>

      {weekGroups.map((weekDays, weekIdx) => {
        const { week, year } = isoWeekOf(weekDays[0]);
        const isDelivery = allDeliveryWeekSet.has(`${year}-${String(week).padStart(2, '0')}`);

        // Build plan spans for this week
        const planMap = {};
        weekDays.forEach((day, colIdx) => {
          const ds = fmt(day);
          (byDate[ds] || []).filter(j => j.status !== 'cancelled' && (showCompleted || j.status !== 'completed')).forEach(job => {
            const key = job.plan_id || `solo-${job.id}-${ds}`;
            if (!planMap[key]) planMap[key] = { entries: [], planId: job.plan_id, machineId: job.machine_id };
            planMap[key].entries.push({ job, ds, colIdx });
          });
        });

        const spans = Object.values(planMap).map(({ entries, planId, machineId }) => {
          entries.sort((a, b) => a.colIdx - b.colIdx);
          const startCol = entries[0].colIdx;
          const endCol   = entries[entries.length - 1].colIdx;
          const jobs     = entries.map(e => e.job);
          const totalQty = jobs.reduce((s, j) => s + (j.target_qty || 0), 0);
          const dayBefore = fmt(addDays(weekDays[0], -1));
          const dayAfter  = fmt(addDays(weekDays[6], 1));
          const cameFromBefore = planId && (byDate[dayBefore] || []).some(j => j.plan_id === planId && j.machine_id === machineId);
          const continuesAfter = planId && (byDate[dayAfter]  || []).some(j => j.plan_id === planId && j.machine_id === machineId);
          return { key: planId || `solo-${jobs[0].id}`, jobs, startCol, endCol, totalQty, cameFromBefore, continuesAfter };
        });

        // Assign spans to lanes so non-overlapping spans share a row
        const sorted = [...spans].sort((a, b) => a.startCol - b.startCol || a.endCol - b.endCol);
        const laneEnds = [];
        const spansWithLane = sorted.map(span => {
          let lane = laneEnds.findIndex(end => end < span.startCol);
          if (lane === -1) { lane = laneEnds.length; laneEnds.push(-1); }
          laneEnds[lane] = span.endCol;
          return { ...span, lane };
        });
        const laneCount = laneEnds.length;

        return (
          <div key={`week-${year}-${week}`} style={{ borderTop: weekIdx === 0 ? 'none' : '1px solid #2a3040', paddingTop: weekIdx === 0 ? '0' : '6px', marginBottom: '4px' }}>
            {isDelivery && (
              <div style={{ background: 'rgba(251,191,36,0.07)', border: '1px solid rgba(251,191,36,0.22)', borderRadius: '5px', padding: '3px 10px', marginBottom: '2px', fontSize: '10px', color: '#fbbf24', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                📦 Delivery week — Wk {week}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '2px', paddingTop: '4px', marginBottom: laneCount > 0 ? '2px' : '0' }}>
              {weekDays.map(day => {
                const ds         = fmt(day);
                const isToday    = ds === TODAY;
                const isCurMonth = day.getMonth() === curMonth;
                const holiday    = holidays[ds];
                const isWknd     = isWeekendStr(ds);
                const nonWork    = holiday || isWknd;
                const hasJobs    = (byDate[ds] || []).some(j => j.status !== 'cancelled');
                return (
                  <div key={ds}
                    onClick={() => onDayClick(day)}
                    style={{
                      backgroundImage: nonWork ? crossHatch(!!holiday) : undefined,
                      backgroundColor: isToday ? 'rgba(20,184,166,0.08)'
                        : nonWork ? '#131920' : '#1e2329',
                      border: `1px solid ${isToday ? 'rgba(20,184,166,0.35)' : '#2a3040'}`,
                      borderRadius: '6px', padding: '5px 6px', minHeight: '52px',
                      cursor: 'pointer', opacity: isCurMonth ? 1 : 0.35, transition: 'border-color 0.15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#14b8a6'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = isToday ? 'rgba(20,184,166,0.35)' : '#2a3040'; }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                      <span style={{ fontSize: '22px', fontWeight: isToday ? '700' : '400', lineHeight: 1, color: isToday ? '#14b8a6' : holiday ? '#fbbf24' : isWknd ? '#4b5563' : '#f0f2f5' }}>
                        {day.getDate()}
                      </span>
                      <span style={{ fontSize: '9px', color: isToday ? '#14b8a6' : holiday ? '#fbbf24' : isWknd ? '#374151' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                        {DAY_LABELS[day.getDay() === 0 ? 6 : day.getDay() - 1]}
                      </span>
                    </div>
                    {holiday && isCurMonth && (
                      <div style={{ fontSize: '8px', color: '#fbbf24', lineHeight: 1.2, marginTop: '1px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', opacity: 0.9 }}>{holiday}</div>
                    )}
                    {!hasJobs && isDirector && isCurMonth && !nonWork && (
                      <button onClick={e => { e.stopPropagation(); onEdit(null); }}
                        style={{ background: 'none', border: 'none', color: '#374151', fontSize: '14px', cursor: 'pointer', padding: 0, lineHeight: 1, marginTop: '1px' }}>+</button>
                    )}
                  </div>
                );
              })}
            </div>

            {laneCount > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: `repeat(${laneCount}, 26px)`, gap: '2px' }}>
                {spansWithLane.map(span => (
                  <div key={span.key} style={{ gridColumn: `${span.startCol + 1} / ${span.endCol + 2}`, gridRow: span.lane + 1, minWidth: 0, position: 'relative' }}>
                    {(() => {
                      const runDays = new Set(span.jobs.map(j => j.scheduled_date));
                      const mId = span.jobs[0].machine_id;
                      const off = [];
                      for (let c = span.startCol; c <= span.endCol; c++) {
                        const ds = fmt(weekDays[c]);
                        if (runDays.has(ds) || !(isWeekendStr(ds) || holidays[ds] || downFor(mId, ds))) continue;
                        off.push([c - span.startCol, c - span.startCol + 1]);
                      }
                      return fadeStretches(off, span.endCol - span.startCol + 1, { gap: 2, radius: 4 }).map(f => <div key={f.key} aria-hidden="true" style={f.style} />);
                    })()}
                    <MonthSpanCard span={span} colours={colours} filterText={filterText} urgencyDays={urgencyDays}
                      selected={selected === span.jobs[0].id} onSelect={id => setSelected(cur => (cur === id ? null : id))} />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Job card ──────────────────────────────────────────────────

// Returns the ISO date string of the Friday in ISO week `week` of `year`.
function weekToFriday(week, year) {
  // Jan 4 is always in week 1 per ISO 8601
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7; // Mon=1..Sun=7
  const monday1 = new Date(jan4.getTime() - (dayOfWeek - 1) * 86400000);
  const friday = new Date(monday1.getTime() + ((week - 1) * 7 + 4) * 86400000);
  return fmt(friday);
}

function JobCard({ job, isDirector, onEdit, onDelete, detailed = false, colours = [], onLiveView, planTotals, urgencyDays = { red: 2, amber: 7 } }) {
  const canEdit  = isDirector;
  const liveId   = job.job?.id || job.job_id;
  const isActive = job.status === 'started' && liveId;
  const sc = getStatusColor(job);
  const mbHex      = getMbHex(job.recipe, colours);
  const mbBg       = mbHex ? getMbDarkBg(mbHex)     : null;
  const mbText     = mbHex ? getMbTextColor(mbHex)   : null;
  const titleColor = getContrastTitle(mbHex);
  const hasPendingChange = job.job?.pending_change != null;
  const wasAcknowledged  = !hasPendingChange && job.job?.pending_change_acknowledged_at != null;

  const orderLine = job.order_line?.order;
  const dueBadge = (() => {
    if (!orderLine?.delivery_week || !orderLine?.delivery_year) return null;
    const fri = weekToFriday(orderLine.delivery_week, orderLine.delivery_year);
    const [fy, fm, fd] = fri.split('-').map(Number);
    const hardDL    = fmt(addDays(new Date(fy, fm - 1, fd), -urgencyDays.red));
    const primaryDL = fmt(addDays(new Date(fy, fm - 1, fd), -urgencyDays.amber));
    const color = job.scheduled_date > hardDL ? '#ef4444' : job.scheduled_date > primaryDL ? '#f59e0b' : '#6b7280';
    return { label: `Due Wk ${orderLine.delivery_week}`, color };
  })();

  return (
    <div
      onClick={isActive && onLiveView ? () => onLiveView(liveId) : undefined}
      style={{ background: mbBg || sc.bg, border: `1px solid ${sc.border}`, borderRadius: '5px', padding: detailed ? '8px 10px' : '5px 7px', cursor: isActive && onLiveView ? 'pointer' : undefined }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '4px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: detailed ? '14px' : '12px', fontWeight: '700', color: titleColor, lineHeight: 1.2, marginBottom: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
            {[job.parts?.name, job.order_number].filter(Boolean).join(' — ') || '—'}
          </div>
          {isActive && planTotals?.total_good_parts != null ? (
            <div style={{ fontSize: '10px', color: mbText || '#f0f2f5', fontWeight: '600', marginTop: '1px' }}>
              {planTotals.total_good_parts.toLocaleString()} / {(planTotals.target_parts || job.target_qty || 0).toLocaleString()} pcs
            </div>
          ) : job.target_qty ? (
            <div style={{ fontSize: '9px', color: mbText || '#6b7280' }}>
              {job.target_qty.toLocaleString()} pcs
            </div>
          ) : null}
          {detailed && job.notes && <div style={{ fontSize: '10px', color: mbText || '#9ca3af', marginTop: '4px', fontStyle: 'italic' }}>{job.notes}</div>}
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
            <button onClick={() => onEdit(job)}      style={st.iconEdit} title="Edit"><i className="fa-solid fa-pen"   style={{ fontSize: '10px' }} /></button>
            <button onClick={() => onDelete(job.id)} style={st.iconDel}  title="Delete"><i className="fa-solid fa-trash" style={{ fontSize: '10px' }} /></button>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px', gap: '4px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '8px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', background: 'rgba(255,255,255,0.06)', color: sc.text }}>{job.status}</span>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {dueBadge && (
            <span style={{ fontSize: '8px', fontWeight: '700', padding: '1px 5px', borderRadius: '3px', background: 'rgba(255,255,255,0.06)', color: dueBadge.color, border: `1px solid ${dueBadge.color}44` }}>
              {dueBadge.label}
            </span>
          )}
          {job.shifts > 1 && (
            <span style={{ fontSize: '8px', fontWeight: '700', padding: '1px 6px', borderRadius: '3px', background: 'rgba(139,92,246,0.15)', color: '#a78bfa', border: '1px solid rgba(139,92,246,0.35)', display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
              <i className="fa-solid fa-moon" style={{ fontSize: '7px' }} />{job.shifts}×
            </span>
          )}
        </div>
      </div>
      {hasPendingChange && (
        <div style={{ fontSize: '9px', color: '#fbbf24', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>⚠</span><span>Awaiting setter acknowledgement</span>
        </div>
      )}
      {wasAcknowledged && (
        <div style={{ fontSize: '9px', color: '#86efac', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>✓</span><span>Change acknowledged</span>
        </div>
      )}
    </div>
  );
}

// ── Schedule modal ────────────────────────────────────────────

export function ScheduleModal({ job, machines, moulds, parts, recipes, scheduled = [], closures = [], onSaved, onClose, hideDateField = false, planJobCount = 0, planSiblings = [] }) {
  const { toast } = useToast();
  const isNew = !job?.id;
  const todayStr = fmt(new Date());
  const [form, setForm] = useState({
    part_id:        job?.part_id        ? String(job.part_id)        : '',
    machine_id:     job?.machine_id     ? String(job.machine_id)     : '',
    mould_id:       job?.mould_id       ? String(job.mould_id)       : '',
    recipe_id:      job?.recipe_id      ? String(job.recipe_id)      : '',
    scheduled_date: job?.scheduled_date || todayStr,
    target_qty:     (() => {
      if (!job?.id && !job?.target_qty) return '';
      if (planSiblings.length > 1) {
        const total = planSiblings.reduce((s, j) => s + (j.target_qty || 0), 0);
        return total > 0 ? String(total) : (job?.target_qty ? String(job.target_qty) : '');
      }
      return job?.target_qty ? String(job.target_qty) : '';
    })(),
    shifts:         job?.shifts         ? String(job.shifts)         : '1',
    order_line_id:  job?.order_line_id  ? String(job.order_line_id)  : '',
    notes:          job?.notes          || '',
  });
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [cycleTime, setCycleTime]     = useState('');
  const [cycleSource, setCycleSource] = useState(''); // 'golden' | 'part' | 'manual'
  const [openLines, setOpenLines]     = useState([]);
  const [showMouldInfo, setShowMouldInfo] = useState(false);
  const navigate = useNavigate();

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  // Load open order lines when part changes — only clear the selection; list is always loaded
  function onPartChange(partId) {
    const part = parts.find(p => String(p.id) === partId);
    setForm(f => ({
      ...f,
      part_id:       partId,
      mould_id:      part?.mould_id ? String(part.mould_id) : f.mould_id,
      recipe_id:     '',
      order_line_id: '',
    }));
    setCycleTime('');
    setCycleSource('');
  }

  // Link an order line — auto-fill remaining qty into target_qty
  function onOrderLineChange(lineId) {
    const line = openLines.find(l => String(l.id) === lineId);
    setForm(f => ({
      ...f,
      order_line_id: lineId,
      target_qty:    line ? String(line.remaining) : f.target_qty,
    }));
  }

  // Load all open order lines on mount (not filtered by part — director can link to any order)
  useEffect(() => {
    api.get('/api/v1/orders/open-lines')
      .then(r => setOpenLines(r.data || []))
      .catch(() => {});
  }, []); // eslint-disable-line

  // Auto-fetch golden run cycle time; fall back to part's target_cycle_s
  useEffect(() => {
    if (!form.mould_id) return;
    const params = new URLSearchParams({ mould_id: form.mould_id });
    if (form.recipe_id) params.set('recipe_id', form.recipe_id);
    api.get(`/api/v1/jobs/golden-cycle?${params}`)
      .then(r => {
        if (cycleSource === 'manual') return;
        if (r.data?.cycle_time_s) {
          setCycleTime(String(r.data.cycle_time_s));
          setCycleSource('golden');
        } else {
          const part = parts.find(p => String(p.id) === form.part_id);
          if (part?.target_cycle_s) {
            setCycleTime(String(part.target_cycle_s));
            setCycleSource('part');
          } else {
            setCycleTime('');
            setCycleSource('');
          }
        }
      })
      .catch(() => {});
  }, [form.mould_id, form.recipe_id]); // eslint-disable-line

  // Auto-select recipe when none is chosen: prefer the primary recipe, else auto-pick if only one linked
  useEffect(() => {
    if (form.recipe_id) return;
    const part = parts.find(p => String(p.id) === form.part_id);
    const partRecipes = part?.part_recipes || [];
    if (partRecipes.length === 0) return;
    const primary = partRecipes.find(pr => pr.is_primary);
    const pick = primary ?? (partRecipes.length === 1 ? partRecipes[0] : null);
    if (pick?.recipe?.id) setForm(f => ({ ...f, recipe_id: String(pick.recipe.id) }));
  }, [form.part_id]); // eslint-disable-line

  // Compute auto-plan preview using the same capacity model as the scheduling
  // engine, so a job planned here needs the same number of days as one proposed
  // automatically.
  let autoPlanData = null;
  if (form.target_qty && cycleTime) {
    const ct = parseFloat(cycleTime);
    const totalQty = parseInt(form.target_qty);
    if (ct > 0 && totalQty > 0) {
      const mould   = moulds.find(m => String(m.id) === form.mould_id);
      const machine = machines.find(m => String(m.id) === form.machine_id);
      const shifts  = parseInt(form.shifts) || 1;
      const cap     = dailyCapacity({ machine, mould, cycleS: ct, shifts });

      if (cap.partsPerDay > 0) {
        // Day one pays the changeover, but only on a run that spans more than one
        // day — a single-day job gets full capacity. Same rule as the engine.
        const isMultiDay = totalQty > cap.partsPerDay;
        const day1Parts  = isMultiDay && cap.changeoverMin > 0 ? cap.firstDayParts : cap.partsPerDay;
        const daysNeeded = day1Parts >= totalQty
          ? 1
          : 1 + Math.ceil((totalQty - day1Parts) / cap.partsPerDay);

        if (daysNeeded > 365) {
          autoPlanData = { tooLong: true };
        } else {
          const startD = form.scheduled_date
            ? new Date(form.scheduled_date + 'T12:00:00')
            : new Date();
          const hols = { ...buildHolidays(startD), ...closuresToMap(closures) };
          const workingDays = [];
          let cur = new Date(startD);
          let safety = 0;
          while (workingDays.length < daysNeeded && safety < 500) {
            const ds = fmt(cur);
            if (!isWeekendStr(ds) && !hols[ds]) workingDays.push(ds);
            cur = addDays(cur, 1);
            safety++;
          }
          const entries = [];
          let remaining = totalQty;
          workingDays.forEach((day, i) => {
            const capToday = i === 0 ? day1Parts : cap.partsPerDay;
            const dayQty   = Math.min(capToday, remaining);
            entries.push({ date: day, qty: dayQty, reduced: i === 0 && capToday < cap.partsPerDay });
            remaining -= dayQty;
          });
          autoPlanData = {
            ...cap,
            cycleS: ct, shifts, daysNeeded, entries, day1Parts, isMultiDay,
            machineName: machine ? machineName(machine) : null,
          };
        }
      }
    }
  }

  // What saving would push, and what that makes late -- asked before saving
  // (the backlog, 20 Oct 2026: placing work into a full board pushes other
  // orders, and the director should see which, and whether any then misses
  // its cut-off). New runs only; the same days the save sends.
  const [pushes, setPushes] = useState(null);
  const pushDates = isNew && autoPlanData && !autoPlanData.tooLong && form.machine_id ? autoPlanData.entries.map(e => e.date).join(',') : '';
  useEffect(() => {
    setPushes(null);
    if (!pushDates) return undefined;
    let alive = true;
    const t = setTimeout(() => {
      api.post('/api/v1/schedule/make-room/preview-new', { machine_id: Number(form.machine_id), dates: pushDates.split(',') })
        .then(r => { if (alive) setPushes(r.data); })
        .catch(() => { if (alive) setPushes(null); });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [pushDates, form.machine_id]);

  function save() {
    if (!form.part_id)        { setError('Part is required');    return; }
    if (!form.machine_id)     { setError('Machine is required'); return; }
    if (!form.scheduled_date) { setError('Date is required');    return; }
    if (autoPlanData?.tooLong) { setError('Plan exceeds 1 year — check cycle time and qty'); return; }
    setSaving(true);

    const isMultiDayPlan = !isNew && job?.plan_id && planJobCount > 1;

    // autoPlanData / re-plan path — recalculates days from qty + cycle time.
    // Works for new jobs, single-day edits, AND multi-day plans (deletes all siblings first).
    if (autoPlanData && !autoPlanData.tooLong) {
      const base = {
        part_id:       parseInt(form.part_id),
        machine_id:    parseInt(form.machine_id),
        mould_id:      form.mould_id      ? parseInt(form.mould_id)      : null,
        recipe_id:     form.recipe_id     ? parseInt(form.recipe_id)     : null,
        order_line_id: form.order_line_id ? parseInt(form.order_line_id) : null,
        shifts:        parseInt(form.shifts) || 1,
        notes:         form.notes || null,
      };
      const jobs = autoPlanData.entries.map(e => ({ ...base, scheduled_date: e.date, target_qty: e.qty }));
      const doBatch = async () => {
        try {
          // Whatever is in the new run's way is pushed by the server's one
          // cascade, in the same request that creates the run -- only as far
          // as it has to, stepping over anything running or finished. Until
          // 25 Sep this pushed rigidly from here, as a separate request.
          const r = await api.post('/api/v1/schedule/batch', { jobs, make_room: true });
          for (const n of r.data?.notes || []) {
            if (n.kind === 'late') toast.warning(n.says, 8000);
            else toast.info(n.says, 7000);
          }
          onSaved(r.data?.rows?.[0]?.id);
        } catch (err) {
          setError(err.response?.data?.error || 'Save failed');
          setSaving(false);
        }
      };
      if (!isNew) {
        // The run's days are replaced in one step on the server: it keeps its
        // id, whatever is in the way moves only as far as it has to, and the
        // history gets one entry. Until 25 Sep this deleted each day in its own
        // request and then created the run again under a new id -- three
        // history entries, a split history, and a deleted run if the second
        // half failed.
        const currentIds = isMultiDayPlan && planSiblings.length > 0
          ? planSiblings.map(s => s.id)
          : [job.id];
        api.post('/api/v1/schedule/replan', { plan_id: job.plan_id || null, remove_ids: currentIds, jobs },
          withExpect(isMultiDayPlan && planSiblings.length > 0 ? planSiblings : [job]))
          .then(r => {
            for (const n of r.data?.notes || []) {
              if (n.kind === 'late') toast.warning(n.says, 8000);
              else toast.info(n.says, 7000);
            }
            onSaved(r.data?.rows?.[0]?.id);
          })
          .catch(err => { setError(err.response?.data?.error || 'Save failed'); setSaving(false); });
      } else {
        doBatch();
      }
      return;
    }

    // Editing a multi-day plan: push shared fields to all days; shift dates if start changed
    if (isMultiDayPlan) {
      const sharedFields = {
        part_id:       parseInt(form.part_id),
        machine_id:    parseInt(form.machine_id),
        mould_id:      form.mould_id      ? parseInt(form.mould_id)      : null,
        recipe_id:     form.recipe_id     ? parseInt(form.recipe_id)     : null,
        order_line_id: form.order_line_id ? parseInt(form.order_line_id) : null,
        shifts:        parseInt(form.shifts) || 1,
        notes:         form.notes || null,
      };
      const offsetDays = Math.round(
        (new Date(form.scheduled_date + 'T12:00:00') - new Date(job.scheduled_date + 'T12:00:00')) / 86400000
      );
      // The run's details, then -- only once they are saved -- its move: two
      // things, each recorded once, never racing each other (6 Oct 2026).
      let moves = null;
      if (offsetDays !== 0 && planSiblings.length > 0) {
        const newStart = new Date(form.scheduled_date + 'T12:00:00');
        const hols = { ...buildHolidays(newStart), ...closuresToMap(closures) };
        const sorted = [...planSiblings].sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
        moves = sorted.map((s, n) => ({
          id: s.id,
          scheduled_date: getNthWorkingDay(newStart, n, hols),
        }));
      }
      api.put(`/api/v1/schedule/plan/${job.plan_id}`, sharedFields)
        .then(async (r) => {
          if (moves) await api.put('/api/v1/schedule/batch-move', { moves });
          onSaved(r.data?.[0]?.id);
        })
        .catch(err => { setError(err.response?.data?.error || 'Save failed'); setSaving(false); });
      return;
    }

    const payload = {
      part_id:        parseInt(form.part_id),
      machine_id:     parseInt(form.machine_id),
      mould_id:       form.mould_id      ? parseInt(form.mould_id)      : null,
      recipe_id:      form.recipe_id     ? parseInt(form.recipe_id)     : null,
      order_line_id:  form.order_line_id ? parseInt(form.order_line_id) : null,
      scheduled_date: form.scheduled_date,
      target_qty:     form.target_qty ? parseInt(form.target_qty) : null,
      shifts:         parseInt(form.shifts) || 1,
      notes:          form.notes || null,
    };
    const req = isNew
      ? api.post('/api/v1/schedule', payload)
      : api.put(`/api/v1/schedule/${job.id}`, payload, withExpect([job]));
    req.then(r => onSaved(r.data?.id)).catch(err => {
      setError(err.response?.data?.error || 'Save failed');
      setSaving(false);
    });
  }

  // "Save without re-planning": update shared fields on all plan days, keep existing day count.
  // Used when the user wants to record a change (e.g. shifts) without recalculating duration.
  function saveKeepDays() {
    if (!form.part_id)        { setError('Part is required');    return; }
    if (!form.machine_id)     { setError('Machine is required'); return; }
    if (!form.scheduled_date) { setError('Date is required');    return; }
    setSaving(true);
    const sharedFields = {
      part_id:       parseInt(form.part_id),
      machine_id:    parseInt(form.machine_id),
      mould_id:      form.mould_id      ? parseInt(form.mould_id)      : null,
      recipe_id:     form.recipe_id     ? parseInt(form.recipe_id)     : null,
      order_line_id: form.order_line_id ? parseInt(form.order_line_id) : null,
      shifts:        parseInt(form.shifts) || 1,
      notes:         form.notes || null,
    };
    const offsetDays = Math.round(
      (new Date(form.scheduled_date + 'T12:00:00') - new Date(job.scheduled_date + 'T12:00:00')) / 86400000
    );
    // Distribute the entered total evenly across all plan siblings.
    // The form shows the plan total, so we must not assign it to one day only.
    // Sent with the details, so one save is one change in the history -- it
    // was one request per day, all at once (6 Oct 2026).
    const body = { ...sharedFields };
    if (form.target_qty !== '' && form.target_qty != null) {
      const total   = parseInt(form.target_qty) || 0;
      const siblings = planSiblings.length > 0 ? planSiblings : [job];
      const n       = siblings.length;
      const perDay  = Math.floor(total / n);
      const rem     = total - perDay * n;
      body.day_qty = Object.fromEntries(siblings.map((s, i) => [s.id, perDay + (i === n - 1 ? rem : 0)]));
    }
    let moves = null;
    if (offsetDays !== 0 && planSiblings.length > 0) {
      const newStart = new Date(form.scheduled_date + 'T12:00:00');
      const hols = { ...buildHolidays(newStart), ...closuresToMap(closures) };
      const sorted = [...planSiblings].sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));
      moves = sorted.map((s, n) => ({
        id: s.id,
        scheduled_date: getNthWorkingDay(newStart, n, hols),
      }));
    }
    api.put(`/api/v1/schedule/plan/${job.plan_id}`, body)
      .then(async (r) => {
        if (moves) await api.put('/api/v1/schedule/batch-move', { moves });
        onSaved(r.data?.[0]?.id);
      })
      .catch(err => { setError(err.response?.data?.error || 'Save failed'); setSaving(false); });
  }

  const selectedMould = moulds.find(m => String(m.id) === form.mould_id);
  const selectedPart = parts.find(p => String(p.id) === form.part_id);
  const mouldLocked = !!selectedPart?.mould_id;
  const partRecipeIds = new Set((selectedPart?.part_recipes || []).map(pr => pr.recipe?.id).filter(Boolean));
  const filteredRecipes = partRecipeIds.size > 0 ? recipes.filter(r => partRecipeIds.has(r.id)) : recipes;
  const linkedLine = openLines.find(l => String(l.id) === form.order_line_id);

  // Exclude all siblings of the current plan — they share the same machine/mould legitimately
  const planSiblingIds = new Set(planSiblings.map(s => s.id));
  // A new run's save pushes what is in its way (make_room): those runs are not
  // in the way once it is saved (20 Oct 2026). Running or finished ones cannot
  // move and still count.
  const pushedAway = new Set(isNew && pushes?.ok ? (pushes.moves || []).map(m => m.id) : []);
  const pushesLoading = isNew && !!pushDates && !pushes;
  // The run against its order's cut-off (the Wednesday of its delivery week).
  const lineCutoff = linkedLine?.delivery_week && linkedLine?.delivery_year
    ? fmt(addDays(new Date(weekToFriday(linkedLine.delivery_week, linkedLine.delivery_year) + 'T12:00:00'), -2)) : null;
  const runLastDay = autoPlanData?.entries?.length ? autoPlanData.entries[autoPlanData.entries.length - 1].date : null;
  const runLate = isNew && lineCutoff && runLastDay && runLastDay > lineCutoff;

  const conflicts = (form.machine_id && form.scheduled_date)
    ? scheduled.filter(j =>
        String(j.machine_id) === form.machine_id &&
        j.scheduled_date === form.scheduled_date &&
        j.id !== job?.id &&
        !planSiblingIds.has(j.id) &&
        !pushedAway.has(j.id) &&
        j.status !== 'cancelled'
      )
    : [];
  // When editing a multi-day plan without changing the start date, use the actual sibling dates.
  // autoPlanData recomputes from form.scheduled_date (the clicked sibling, not necessarily day 1),
  // generating dates that may extend past the original plan and produce false mould conflicts.
  const isEditingMultiDayPlan = !isNew && planSiblings.length > 1;
  const planDates = (isEditingMultiDayPlan && form.scheduled_date === job?.scheduled_date)
    ? planSiblings.map(s => s.scheduled_date)
    : (autoPlanData?.entries?.map(e => e.date) || (form.scheduled_date ? [form.scheduled_date] : []));
  const mouldConflictDates = form.mould_id
    ? planDates.filter(date =>
        scheduled.some(j =>
          String(j.mould_id) === form.mould_id &&
          j.scheduled_date === date &&
          j.id !== job?.id &&
          !planSiblingIds.has(j.id) &&
          !pushedAway.has(j.id) &&
          j.status !== 'cancelled'
        )
      )
    : [];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose} onKeyDown={e => e.key === 'Escape' && onClose()}>
      <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '16px', width: '90%', maxWidth: '480px', maxHeight: '85vh', overflowY: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 20px 16px', borderBottom: '1px solid #2a3040', position: 'sticky', top: 0, background: '#1e2329', zIndex: 1 }}>
          <div style={{ fontSize: '16px', fontWeight: '700' }}>{isNew ? 'Schedule Job' : 'Edit Scheduled Job'}</div>
          <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '16px', cursor: 'pointer' }} onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {error && <div style={{ background: '#7f1d1d', color: '#fca5a5', borderRadius: '8px', padding: '10px 14px', fontSize: '13px' }}>{error}</div>}

          {!isNew && planJobCount > 1 && (
            <div style={{ background: 'rgba(20,184,166,0.07)', border: '1px solid rgba(20,184,166,0.2)', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#5eead4' }}>
              This is a {planJobCount}-day plan — changes to recipe, machine, mould, part and notes will apply to all {planJobCount} days.
            </div>
          )}

          {conflicts.length > 0 && !pushesLoading && (
            <div style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#fca5a5' }}>
              <div style={{ fontWeight: '700', marginBottom: '4px' }}>
                <i className="fa-solid fa-circle-xmark" style={{ marginRight: '6px' }} />
                Machine already occupied on this date — cannot save
              </div>
              {conflicts.map(c => (
                <div key={c.id} style={{ fontSize: '11px', color: '#ef4444', marginTop: '3px' }}>
                  {[c.parts?.name, c.order_number].filter(Boolean).join(' · ') || c.moulds?.ref || '—'}
                </div>
              ))}
            </div>
          )}

          {mouldConflictDates.length > 0 && !pushesLoading && (
            <div style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '10px 14px', fontSize: '12px', color: '#fca5a5' }}>
              <div style={{ fontWeight: '700', marginBottom: '4px' }}>
                <i className="fa-solid fa-circle-xmark" style={{ marginRight: '6px' }} />
                Mould {selectedMould?.ref || `#${form.mould_id}`} is already in use on {mouldConflictDates.length === 1 ? mouldConflictDates[0] : `${mouldConflictDates.length} days`}
              </div>
              {mouldConflictDates.map(date => {
                const conflicting = scheduled.filter(j =>
                  String(j.mould_id) === form.mould_id &&
                  j.scheduled_date === date &&
                  j.id !== job?.id &&
                  !planSiblingIds.has(j.id) &&
                  !pushedAway.has(j.id) &&
                  j.status !== 'cancelled'
                );
                return conflicting.map(c => (
                  <div key={c.id} style={{ fontSize: '11px', color: '#ef4444', marginTop: '3px' }}>
                    {date} · {c.machines?.name || `Machine #${c.machine_id}`} · {[c.parts?.name, c.order_number].filter(Boolean).join(' · ') || '—'}
                  </div>
                ));
              })}
              <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px' }}>
                A mould can only run in one machine at a time.
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={st.mField}>
              <label style={st.mLabel}>Part *</label>
              <select style={st.mInput} value={form.part_id} onChange={e => onPartChange(e.target.value)}>
                <option value="">— Select part —</option>
                {parts.map(p => <option key={p.id} value={p.id}>{p.name}{p.part_number ? ` · ${p.part_number}` : ''}</option>)}
              </select>
            </div>
            <div style={st.mField}>
              <label style={st.mLabel}>Link to Order <span style={{ color: '#4b5563', fontWeight: '400' }}>(optional)</span></label>
              {(() => {
                const partLines  = openLines.filter(l => form.part_id && String(l.part_id) === form.part_id);
                const otherLines = openLines.filter(l => !form.part_id || String(l.part_id) !== form.part_id);
                return (
                  <select style={st.mInput} value={form.order_line_id} onChange={e => onOrderLineChange(e.target.value)}>
                    <option value="">— No order link —</option>
                    {partLines.length > 0 && (
                      <optgroup label={selectedPart?.name || 'This part'}>
                        {partLines.map(l => (
                          <option key={l.id} value={l.id}>
                            W{l.delivery_week}/{l.delivery_year} · {l.customer} · {l.remaining.toLocaleString()} pcs
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {otherLines.length > 0 && (
                      <optgroup label="Other open orders">
                        {otherLines.map(l => (
                          <option key={l.id} value={l.id}>
                            {l.part_name} · W{l.delivery_week}/{l.delivery_year} · {l.customer} · {l.remaining.toLocaleString()} pcs
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                );
              })()}
              {openLines.length === 0 && (
                <div style={{ fontSize: '10px', color: '#4b5563', marginTop: '3px' }}>No open orders</div>
              )}
            </div>
          </div>
          {linkedLine && (
            <div style={{ fontSize: '11px', color: '#14b8a6', background: 'rgba(20,184,166,0.06)', border: '1px solid rgba(20,184,166,0.2)', borderRadius: '6px', padding: '6px 10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: '600' }}>{selectedPart?.name}</span>
              <span style={{ color: '#4b5563' }}>·</span>
              <span>W{linkedLine.delivery_week}/{linkedLine.delivery_year}</span>
              <span style={{ color: '#4b5563' }}>·</span>
              <span>{linkedLine.customer}</span>
              <span style={{ color: '#4b5563' }}>·</span>
              <span>{linkedLine.remaining.toLocaleString()} pcs remaining</span>
            </div>
          )}
          <div style={st.mField}>
            <label style={st.mLabel}>Machine *</label>
            <select style={st.mInput} value={form.machine_id} onChange={e => set('machine_id', e.target.value)}>
              <option value="">— Select machine —</option>
              {machines.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
            </select>
          </div>
          <div style={st.mField}>
            <label style={{ ...st.mLabel, display: 'flex', alignItems: 'center', gap: '6px' }}>
              Mould
              {mouldLocked && (
                <span
                  style={{ position: 'relative', lineHeight: 1 }}
                  onMouseEnter={() => setShowMouldInfo(true)}
                  onMouseLeave={() => setShowMouldInfo(false)}
                >
                  <i className="fa-solid fa-circle-info" style={{ color: '#6b7280', fontSize: '10px', cursor: 'help' }} />
                  {showMouldInfo && (
                    <div style={{ position: 'absolute', bottom: 'calc(100% + 6px)', left: '50%', transform: 'translateX(-50%)', background: '#0f1117', border: '1px solid #2a3040', borderRadius: '8px', padding: '10px 12px', width: '230px', fontSize: '11px', color: '#9ca3af', zIndex: 200, lineHeight: 1.55, boxShadow: '0 4px 16px rgba(0,0,0,0.5)' }}>
                      Auto-set from the part's linked mould. To change it, update the{' '}
                      <span onClick={() => navigate(`/admin/parts/${form.part_id}`)} style={{ color: '#14b8a6', cursor: 'pointer', textDecoration: 'underline' }}>Part</span>
                      {' '}or the{' '}
                      <span onClick={() => navigate('/admin/moulds')} style={{ color: '#14b8a6', cursor: 'pointer', textDecoration: 'underline' }}>Mould</span>.
                    </div>
                  )}
                </span>
              )}
            </label>
            <select
              style={{ ...st.mInput, opacity: mouldLocked ? 0.45 : 1, cursor: mouldLocked ? 'not-allowed' : undefined }}
              value={form.mould_id}
              onChange={e => set('mould_id', e.target.value)}
              disabled={mouldLocked}
            >
              <option value="">— Select mould —</option>
              {moulds.map(m => <option key={m.id} value={m.id}>{m.ref} — {m.name}</option>)}
            </select>
          </div>
          <div style={st.mField}>
            <label style={st.mLabel}>Recipe</label>
            <select style={st.mInput} value={form.recipe_id} onChange={e => set('recipe_id', e.target.value)}>
              <option value="">— Select recipe —</option>
              {filteredRecipes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            {form.part_id && partRecipeIds.size === 0 && recipes.length > 0 && (
              <div style={{ fontSize: '10px', color: '#4b5563', marginTop: '3px' }}>No recipes linked to this part — showing all</div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: hideDateField ? '1fr' : '1fr 1fr', gap: '12px' }}>
            {!hideDateField && (
              <div style={st.mField}>
                <label style={st.mLabel}>Start date *</label>
                <input type="date" style={st.mInput} value={form.scheduled_date} onChange={e => set('scheduled_date', e.target.value)} />
              </div>
            )}
            <div style={st.mField}>
              <label style={st.mLabel}>Target qty (pcs)</label>
              <input type="number" style={st.mInput} value={form.target_qty} onChange={e => set('target_qty', e.target.value)} min="0" placeholder="0" />
            </div>
          </div>

          <div style={st.mField}>
            <label style={st.mLabel}>Shifts per day</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {['1', '2', '3'].map(n => (
                <button key={n} onClick={() => set('shifts', n)}
                  style={{ flex: 1, padding: '9px 8px', border: `1px solid ${form.shifts === n ? '#14b8a6' : '#2a3040'}`, borderRadius: '6px', background: form.shifts === n ? 'rgba(20,184,166,0.12)' : '#0f1117', color: form.shifts === n ? '#14b8a6' : '#9ca3af', cursor: 'pointer', fontFamily: 'system-ui', fontSize: '12px', fontWeight: '600', transition: 'all 0.15s' }}>
                  {n} × 8h
                </button>
              ))}
            </div>
          </div>

          <div style={{ background: '#0f1117', border: '1px solid #2a3040', borderRadius: '8px', padding: '12px' }}>
              <div style={st.mField}>
                <label style={{ ...st.mLabel, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Cycle time (s/shot)</span>
                  {cycleSource === 'golden' && <span style={{ fontSize: '9px', color: '#fbbf24', fontWeight: '600' }}>⭐ From golden run</span>}
                  {cycleSource === 'part' && <span style={{ fontSize: '9px', color: '#60a5fa', fontWeight: '600' }}>◎ From part spec</span>}
                  {cycleSource === 'manual' && <span style={{ fontSize: '9px', color: '#9ca3af' }}>Manual</span>}
                  {!cycleSource && form.mould_id && <span style={{ fontSize: '9px', color: '#6b7280' }}>No golden run found</span>}
                </label>
                <input
                  type="number" style={st.mInput} value={cycleTime}
                  onChange={e => { setCycleTime(e.target.value); setCycleSource('manual'); }}
                  min="1" step="0.5" placeholder="e.g. 28.5"
                />
                {selectedMould?.cavities > 1 && (
                  <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '3px' }}>
                    {selectedMould.cavities}-cavity mould — {selectedMould.cavities} parts per shot
                  </div>
                )}
              </div>

              {autoPlanData && !autoPlanData.tooLong && (
                <div style={{ background: '#1a2030', border: '1px solid #2a3040', borderRadius: '6px', padding: '10px 12px', marginTop: '10px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#14b8a6', marginBottom: '8px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <span>{autoPlanData.partsPerDay.toLocaleString()} pcs/day</span>
                    <span style={{ color: '#4b5563' }}>·</span>
                    <span>{autoPlanData.daysNeeded} working day{autoPlanData.daysNeeded !== 1 ? 's' : ''}</span>
                    {autoPlanData.cavities > 1 && <><span style={{ color: '#4b5563' }}>·</span><span style={{ color: '#6b7280' }}>{autoPlanData.cavities} cav</span></>}
                  </div>

                  <CapacityBreakdown plan={autoPlanData} totalQty={parseInt(form.target_qty)} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '160px', overflowY: 'auto' }}>
                    {autoPlanData.entries.map(e => (
                      <div key={e.date} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', padding: '3px 6px', background: 'rgba(255,255,255,0.03)', borderRadius: '3px' }}>
                        <span style={{ color: '#9ca3af' }}>
                          {new Date(e.date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                          {e.reduced && <span style={{ color: '#f59e0b', fontSize: '10px', marginLeft: '6px' }}>changeover</span>}
                        </span>
                        <span style={{ color: '#f0f2f5', fontVariantNumeric: 'tabular-nums' }}>{e.qty.toLocaleString()} pcs</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {pushes && (
                <div style={{ background: '#1a2030', border: `1px solid ${(pushes.shifted || []).some(sh => sh.late_by > 0 && !sh.was_late) ? '#ef444466' : '#2a3040'}`, borderRadius: '6px', padding: '10px 12px', marginTop: '10px', fontSize: '12px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#f59e0b', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>What saving does to the board</div>
                  {runLate && (
                    <div style={{ color: '#fca5a5', marginBottom: '6px' }}>
                      <b>This run ends {new Date(runLastDay + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}, after its cut-off</b> ({new Date(lineCutoff + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}, Wk {linkedLine.delivery_week}). Start it earlier, run it on more shifts, or split it over two machines.
                    </div>
                  )}
                  {pushes.ok === false
                    ? <div style={{ color: '#fca5a5' }}>{pushes.says}</div>
                    : !(pushes.shifted || []).length
                      ? <div style={{ color: '#9ca3af' }}>Nothing else moves: these days are free.</div>
                      : <>
                          <div style={{ color: '#d1d5db', marginBottom: '4px' }}>{pushes.shifted.length} run{pushes.shifted.length === 1 ? '' : 's'} move{pushes.shifted.length === 1 ? 's' : ''} later, each as far as it has to:</div>
                          {pushes.shifted.map(sh => {
                            const d = (x) => new Date(x + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                            const late = sh.late_by > 0 && !sh.was_late;
                            return (
                              <div key={sh.key} style={{ padding: '3px 0', color: late ? '#fca5a5' : '#d1d5db' }}>
                                {sh.week ? <b>W{sh.week} </b> : null}{sh.part}: {d(sh.from[0])} – {d(sh.from[1])} → <b>{d(sh.to[0])} – {d(sh.to[1])}</b>
                                {late
                                  ? <span> · <b>misses its cut-off</b> ({d(sh.deadline)}) by {sh.late_by} working day{sh.late_by === 1 ? '' : 's'}</span>
                                  : sh.was_late ? <span style={{ color: '#9ca3af' }}> · was already late</span>
                                  : sh.deadline ? <span style={{ color: '#6b7280' }}> · still makes {d(sh.deadline)}</span> : null}
                              </div>
                            );
                          })}
                        </>}
                </div>
              )}
              {autoPlanData?.tooLong && (
                <div style={{ fontSize: '11px', color: '#fca5a5', padding: '6px 10px', background: 'rgba(127,29,29,0.2)', borderRadius: '4px', marginTop: '10px' }}>
                  Plan would exceed 1 year — check cycle time and target qty
                </div>
              )}

              {!isNew && planSiblings.length > 1 && !autoPlanData && (
                <div style={{ background: '#1a2030', border: '1px solid #2a3040', borderRadius: '6px', padding: '10px 12px', marginTop: '10px' }}>
                  <div style={{ fontSize: '11px', fontWeight: '700', color: '#14b8a6', marginBottom: '8px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <span>{planSiblings.length} working day{planSiblings.length !== 1 ? 's' : ''}</span>
                    <span style={{ color: '#4b5563' }}>·</span>
                    <span style={{ color: '#6b7280' }}>current plan breakdown</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', maxHeight: '160px', overflowY: 'auto' }}>
                    {[...planSiblings].sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date)).map(s => (
                      <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', padding: '3px 6px', background: s.id === job?.id ? 'rgba(20,184,166,0.07)' : 'rgba(255,255,255,0.03)', borderRadius: '3px' }}>
                        <span style={{ color: s.id === job?.id ? '#5eead4' : '#9ca3af' }}>
                          {new Date(s.scheduled_date + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                          {s.id === job?.id && <span style={{ marginLeft: '5px', fontSize: '9px', color: '#14b8a6' }}>← this</span>}
                        </span>
                        <span style={{ color: '#f0f2f5', fontVariantNumeric: 'tabular-nums' }}>{(s.target_qty || 0).toLocaleString()} pcs</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

          <div style={st.mField}>
            <label style={st.mLabel}>Notes</label>
            <textarea style={{ ...st.mInput, minHeight: '60px', resize: 'vertical' }} value={form.notes} onChange={e => set('notes', e.target.value)} />
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', padding: '16px 20px', borderTop: '1px solid #2a3040', position: 'sticky', bottom: 0, background: '#1e2329', flexWrap: 'wrap' }}>
          <button style={{ background: '#1e2329', color: '#9ca3af', border: '1px solid #2a3040', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', fontFamily: 'system-ui', flexShrink: 0 }} onClick={onClose}>Cancel</button>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {/* Secondary: keep existing days, just push field changes to all siblings */}
            {autoPlanData && !autoPlanData.tooLong && !isNew && job?.plan_id && planJobCount > 1 && (
              <button
                style={{ background: 'transparent', color: '#9ca3af', border: '1px solid #4b5563', borderRadius: '8px', padding: '10px 16px', fontSize: '12px', fontWeight: '600', fontFamily: 'system-ui',
                  opacity: (saving || conflicts.length > 0 || mouldConflictDates.length > 0) ? 0.4 : 1,
                  cursor: (saving || conflicts.length > 0 || mouldConflictDates.length > 0) ? 'not-allowed' : 'pointer' }}
                disabled={saving || conflicts.length > 0 || mouldConflictDates.length > 0}
                title={`Apply field changes to all ${planJobCount} days without recalculating duration`}
                onClick={saveKeepDays}>
                Save without re-planning
              </button>
            )}
            <button
              style={{ background: '#14b8a6', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 24px', fontSize: '13px', fontWeight: '700', fontFamily: 'system-ui',
                opacity: (saving || pushesLoading || conflicts.length > 0 || mouldConflictDates.length > 0) ? 0.4 : 1,
                cursor: (saving || pushesLoading || conflicts.length > 0 || mouldConflictDates.length > 0) ? 'not-allowed' : 'pointer' }}
              disabled={saving || pushesLoading || conflicts.length > 0 || mouldConflictDates.length > 0}
              onClick={save}>
              {saving ? 'Saving...' : pushesLoading ? 'Working out what it pushes…' : autoPlanData && !autoPlanData.tooLong
                ? `${isNew ? 'Schedule' : 'Re-plan'} (${autoPlanData.daysNeeded}d)`
                : isNew ? 'Schedule' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Shared ────────────────────────────────────────────────────

function ChangeoverBadge({ info }) {
  if (!info) return null;
  return (
    <div style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)', borderRadius: '4px', padding: '3px 5px', fontSize: '9px', color: '#fbbf24', lineHeight: 1.3 }}>
      ⟳ {info.total} min · {info.fromRef} → {info.toRef}
    </div>
  );
}

// Another base material, or lighter after darker in the colour order.
function PurgeBadge({ info }) {
  if (!info) return null;
  return (
    <div style={{ background: 'rgba(167,139,250,0.08)', border: '1px solid rgba(167,139,250,0.25)', borderRadius: '4px', padding: '3px 5px', fontSize: '9px', color: '#c4b5fd', lineHeight: 1.3 }}
      title={info.kind === 'material' ? 'The barrel is purged before the new material.' : 'Lighter after darker in the colour order (Admin → Settings).'}>
      <i className="fa-solid fa-fill-drip" style={{ marginRight: '4px' }} />
      {info.kind === 'material' ? 'Material change' : 'Purge'} · {info.from} → {info.to}
    </div>
  );
}

function EmptyState({ msg }) {
  return (
    <div style={{ background: '#1e2329', border: '1px solid #2a3040', borderRadius: '10px', padding: '48px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
      {msg}
    </div>
  );
}

// ── Live Job Panel ────────────────────────────────────────────
function LiveJobPanel({ jobId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setFetchError(null);
    api.get(`/api/v1/jobs/${jobId}/live-summary`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(err => { setFetchError(err.response?.data?.error || 'Failed to load job data'); setLoading(false); });
    const iv = setInterval(() => {
      api.get(`/api/v1/jobs/${jobId}/live-summary`)
        .then(r => setData(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(iv);
  }, [jobId]);

  const activeMs = data?.efficiency?.active_ms || 0;
  const elapsedH = Math.floor(activeMs / 3600000);
  const elapsedM = Math.floor((activeMs % 3600000) / 60000);
  const elapsedStr = elapsedH > 0 ? `${elapsedH}h ${elapsedM}m` : `${elapsedM}m`;

  const pace = data?.production?.actual_rate_per_hour;
  const reqPace = data?.production?.required_rate_per_hour;
  const paceColor = !pace || !reqPace ? '#9ca3af'
    : pace >= reqPace ? '#86efac'
    : pace >= reqPace * 0.75 ? '#fbbf24'
    : '#fca5a5';

  const eff = data?.efficiency?.efficiency_pct;
  const effColor = eff == null ? '#9ca3af' : eff >= 85 ? '#86efac' : eff >= 70 ? '#fbbf24' : '#fca5a5';

  const drift = data?.weights?.drift_pct;
  const driftColor = drift == null ? '#9ca3af' : Math.abs(drift) <= 2 ? '#86efac' : Math.abs(drift) <= 5 ? '#fbbf24' : '#fca5a5';

  const chartData = (data?.weights?.entries || []).map(w => ({
    time: new Date(w.logged_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
    weight: parseFloat(w.weight_g),
  }));
  const targetWeight = data?.weights?.target_weight_g;

  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300 }} onClick={onClose} />
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: '480px',
        background: '#1e2329', borderLeft: '1px solid #2a3040', zIndex: 301, overflowY: 'auto',
        fontFamily: 'system-ui, sans-serif', color: '#f0f2f5',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderBottom: '1px solid #2a3040', position: 'sticky', top: 0, background: '#1e2329', zIndex: 1 }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '700' }}>Live Job</div>
            {data && <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              {data.job.part?.name || data.job.mould?.ref} · {data.job.machine?.name}
            </div>}
          </div>
          <button style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '20px', cursor: 'pointer' }} onClick={onClose}>✕</button>
        </div>

        {loading && <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#6b7280' }}>Loading…</div>}

        {!loading && fetchError && (
          <div style={{ margin: '24px', background: '#7f1d1d', color: '#fca5a5', borderRadius: '8px', padding: '16px', fontSize: '13px' }}>
            Error: {fetchError}<br /><span style={{ color: '#9ca3af', fontSize: '11px' }}>Job ID: {jobId}</span>
          </div>
        )}

        {!loading && data && (
          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

            {/* Header info */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <LiveStat label="Status" value={data.job.golden_run ? '⭐ Golden' : data.job.status} />
              <LiveStat label="Elapsed" value={elapsedStr} />
              <LiveStat label="Setter" value={data.job.setter_name || '—'} />
              <LiveStat label="Recipe" value={data.job.recipe?.name || '—'} />
            </div>

            {/* Order Progress — hero block */}
            {(() => {
              const pp = data.plan_production;
              const totalMade   = pp?.total_good_parts ?? data.production.good_parts;
              const orderTarget = pp?.target_parts     || data.production.target_parts;
              const pct         = orderTarget ? Math.min(100, Math.round(totalMade / orderTarget * 100)) : 0;
              return (
                <div style={{ background: '#0f1117', border: '1px solid #2a3040', borderRadius: '10px', padding: '16px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.08em', marginBottom: '10px' }}>
                    Order Progress
                    {pp && (
                      <span style={{ marginLeft: '8px', fontWeight: '400', textTransform: 'none', letterSpacing: 'normal', fontSize: '11px' }}>
                        · {pp.completed_shifts} of {pp.total_shifts} shifts done
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px', marginBottom: '10px' }}>
                    <span style={{ fontSize: '36px', fontWeight: '800', letterSpacing: '-0.02em', lineHeight: 1 }}>
                      {totalMade.toLocaleString()}
                    </span>
                    <span style={{ fontSize: '15px', color: '#6b7280' }}>
                      / {orderTarget ? orderTarget.toLocaleString() : '—'} pcs
                    </span>
                  </div>
                  {orderTarget > 0 && (
                    <>
                      <div style={{ background: '#1e2329', borderRadius: '4px', height: '10px', overflow: 'hidden', marginBottom: '8px' }}>
                        <div style={{ height: '100%', borderRadius: '4px', background: '#14b8a6', width: `${pct}%` }} />
                      </div>
                      <div style={{ fontSize: '11px', color: '#6b7280' }}>
                        {orderTarget - totalMade > 0
                          ? `${(orderTarget - totalMade).toLocaleString()} pcs remaining`
                          : '✓ Target reached'}
                      </div>
                    </>
                  )}
                </div>
              );
            })()}

            {/* This Shift */}
            <LiveSection title="This Shift">
              <div style={{ marginBottom: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '4px' }}>
                  <span style={{ color: '#6b7280' }}>Finished pcs</span>
                  <span style={{ fontWeight: '700' }}>
                    {data.production.good_parts}
                    {data.production.target_parts ? ` / ${data.production.target_parts.toLocaleString()}` : ''}
                  </span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <LiveStat label="Scrap" value={`${data.production.total_scrap} pcs`} valueColor={data.production.total_scrap > 0 ? '#fca5a5' : '#86efac'} />
                <LiveStat label="Scrap rate" value={data.production.scrap_rate != null ? `${data.production.scrap_rate}%` : '—'} valueColor={data.production.scrap_rate > 5 ? '#fca5a5' : '#86efac'} />
                {pace != null && <LiveStat label="Pace (pcs/h)" value={pace} valueColor={paceColor} />}
                {reqPace != null && <LiveStat label="Required pace" value={reqPace} />}
              </div>
            </LiveSection>

            {/* Weights */}
            <LiveSection title="Shot Weight">
              {chartData.length > 1 ? (
                <div style={{ height: '160px', marginBottom: '10px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: 0 }}>
                      <XAxis dataKey="time" tick={{ fontSize: 9, fill: '#6b7280' }} />
                      <YAxis tick={{ fontSize: 9, fill: '#6b7280' }} domain={['auto', 'auto']} />
                      <Tooltip contentStyle={{ background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', fontSize: '11px' }} />
                      {targetWeight && <ReferenceLine y={targetWeight} stroke="#14b8a6" strokeDasharray="4 2" />}
                      <Line type="monotone" dataKey="weight" stroke="#f0f2f5" dot={{ r: 2, fill: '#f0f2f5' }} strokeWidth={1.5} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '10px' }}>Not enough data to plot</div>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <LiveStat label="Latest" value={data.weights.latest ? `${data.weights.latest.weight_g}g` : '—'} />
                <LiveStat label="Target" value={targetWeight ? `${targetWeight}g` : '—'} />
                <LiveStat label="Drift" value={drift != null ? `${drift > 0 ? '+' : ''}${drift}%` : '—'} valueColor={driftColor} />
                <LiveStat label="Last logged" value={data.weights.minutes_since_last != null ? `${data.weights.minutes_since_last}m ago` : '—'}
                  valueColor={data.weights.overdue ? '#fca5a5' : '#86efac'} />
              </div>
            </LiveSection>

            {/* Quality */}
            <LiveSection title="Quality">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                <LiveStat label="Inspections passed" value={`${data.quality.pass_count} / ${data.quality.pass_count + data.quality.fail_count}`} />
                <LiveStat label="Pass rate" value={data.quality.pass_rate != null ? `${data.quality.pass_rate}%` : '—'} valueColor={data.quality.pass_rate < 95 ? '#fbbf24' : '#86efac'} />
              </div>
              {data.quality.defect_breakdown.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', marginBottom: '6px', letterSpacing: '0.06em' }}>Defect breakdown</div>
                  {data.quality.defect_breakdown.map(d => {
                    const total = data.quality.defect_breakdown.reduce((s, x) => s + x.count, 0);
                    const pct = Math.round(d.count / total * 100);
                    return (
                      <div key={d.type} style={{ marginBottom: '4px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '2px' }}>
                          <span>{d.type}</span>
                          <span style={{ color: '#9ca3af' }}>{d.count} ({pct}%)</span>
                        </div>
                        <div style={{ background: '#0f1117', borderRadius: '3px', height: '4px' }}>
                          <div style={{ height: '100%', borderRadius: '3px', background: '#ef4444', width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {data.quality.active_issues.length > 0 && (
                <div>
                  <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#fca5a5', marginBottom: '6px', letterSpacing: '0.06em' }}>Active issues (last 2h)</div>
                  {data.quality.active_issues.slice(0, 5).map((issue, i) => {
                    const minsAgo = Math.floor((Date.now() - new Date(issue.logged_at).getTime()) / 60000);
                    return (
                      <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '5px 0', borderTop: '1px solid #2a3040', fontSize: '12px' }}>
                        <span style={{ color: '#fca5a5' }}>{issue.type === 'scrap' ? `${issue.count} scrap` : '✗ Fail'}</span>
                        <span style={{ color: '#6b7280', flex: 1 }}>
                          {issue.type === 'scrap' ? issue.defect : (issue.defects || []).join(', ')}
                        </span>
                        <span style={{ color: '#4b5563', whiteSpace: 'nowrap' }}>{minsAgo}m ago</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </LiveSection>

            {/* Efficiency */}
            <LiveSection title="Efficiency">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <LiveStat label="Efficiency" value={eff != null ? `${eff}%` : '—'} valueColor={effColor} />
                <LiveStat label="Est. cycles" value={data.efficiency.estimated_cycles ?? '—'} />
                <LiveStat label="Target cycle" value={data.efficiency.target_cycle_s ? `${data.efficiency.target_cycle_s}s` : '—'} />
              </div>
            </LiveSection>

          </div>
        )}
      </div>
    </>
  );
}

function LiveSection({ title, children }) {
  return (
    <div style={{ background: '#0f1117', border: '1px solid #2a3040', borderRadius: '10px', padding: '14px' }}>
      <div style={{ fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.08em', marginBottom: '12px' }}>{title}</div>
      {children}
    </div>
  );
}

function LiveStat({ label, value, valueColor }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
      <div style={{ fontSize: '9px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: '14px', fontWeight: '700', color: valueColor || '#f0f2f5' }}>{value}</div>
    </div>
  );
}

const st = {
  navBtn:      { background: '#2a3040', border: 'none', color: '#f0f2f5', borderRadius: '6px', padding: '5px 12px', cursor: 'pointer', fontSize: '14px', fontFamily: 'system-ui' },
  hdrCell:     { padding: '8px', background: '#1a2030', borderRadius: '6px', fontSize: '11px', fontWeight: '700', color: '#6b7280' },
  machineLabel:{ padding: '8px 10px', background: '#1e2329', border: '1px solid #2a3040', borderRadius: '6px', display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  addCell:     { background: 'transparent', border: '1px dashed #2a3040', color: '#374151', borderRadius: '4px', padding: '4px', fontSize: '18px', cursor: 'pointer', width: '100%', flexGrow: 1, fontFamily: 'system-ui' },
  iconEdit:    { background: 'rgba(29,78,216,0.15)', border: '1px solid rgba(29,78,216,0.3)', color: '#93c5fd', borderRadius: '5px', width: '26px', height: '26px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  iconDel:     { background: 'rgba(127,29,29,0.15)', border: '1px solid rgba(127,29,29,0.4)', color: '#fca5a5', borderRadius: '5px', width: '26px', height: '26px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  mField:      { display: 'flex', flexDirection: 'column', gap: '4px' },
  mLabel:      { fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.06em', color: '#6b7280' },
  mInput:      { background: '#0f1117', border: '1px solid #2a3040', borderRadius: '6px', color: '#f0f2f5', padding: '8px 10px', fontSize: '13px', fontFamily: 'system-ui', width: '100%', boxSizing: 'border-box' },
};

// The machine column stays put while the days scroll under it. Each of its
// cells sits in a square of page background that also fills the grid's 3px
// gaps around it, so a run scrolling past disappears into the page instead of
// butting up against the machine card (Matic, 28 Sep 2026). On the right the
// square reaches 8px past the card -- the 3px grid gap plus 5px of the column
// -- so the column reads as apart from the days, scrolled or not. The card
// stays 160px wide. The negative margins cancel the padding, so row heights
// are what they were. `top` is false for the header corner, which has
// nothing above it; `bottom` is false for the last machine, which has nothing
// below it -- reaching 3px past the grid there gave the board 3px of vertical
// scroll of its own (Matic, 9 Oct 2026).
const MACHINE_COL = 165;   // the card's 160px + 5px of gutter
// On a phone the column is slimmer: at 165 it took 40% of a phone held upright
// and left a day and a half of board (the director, 12 Oct 2026). The card
// then wraps the name, puts its buttons on a row of their own and drops the
// tonnage.
const PHONE_WIDTH = 600;
const PHONE_MACHINE_COL = 104;
const machineColFor = (w) => (w <= PHONE_WIDTH ? PHONE_MACHINE_COL : MACHINE_COL);
function stickCell(zIndex, top = true, bottom = true) {
  return {
    position: 'sticky', left: 0, zIndex, display: 'flex', background: '#0f1117',
    margin: `${top ? -3 : 0}px -3px ${bottom ? -3 : 0}px 0`, padding: `${top ? 3 : 0}px 8px ${bottom ? 3 : 0}px 0`,
  };
}
